import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { database, auth } from '../firebaseConfig';
import { ref, push, update, serverTimestamp, onValue } from 'firebase/database';
import { sampleReport } from './reportData';
import { normalizeReport, DEFAULT_KICKOFF } from './reportSchema';
import MorphBackground from '../Landingpage/MorphBackground';
import SalesGodLaunch from './SalesGodLaunch';
import LeadMagnetView from './LeadMagnetView';
import {
  WARMUP_START_VOLUME,
  WARMUP_RAMP_DAYS,
  WARMUP_TARGET_MIN,
  WARMUP_TARGET_MAX,
  WARMUP_TARGET_DEFAULT,
  clampWarmupTarget,
  warmupPlannedNew,
  warmupFollowups,
  warmupDateKey,
  isWarmupWeekend,
  warmupDayIndex,
  warmupWorkdayDate,
  fmtWarmupDate,
  warmupDateKeyToMs,
  fmtWarmupDayLong,
  warmupSubmittedLate,
} from './warmupUtils';
import {
  computeProjection,
  formatEUR,
  formatPct,
  TIER_NAMES,
  SCENARIO_SPREAD,
  clamp,
} from './pricingEngine';
import {
  PHASES,
  REGULAR_SUPER_STATUS_ORDER,
  getColumn,
  isLostSuperStatus,
  computeStatusDurations,
  formatDuration,
  formatCountdown,
  DEFAULT_SUPER_STATUS,
  SALES_PHASE_ID,
  PREPARATION_PHASE_ID,
  KICKOFF_SUPER_STATUS,
  FINAL_SUPER_STATUS,
  SHARED_CHECKLIST_ITEMS,
} from '../Admin/structureConfig';
import {
  ACCOUNT_SETUP_PHASES,
  accountSetupCompletion,
  phaseCompletion,
} from '../Protocool/accountSetupConfig';
import './ResearchPage.css';

const formatMultiple = (x) => `${x.toFixed(2).replace('.', ',')}x`;
const nf1 = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 });

// Ampel-Farben für den ROI: >=1,0x gut (grün), 0,7–1,0x Risiko (gelb), darunter schlecht (rot)
const roiTone = (roi) => {
  if (roi >= 1.0) return { color: '#4ade80', bg: 'rgba(74, 222, 128, 0.1)', border: 'rgba(74, 222, 128, 0.2)' };
  if (roi >= 0.7) return { color: '#facc15', bg: 'rgba(250, 204, 21, 0.1)', border: 'rgba(250, 204, 21, 0.2)' };
  return { color: '#f87171', bg: 'rgba(248, 113, 113, 0.1)', border: 'rgba(248, 113, 113, 0.2)' };
};

// Verdict-Label & Level aus dem dynamischen Gesamtscore
const verdictForScore = (score) => {
  if (score >= 75) return { label: 'Sehr guter SalesGod-Fit', level: 'high', color: '#4ade80' };
  if (score >= 60) return { label: 'Guter SalesGod-Fit', level: 'high', color: '#4ade80' };
  if (score >= 45) return { label: 'Bedingt geeigneter Case', level: 'mid', color: '#facc15' };
  return { label: 'Schwacher Outbound-Case', level: 'low', color: '#f87171' };
};

// ── Score-Ring ──
const ScoreRing = ({ score, maxScore, color }) => {
  const radius = 64;
  const stroke = 6;
  const circumference = 2 * Math.PI * radius;
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const id = setTimeout(() => setProgress((score / maxScore) * circumference), 60);
    return () => clearTimeout(id);
  }, [score, maxScore, circumference]);

  return (
    <div className="rp-score-ring">
      <svg width="160" height="160" viewBox="0 0 160 160">
        <circle cx="80" cy="80" r={radius} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} />
        <circle
          cx="80"
          cy="80"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          transform="rotate(-90 80 80)"
          style={{ transition: 'stroke-dashoffset 0.9s cubic-bezier(0.4, 0, 0.2, 1), stroke 0.4s ease' }}
        />
      </svg>
      <div className="rp-score-ring-value">
        <span className="rp-score-ring-num" style={{ color }}>{score}</span>
        <span className="rp-score-ring-max">/ {maxScore}</span>
      </div>
    </div>
  );
};

const SectionHead = ({ eyebrow, title, sub, action }) => (
  <div className="rp-section-head">
    <div className="rp-section-head-row">
      <div>
        {eyebrow && <span className="rp-eyebrow">{eyebrow}</span>}
        <h2 className="rp-section-title">{title}</h2>
      </div>
      {action}
    </div>
    {sub && <p className="rp-section-sub">{sub}</p>}
  </div>
);

// ── Live-Configurator (immer ausgeklappt) ──
const Configurator = ({ config, setConfig, derived, defaultConfig }) => {
  const set = (patch) => setConfig((c) => ({ ...c, ...patch }));
  const isDirty = JSON.stringify(config) !== JSON.stringify(defaultConfig);

  const isManualAppt = config.apptValueMode === 'manual';
  const isManualMonthly = config.monthlyPriceMode === 'manual';

  return (
    <div className="rp-cfg rp-cfg-open">
      <div className="rp-cfg-grid">
        <div className="rp-cfg-field rp-cfg-field-wide">
          <div className="rp-cfg-field-head">
            <label>Paket wählen</label>
            <span className="rp-cfg-field-value">{formatEUR(derived.monthlyPrice)} / Mon.</span>
          </div>
          <div className="rp-cfg-seg rp-cfg-seg-lg">
            {TIER_NAMES.map((name, i) => (
              <button
                key={name}
                className={`rp-cfg-seg-btn ${config.tier === i ? 'rp-cfg-seg-active' : ''}`}
                onClick={() => set({ tier: i })}
              >
                {name}
              </button>
            ))}
          </div>
        </div>

        <div className="rp-cfg-field rp-cfg-field-wide">
          <div className="rp-cfg-field-head">
            <label>Monatlicher Retainer</label>
            <div className="rp-cfg-mode">
              <button
                className={`rp-cfg-mode-btn ${!isManualMonthly ? 'rp-cfg-mode-active' : ''}`}
                onClick={() => set({ monthlyPriceMode: 'auto' })}
              >
                auto
              </button>
              <button
                className={`rp-cfg-mode-btn ${isManualMonthly ? 'rp-cfg-mode-active' : ''}`}
                onClick={() => set({ monthlyPriceMode: 'manual', monthlyPriceManual: derived.monthlyPrice })}
              >
                manuell
              </button>
            </div>
          </div>

          {isManualMonthly ? (
            <>
              <div className="rp-cfg-readout">
                <span className="rp-cfg-readout-value">{formatEUR(config.monthlyPriceManual)}</span>
                <span className="rp-cfg-readout-unit">/ Monat</span>
              </div>
              <input
                type="range" min="0" max="15000" step="20"
                value={config.monthlyPriceManual}
                onChange={(e) => set({ monthlyPriceManual: Number(e.target.value) })}
                className="rp-slider"
              />
              <p className="rp-cfg-hint">Manueller Retainer – überschreibt den automatischen Paketpreis und fließt direkt in Investment &amp; ROI ein.</p>
            </>
          ) : (
            <div className="rp-cfg-readout">
              <span className="rp-cfg-readout-value">{formatEUR(derived.monthlyPrice)}</span>
              <span className="rp-cfg-readout-unit">/ Monat (automatisch aus Paket &amp; Deal-Wert)</span>
            </div>
          )}
        </div>

        <div className="rp-cfg-field">
          <div className="rp-cfg-field-head">
            <label>Ø Umsatz pro Abschluss</label>
            <span className="rp-cfg-field-value">{formatEUR(config.revenuePerClose)}</span>
          </div>
          <input
            type="range" min="100" max="100000" step="100"
            value={config.revenuePerClose}
            onChange={(e) => set({ revenuePerClose: Number(e.target.value) })}
            className="rp-slider"
          />
        </div>

        <div className="rp-cfg-field">
          <div className="rp-cfg-field-head">
            <label>Geschätzte Marge</label>
            <span className="rp-cfg-field-value">{formatPct(config.profitMargin)}</span>
          </div>
          <input
            type="range" min="10" max="100" step="1"
            value={Math.round(config.profitMargin * 100)}
            onChange={(e) => set({ profitMargin: Number(e.target.value) / 100 })}
            className="rp-slider"
          />
        </div>

        <div className="rp-cfg-field">
          <div className="rp-cfg-field-head">
            <label>Ø Termine / Monat</label>
            <span className="rp-cfg-field-value">{derived.expectedAppts}</span>
          </div>
          <input
            type="range" min="0" max="100" step="1"
            value={derived.expectedAppts}
            onChange={(e) => set({ expectedApptsOverride: Number(e.target.value) })}
            className="rp-slider"
          />
        </div>

        <div className="rp-cfg-field">
          <div className="rp-cfg-field-head">
            <label>Abschlüsse pro Termin <span className="rp-cfg-tag-drives">ROI</span></label>
            <span className="rp-cfg-field-value">{formatPct(derived.closeRate)}</span>
          </div>
          <input
            type="range" min="2" max="100" step="1"
            value={Math.round(derived.closeRate * 100)}
            onChange={(e) => set({ closeRate: Number(e.target.value) / 100 })}
            className="rp-slider"
          />
          <p className="rp-cfg-hint">
            Wie oft ein qualifizierter Termin realistisch zum Abschluss führt – bei {formatPct(derived.closeRate)} entstehen aus {derived.expectedAppts} Terminen/Monat ca. {Math.round(derived.realisticClosesPerMonth * 10) / 10} Abschlüsse. Treibt nur die ROI- &amp; Neukunden-Szenarien, nicht den Termin-Preis.
          </p>
        </div>

        <div className="rp-cfg-field">
          <div className="rp-cfg-field-head">
            <label>Preis ab dem 4. Termin</label>
            <div className="rp-cfg-mode">
              <button
                className={`rp-cfg-mode-btn ${!isManualAppt ? 'rp-cfg-mode-active' : ''}`}
                onClick={() => set({ apptValueMode: 'auto' })}
              >
                auto
              </button>
              <button
                className={`rp-cfg-mode-btn ${isManualAppt ? 'rp-cfg-mode-active' : ''}`}
                onClick={() => set({ apptValueMode: 'manual', apptValueManual: derived.appointmentValue })}
              >
                manuell
              </button>
            </div>
          </div>

          {isManualAppt ? (
            <>
              <div className="rp-cfg-readout">
                <span className="rp-cfg-readout-value">{formatEUR(config.apptValueManual)}</span>
                <span className="rp-cfg-readout-unit">/ zusätzlicher Termin</span>
              </div>
              <input
                type="range" min="0" max="2000" step="10"
                value={config.apptValueManual}
                onChange={(e) => set({ apptValueManual: Number(e.target.value) })}
                className="rp-slider"
              />
            </>
          ) : (
            <div className="rp-cfg-readout">
              <span className="rp-cfg-readout-value">{formatEUR(derived.appointmentValue)}</span>
              <span className="rp-cfg-readout-unit">/ zusätzlicher Termin</span>
            </div>
          )}
        </div>
      </div>

      {isDirty && (
        <div className="rp-cfg-footer">
          <button className="rp-cfg-reset" onClick={() => setConfig(defaultConfig)}>
            Auf Ausgangswerte zurücksetzen
          </button>
        </div>
      )}
    </div>
  );
};

// Eine Bar-Zeile für die Investition/Gewinn-Visualisierung
const BarRow = ({ label, value, pct, variant }) => (
  <div className="rp-bar-row">
    <div className="rp-bar-row-head">
      <span className="rp-bar-row-label">{label}</span>
      <span className={`rp-bar-row-value rp-bar-${variant}-text`}>{value}</span>
    </div>
    <div className="rp-bar-track">
      <div className={`rp-bar-fill rp-bar-${variant}`} style={{ width: `${pct}%` }} />
    </div>
  </div>
);

// ── IMPACT-TAB ──
const ImpactTab = ({ report, derived }) => {
  const { verdict, scenarios, feeVsUpside, executiveSummary, riskFraming } = report;
  const [period, setPeriod] = useState('year'); // 'year' | 'month'
  const [showYear2, setShowYear2] = useState(false);
  const v = derived.verdict;

  const div = period === 'month' ? 12 : 1;
  const closesFmt = (n) => (period === 'month' ? nf1.format(n / 12) : String(n));
  const periodSuffix = period === 'month' ? '/ Monat' : '/ Jahr';

  const barMax = Math.max(derived.baseRevenue, derived.investmentYear1, derived.baseProfit);
  const tone = roiTone(derived.roiMultiple);
  const netPlus = derived.baseProfit - derived.investmentYear1;

  // Verhältnis Investition ↔ Profit-Upside als eine gestapelte Bar
  const ratioTotal = derived.investmentYear1 + derived.baseProfit || 1;
  const costPct = (derived.investmentYear1 / ratioTotal) * 100;
  const gainPct = 100 - costPct;

  return (
    <div className="rp-tab">
      {/* ── PAYOFF (ganz oben) ── */}
      <section className="rp-payoff">
        <div className="rp-payoff-glow" />
        <span className="rp-payoff-eyebrow">Lohnt sich das?</span>
        <div className="rp-payoff-compare">
          <div className="rp-payoff-side">
            <span className="rp-payoff-side-label">Investition · Jahr 1</span>
            <span className="rp-payoff-side-value">{formatEUR(derived.investmentYear1)}</span>
          </div>
          <div className="rp-payoff-vs">→</div>
          <div className="rp-payoff-side">
            <span className="rp-payoff-side-label">Profit-Upside · realistisch</span>
            <span className="rp-payoff-side-value rp-payoff-side-gain">{formatEUR(derived.baseProfit)}</span>
          </div>
        </div>
        <div className="rp-payoff-takeaways">
          <div className="rp-payoff-roi" style={{ background: tone.bg, borderColor: tone.border }}>
            <span className="rp-payoff-roi-value" style={{ color: tone.color }}>{formatMultiple(derived.roiMultiple)}</span>
            <span className="rp-payoff-roi-label">ROI auf Profitbasis · Jahr 1</span>
          </div>
          <div className="rp-payoff-right">
            <div className="rp-payoff-ratio">
              <div className="rp-payoff-ratio-track">
                <div className="rp-payoff-ratio-seg rp-payoff-ratio-cost" style={{ width: `${costPct}%` }} />
                <div className="rp-payoff-ratio-seg rp-payoff-ratio-gain" style={{ width: `${gainPct}%` }} />
              </div>
              <div className="rp-payoff-ratio-legend">
                <span className="rp-payoff-ratio-leg-cost"><i />Investition · {formatEUR(derived.investmentYear1)}</span>
                <span className="rp-payoff-ratio-leg-gain"><i />Profit-Upside · {formatEUR(derived.baseProfit)}</span>
              </div>
            </div>
            <div className="rp-payoff-break">
              <p>
                Break-even bei <strong>{derived.breakEvenCloses} Abschlüssen</strong> im Jahr.
                Im realistischen Szenario sind <strong>{derived.baseCloses}</strong> drin – {derived.breakEvenCloses <= derived.baseCloses ? 'der Case trägt sich also bereits im ersten Jahr.' : 'der Hebel liegt im Folgegeschäft über die gesamte Kundenbeziehung.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Jahr 2 · Retainer-Effekt (aufklappbar) ── */}
      <section className={`rp-year2 ${showYear2 ? 'rp-year2-open' : ''}`}>
        <button className="rp-year2-toggle" onClick={() => setShowYear2((s) => !s)} aria-expanded={showYear2}>
          <span className="rp-year2-toggle-main">
            <span className="rp-year2-eyebrow">Folgejahr · Retainer-Effekt</span>
            <span className="rp-year2-title">Was passiert in Jahr 2?</span>
          </span>
          <span className="rp-year2-toggle-side">
            <span className="rp-year2-teaser">+{formatEUR(derived.retainedProfitY2)} ohne neue Kosten</span>
            <svg className="rp-year2-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
          </span>
        </button>

        {showYear2 && (
          <div className="rp-year2-body">
            <p className="rp-year2-lead">
              Die Fee fällt nur in dem Jahr an, in dem ein Kunde gewonnen wird. Danach läuft dieser Kunde weiter und
              bringt Umsatz – ohne dass dafür erneut bezahlt werden muss.
            </p>

            <div className="rp-year2-flow">
              <div className="rp-year2-step">
                <span className="rp-year2-step-tag">Jahr 1 · akquiriert</span>
                <span className="rp-year2-step-value">{derived.baseCloses}</span>
                <span className="rp-year2-step-label">Neukunden gewonnen (Fee bezahlt)</span>
              </div>
              <div className="rp-year2-arrow">→ {formatPct(derived.retentionY2)} bleiben</div>
              <div className="rp-year2-step rp-year2-step-keep">
                <span className="rp-year2-step-tag">Jahr 2 · Bestand</span>
                <span className="rp-year2-step-value">{derived.retainedClosesY2}</span>
                <span className="rp-year2-step-label">zahlen weiter – ohne neue Fee</span>
              </div>
            </div>

            <div className="rp-year2-stats">
              <div className="rp-year2-stat">
                <span className="rp-year2-stat-value">{formatEUR(derived.retainedRevenueY2)}</span>
                <span className="rp-year2-stat-label">Umsatz aus Bestand · Jahr 2</span>
              </div>
              <div className="rp-year2-stat">
                <span className="rp-year2-stat-value rp-stat-gain">+{formatEUR(derived.retainedProfitY2)}</span>
                <span className="rp-year2-stat-label">Profit aus Bestand · Jahr 2</span>
              </div>
              <div className="rp-year2-stat" style={{ borderColor: tone.border }}>
                <span className="rp-year2-stat-value" style={{ color: tone.color }}>{formatMultiple(derived.twoYearRoi)}</span>
                <span className="rp-year2-stat-label">ROI über 2 Jahre · eine Investition</span>
              </div>
            </div>

            <div className="rp-year2-callout">
              <span className="rp-year2-callout-mark">↻</span>
              <p>
                <strong>Selbst wenn du SalesGod nach Jahr 1 beenden würdest</strong>, laufen die {derived.retainedClosesY2} gehaltenen
                Kunden weiter und bringen rund {formatEUR(derived.retainedProfitY2)} Profit im 2. Jahr – Geld, das ohne
                weitere Investition fließt. Die Fee war ausschließlich für die Akquise im 1. Jahr.
              </p>
            </div>
          </div>
        )}
      </section>

      {/* ── Best Case vs. Worst Case ── */}
      <section className="rp-block">
        <SectionHead eyebrow="Risiko-Asymmetrie" title="Best Case vs. Worst Case" />
        <div className="rp-bw-grid">
          <div className="rp-bw-card rp-bw-worst">
            <span className="rp-bw-tag rp-bw-tag-worst">Worst Case</span>
            <span className="rp-bw-headline">{riskFraming.worstCaseTitle}</span>
            <span className="rp-bw-amount rp-bw-amount-cost">−{formatEUR(derived.worstCaseCost)}</span>
            <span className="rp-bw-sub">{riskFraming.worstCaseSub}</span>
            <div className="rp-bw-divider" />
            <span className="rp-bw-still">{riskFraming.worstCaseStill}</span>
            <ul className="rp-bw-list">
              {riskFraming.worstCaseInsights.map((item, i) => (
                <li key={i}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="rp-bw-card rp-bw-best">
            <span className="rp-bw-tag rp-bw-tag-best">Best Case</span>
            <span className="rp-bw-headline">{derived.bestCase.closes} Neukunden / Jahr</span>
            <span className="rp-bw-amount rp-bw-amount-gain">+{formatEUR(derived.bestCase.profit)}</span>
            <span className="rp-bw-sub">{riskFraming.bestCaseSub}</span>
            <div className="rp-bw-divider" />
            <span className="rp-bw-still">{riskFraming.bestCaseStill}</span>
            <ul className="rp-bw-list rp-bw-list-best">
              <li>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                {formatEUR(derived.bestCase.revenue)} Umsatz-Upside
              </li>
              {riskFraming.bestCaseExtras.map((item, i) => (
                <li key={i}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="rp-bw-ratio">
          <span className="rp-bw-ratio-value">{formatMultiple(derived.upsideVsRisk)}</span>
          <span className="rp-bw-ratio-label">mögliches Upside im Verhältnis zum maximalen Risiko ({derived.worstCaseMonths} Monate Fixfee)</span>
        </div>
        <p className="rp-note">{riskFraming.note}</p>
      </section>

      {/* ── Szenarien (mit Jahr/Monat-Switch) ── */}
      <section className="rp-block">
        <SectionHead
          eyebrow="Commercial Upside"
          title="Wirtschaftliche Szenarien"
          sub={`Bei Ø ${formatEUR(derived.revenuePerClose)} Umsatz pro Abschluss und ${formatPct(derived.profitMargin)} Marge entsteht pro Neukunde ein Profit von rund ${formatEUR(derived.profitPerClose)}.`}
          action={
            <div className="rp-period-switch">
              <button className={period === 'year' ? 'rp-period-active' : ''} onClick={() => setPeriod('year')}>pro Jahr</button>
              <button className={period === 'month' ? 'rp-period-active' : ''} onClick={() => setPeriod('month')}>pro Monat</button>
            </div>
          }
        />
        <div className="rp-scen-grid">
          {derived.scenarios.map((s, i) => (
            <div key={i} className={`rp-scen-card ${s.highlight ? 'rp-scen-highlight' : ''}`}>
              {s.highlight && <span className="rp-scen-tag">Base Case</span>}
              <span className="rp-scen-name">{s.name}</span>
              <span className="rp-scen-closes">{closesFmt(s.closes)} <small>Neukunden {periodSuffix}</small></span>
              <div className="rp-scen-rows">
                <div className="rp-scen-row">
                  <span>Umsatz {periodSuffix}</span>
                  <span className="rp-scen-rev">{formatEUR(s.revenue / div)}</span>
                </div>
                <div className="rp-scen-row">
                  <span>Profit {periodSuffix}</span>
                  <span className="rp-scen-profit">{formatEUR(s.profit / div)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="rp-note">{scenarios.note}</p>
      </section>

      {/* ── Investition vs. Gewinn (klare Bars, wenig Text) ── */}
      <section className="rp-block">
        <SectionHead eyebrow="Fee vs. Upside" title="Investition vs. Gewinn" />
        <div className="rp-bars">
          <BarRow label="SalesGod-Investition · Jahr 1" value={formatEUR(derived.investmentYear1)} pct={(derived.investmentYear1 / barMax) * 100} variant="cost" />
          <BarRow label="Profit-Upside · Base Case" value={formatEUR(derived.baseProfit)} pct={(derived.baseProfit / barMax) * 100} variant="gain" />
          <BarRow label="Umsatz-Upside · Base Case" value={formatEUR(derived.baseRevenue)} pct={(derived.baseRevenue / barMax) * 100} variant="rev" />
        </div>
        <div className="rp-bar-stats">
          <div className="rp-bar-stat" style={{ borderColor: tone.border }}>
            <span className="rp-bar-stat-value" style={{ color: tone.color }}>{formatMultiple(derived.roiMultiple)}</span>
            <span className="rp-bar-stat-label">ROI auf Profitbasis</span>
          </div>
          <div className="rp-bar-stat">
            <span className="rp-bar-stat-value">{formatPct(derived.feeToProfitRatio)}</span>
            <span className="rp-bar-stat-label">Fee-to-Profit-Upside</span>
          </div>
          <div className="rp-bar-stat">
            <span className="rp-bar-stat-value" style={{ color: netPlus >= 0 ? '#4ade80' : '#f87171' }}>{formatEUR(netPlus)}</span>
            <span className="rp-bar-stat-label">Netto-{netPlus >= 0 ? 'Plus' : 'Minus'} · Jahr 1</span>
          </div>
        </div>
        <p className="rp-note">{feeVsUpside.valueNote}</p>
      </section>

      {/* Leverage callout */}
      <section className="rp-leverage">
        <span className="rp-eyebrow">{verdict.leverage.title}</span>
        <p className="rp-leverage-neg">{verdict.leverage.negative}</p>
        <p className="rp-leverage-pos">{verdict.leverage.positive}</p>
      </section>

      {/* Executive summary */}
      <section className="rp-block rp-exec">
        <SectionHead eyebrow="Executive Summary" title="Zusammenfassung" />
        <div className="rp-prose">
          {executiveSummary.paragraphs.map((p, i) => (
            <p key={i} className="rp-paragraph">{p}</p>
          ))}
          <p className="rp-paragraph">{executiveSummary.bestSegmentsTitle}</p>
        </div>
        <div className="rp-chips">
          {executiveSummary.bestSegments.map((s, i) => (
            <span key={i} className="rp-chip">{s}</span>
          ))}
        </div>
        <p className="rp-closing">{executiveSummary.closing}</p>
      </section>

      {/* ── OUTCOME: Fit-Score ganz unten ── */}
      <section className="rp-outcome">
        <div className="rp-outcome-glow" style={{ background: `radial-gradient(circle, ${v.color}22 0%, transparent 70%)` }} />
        <span className="rp-eyebrow" style={{ textAlign: 'center' }}>Gesamtbewertung</span>
        <div className="rp-outcome-ring">
          <ScoreRing score={derived.totalScore} maxScore={100} color={v.color} />
          <span className="rp-outcome-label" style={{ color: v.color }}>{v.label}</span>
        </div>
        <div className="rp-outcome-summary">
          {verdict.summary.map((p, i) => (
            <p key={i} className="rp-paragraph">{p}</p>
          ))}
        </div>

        {/* Scorecard – sichtbar als Summe zum Ring */}
        <div className="rp-scorebuild">
          <span className="rp-scorebuild-hint">Der Score ist die Summe aus zwei parallelen Systemen:</span>
          {derived.scoreGroups.map((group) => (
            <div key={group.name} className="rp-scoregroup">
              <div className="rp-scoregroup-head">
                <span className="rp-scoregroup-name">{group.name}</span>
                <span className="rp-scoregroup-sum">{group.sum} <small>/ {group.max} Pkt.</small></span>
              </div>
              {group.rows.map((row, i) => (
                <div key={i} className="rp-scorecard-row">
                  <div className="rp-scorecard-top">
                    <span className="rp-scorecard-label">{row.label}</span>
                    <span className="rp-scorecard-score">{row.score}<span className="rp-scorecard-max">/{row.max}</span></span>
                  </div>
                  <div className="rp-scorecard-bar">
                    <div
                      className={`rp-scorecard-fill ${row.score / row.max < 0.55 ? 'rp-fill-warn' : ''} ${group.dynamic ? 'rp-fill-live' : ''}`}
                      style={{ width: `${(row.score / row.max) * 100}%` }}
                    />
                  </div>
                  {row.note && <span className="rp-scorecard-note">{row.note}</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

// Gruppen-Definition des editierbaren Zielgruppen-Researchs
const RESEARCH_GROUPS = [
  { key: 'ideas', label: 'Branchen / Zielgruppen', placeholder: 'Branche hinzufügen…' },
  { key: 'keywords', label: 'Keywords', placeholder: 'Keyword hinzufügen…' },
  { key: 'hashtags', label: 'Hashtags', placeholder: 'Hashtag hinzufügen…', tag: true },
  { key: 'channels', label: 'Plattformen & Kanäle', placeholder: 'Kanal hinzufügen…' },
  { key: 'decisionMakers', label: 'Entscheider', placeholder: 'Entscheider hinzufügen…' },
  { key: 'regions', label: 'Regionen', placeholder: 'Region hinzufügen…' },
  { key: 'revenueBands', label: 'Relevante Umsatzgrößen', placeholder: 'Umsatzgröße hinzufügen…' },
];

export const buildResearchState = (tr = {}) => {
  const out = {};
  RESEARCH_GROUPS.forEach((g) => {
    out[g.key] = (tr[g.key] || []).map((label) => ({ label, active: true }));
  });
  return out;
};

// Chip-State → reine String-Arrays (für die Persistenz im Report).
// Es werden alle vorhandenen Labels gespeichert (Hinzufügen/Entfernen persistiert).
const chipsToArrays = (state = {}) => {
  const out = {};
  RESEARCH_GROUPS.forEach((g) => {
    out[g.key] = (state[g.key] || []).map((it) => it.label);
  });
  return out;
};

// Textarea, die automatisch auf ihren Inhalt wächst (kein Abschneiden / Scrollen)
const AutoGrowTextarea = ({ value, ...rest }) => {
  const ref = useRef(null);
  const resize = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };
  useEffect(resize, [value]);
  return <textarea ref={ref} rows={1} value={value} {...rest} />;
};

// ── Rich-Text-Feld mit echter Fett-Markierung ──
// Intern wird der Text weiterhin als **…**-Markdown gespeichert (kompatibel zu
// KI-Ausgabe, Export und Persistenz), angezeigt und bearbeitet wird aber echter
// Rich-Text: markierte Stellen sind „fett" ohne sichtbare Sternchen und lassen
// sich per Cmd/Strg+B umschalten (wie in einem Textverarbeitungsprogramm).

const escapeHtml = (s) => String(s || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

// **fett**-Markdown (+ Zeilenumbrüche) → HTML mit echtem <strong>.
const markdownToHtml = (md) => {
  const escaped = escapeHtml(md);
  const withStrong = escaped.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  return withStrong.replace(/\n/g, '<br>');
};

// Leerzeichen aus der Markierung herausziehen, damit „** wort **" nicht entsteht.
const wrapBold = (s) => {
  const m = s.match(/^(\s*)([\s\S]*?)(\s*)$/);
  if (!m || !m[2]) return s;
  return `${m[1]}**${m[2]}**${m[3]}`;
};

const isBoldEl = (el) => {
  const tag = el.tagName;
  if (tag === 'B' || tag === 'STRONG') return true;
  const fw = (el.style && el.style.fontWeight) || '';
  return fw === 'bold' || fw === 'bolder' || /^[6-9]00$/.test(fw);
};

// DOM eines contentEditable → **…**-Markdown. Fett-Bereiche werden nur an der
// äußersten Grenze in **…** gewrappt, <br>/Blockelemente werden zu \n.
const serializeRich = (root) => {
  let out = '';
  const walk = (node, bold) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === 3) {
        out += child.nodeValue;
      } else if (child.nodeType === 1) {
        if (child.tagName === 'BR') { out += '\n'; return; }
        const nowBold = isBoldEl(child);
        if (!bold && nowBold) {
          const before = out.length;
          walk(child, true);
          out = out.slice(0, before) + wrapBold(out.slice(before));
        } else {
          if ((child.tagName === 'DIV' || child.tagName === 'P') && out && !out.endsWith('\n')) {
            out += '\n';
          }
          walk(child, bold || nowBold);
        }
      }
    });
  };
  walk(root, false);
  return out;
};

const MarkableText = ({ value, placeholder, onChange, className }) => {
  const ref = useRef(null);
  const lastValue = useRef(null);
  const [empty, setEmpty] = useState(!String(value || '').trim());

  // Externe Wertänderungen (KI-Generierung, Report-Wechsel) ins DOM spiegeln –
  // aber nur, wenn sie nicht von uns selbst stammen, damit der Cursor beim
  // Tippen nicht springt.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const incoming = String(value || '');
    if (incoming === lastValue.current) return;
    lastValue.current = incoming;
    el.innerHTML = markdownToHtml(incoming);
    setEmpty(!incoming.trim());
  }, [value]);

  const emit = () => {
    const el = ref.current;
    if (!el) return;
    const md = serializeRich(el);
    const blank = !md.replace(/[\s\n]/g, '');
    if (blank && el.innerHTML !== '') el.innerHTML = '';
    lastValue.current = blank ? '' : md;
    setEmpty(blank);
    onChange({ target: { value: blank ? '' : md } });
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && (e.key === 'b' || e.key === 'B')) {
      e.preventDefault();
      try { document.execCommand('bold'); } catch (err) { /* execCommand n/a */ }
      emit();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      try { document.execCommand('insertLineBreak'); } catch (err) { /* fallback */ }
      emit();
    }
  };

  // Einfügen immer als reiner Text, damit kein fremdes Markup ins Feld gerät.
  const onPaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text/plain');
    try { document.execCommand('insertText', false, text); } catch (err) { /* */ }
    emit();
  };

  return (
    <div
      ref={ref}
      className={`${className} rp-mark-rich${empty ? ' is-empty' : ''}`}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      aria-label={placeholder}
      data-placeholder={placeholder}
      spellCheck={false}
      onInput={emit}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      onBlur={emit}
    />
  );
};

// Reviews einer E-Mail robust normalisieren. Neu: Liste aus { id, author,
// rating (1–10), comment, at }. Alt-Bestand (einzelnes notes/rating-Feld) wird
// tolerant in eine einzelne Review überführt, damit nichts verloren geht.
let reviewSeq = 0;
const makeReviewId = () => `rv_${Date.now().toString(36)}_${(reviewSeq++).toString(36)}`;
const normalizeReviews = (email) => {
  const list = Array.isArray(email && email.reviews) ? email.reviews : [];
  const clampRating = (v) => (Number.isFinite(v) ? Math.max(0, Math.min(10, Math.round(v))) : 0);
  const out = list
    .filter((r) => r && typeof r === 'object')
    .map((r) => ({
      id: r.id || makeReviewId(),
      author: (r.author || '').trim(),
      rating: clampRating(r.rating),
      subjectRating: clampRating(r.subjectRating),
      comment: (r.comment || '').trim(),
      at: typeof r.at === 'number' ? r.at : Date.now(),
    }));
  // Migration von früherem Einzelfeld (notes/rating) → eine Review.
  if (!out.length && email && (Number.isFinite(email.rating) || (email.notes || '').trim())) {
    out.push({
      id: makeReviewId(),
      author: '',
      rating: Number.isFinite(email.rating) ? email.rating : 0,
      subjectRating: 0,
      comment: (email.notes || '').trim(),
      at: Date.now(),
    });
  }
  return out;
};

// Durchschnitt der vergebenen Bewertungen für ein Feld (rating | subjectRating).
// Nur Reviews mit einem Wert > 0 zählen.
const reviewAverage = (reviews, key = 'rating') => {
  const rated = (reviews || []).filter((r) => r && r[key] > 0);
  if (!rated.length) return null;
  return rated.reduce((s, r) => s + r[key], 0) / rated.length;
};

// 1–10-Sterne-Bewertung: klick zum Setzen, erneuter Klick auf den aktuellen Wert
// setzt zurück. Hover zeigt den potenziellen Wert als Vorschau.
const StarRating = ({ value = 0, onChange, max = 10, readOnly = false }) => {
  const [hover, setHover] = useState(0);
  const shown = readOnly ? value : (hover || value || 0);
  return (
    <div className={`rp-stars ${readOnly ? 'rp-stars-ro' : ''}`} role="radiogroup" aria-label="Bewertung von 1 bis 10">
      <div className="rp-stars-row" onMouseLeave={() => !readOnly && setHover(0)}>
        {Array.from({ length: max }, (_, idx) => {
          const n = idx + 1;
          // Anteil dieses Sterns füllen (für Halbsterne im Durchschnitt).
          const fill = Math.max(0, Math.min(1, shown - idx));
          if (readOnly) {
            const gid = `star-${n}-${Math.round(shown * 10)}`;
            return (
              <span key={n} className="rp-star rp-star-ro" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.28)" strokeWidth="1.5" strokeLinejoin="round">
                  <defs>
                    <linearGradient id={gid}>
                      <stop offset={`${fill * 100}%`} stopColor="#f4be4e" />
                      <stop offset={`${fill * 100}%`} stopColor="transparent" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M12 2.5l2.9 5.9 6.5.95-4.7 4.58 1.1 6.47L12 17.9l-5.8 3.05 1.1-6.47L2.6 9.35l6.5-.95L12 2.5z"
                    fill={`url(#${gid})`}
                  />
                </svg>
              </span>
            );
          }
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={value === n}
              aria-label={`${n} von ${max}`}
              className={`rp-star ${n <= shown ? 'is-on' : ''}`}
              onMouseEnter={() => setHover(n)}
              onFocus={() => setHover(n)}
              onBlur={() => setHover(0)}
              onClick={() => onChange(value === n ? 0 : n)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
                <path d="M12 2.5l2.9 5.9 6.5.95-4.7 4.58 1.1 6.47L12 17.9l-5.8 3.05 1.1-6.47L2.6 9.35l6.5-.95L12 2.5z" />
              </svg>
            </button>
          );
        })}
      </div>
      {!readOnly && <span className="rp-stars-val">{value ? `${value}/${max}` : '–'}</span>}
    </div>
  );
};

// ── Kanal-Strategie: wo & wie Outreach am sinnvollsten ist (aufklappbar) ──
const fitTone = (fit) => (fit >= 66 ? 'high' : fit >= 40 ? 'mid' : 'low');
const fitLabel = (fit) => (fit >= 66 ? 'Stark' : fit >= 40 ? 'Bedingt' : 'Schwach');

const ChannelStrategy = ({ data }) => {
  const [open, setOpen] = useState(false);
  if (!data || !Array.isArray(data.channels) || data.channels.length === 0) return null;
  const top = data.channels.slice(0, 3);

  return (
    <div className={`rp-channels ${open ? 'rp-channels-open' : ''}`}>
      <button className="rp-channels-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="rp-channels-toggle-main">
          <span className="rp-channels-toggle-eyebrow">Sales-Opportunities</span>
          <span className="rp-channels-toggle-title">Wo &amp; wie Outreach am sinnvollsten ist</span>
        </span>
        <span className="rp-channels-toggle-side">
          {!open && (
            <span className="rp-channels-preview">
              {top.map((c, i) => (
                <span key={i} className={`rp-channels-dot rp-tone-${fitTone(clamp(c.fit, 0, 100))}`} title={c.name} />
              ))}
              <span className="rp-channels-count">{data.channels.length} Kanäle</span>
            </span>
          )}
          <svg className="rp-channels-chevron" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>

      {open && (
        <div className="rp-channels-body">
          {data.intro && <p className="rp-channels-intro">{data.intro}</p>}
          <div className="rp-channels-list">
            {data.channels.map((c, i) => {
              const fit = clamp(c.fit, 0, 100);
              const tone = fitTone(fit);
              return (
                <div key={i} className={`rp-channel rp-tone-${tone}`}>
                  <div className="rp-channel-top">
                    <span className="rp-channel-name">{c.name}</span>
                    <span className="rp-channel-badge">{fitLabel(fit)}<span className="rp-channel-badge-num">{fit}</span></span>
                  </div>
                  <div className="rp-channel-bar">
                    <div className="rp-channel-fill" style={{ width: `${fit}%` }} />
                  </div>
                  {c.recommendation && <p className="rp-channel-rec">{c.recommendation}</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Metadaten der "Angebot des Kunden verstehen"-Kacheln.
// tone = Akzentfarbe (RGB-Tripel), icon = inline SVG für den runden Icon-Container.
const OFFER_META = {
  whatIsSold: {
    label: (<><strong>Zusammenfassung</strong></>),
    tone: '109, 167, 237',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><g transform="rotate(16 12 13)"><path d="M9 11V4.8a1.4 1.4 0 0 1 2.8 0V10" /><path d="M11.8 10V3.4a1.4 1.4 0 0 1 2.8 0V10" /><path d="M14.6 10.4V5.2a1.4 1.4 0 0 1 2.8 0V11" /><path d="M17.4 8.6a1.4 1.4 0 0 1 2.8 0V14a7 7 0 0 1-7 7 7 7 0 0 1-4.95-2.05l-3.6-3.6a1.5 1.5 0 0 1 2.12-2.12L9 14.6V8.6a1.4 1.4 0 0 1 2.8 0" /></g><path d="M3.4 4c1.05.95 1.05 2.85 0 3.8" /><path d="M1 5.8c.6.55.6 1.7 0 2.25" /></svg>
    ),
  },
  usp: {
    label: (<><strong>Alleinstellungsmerkmale</strong></>),
    tone: '167, 139, 250',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.5 14.9 8.6l6.6.9-4.8 4.6 1.2 6.6L12 17.6l-5.9 3.1 1.2-6.6L2.5 9.5l6.6-.9z" /></svg>
    ),
  },
  salesSetup: {
    label: (<>Sales <strong>Status quo</strong></>),
    tone: '109, 167, 237',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="3.2" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
    ),
  },
  noBrainerOffer: {
    label: (<><strong>No-Brainer</strong>-Offer</>),
    tone: '109, 167, 237',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12v9H4v-9" /><path d="M2 7h20v5H2z" /><path d="M12 21V7" /><path d="M12 7C12 7 11 2.5 7.5 2.5A2.5 2.5 0 0 0 7.5 7.5C10.5 7.5 12 7 12 7Z" /><path d="M12 7C12 7 13 2.5 16.5 2.5a2.5 2.5 0 0 1 0 5C13.5 7.5 12 7 12 7Z" /></svg>
    ),
  },
};

// Normalisiert offerAnalysis aus dem Report in den editierbaren State.
// Robust gegen das alte Format (usp/salesSetup waren früher reine Strings).
const buildOfferState = (oa = {}) => {
  const o = oa && typeof oa === 'object' ? oa : {};
  // Stichpunkte sind einzeilig: eingebettete/umschließende Zeilenumbrüche (aus der
  // KI-Generierung) zu einem Leerzeichen kollabieren, sonst entstehen leere Zeilen.
  const toList = (v) => (Array.isArray(v)
    ? v.map((x) => String(x || '').replace(/\s*\n\s*/g, ' ').trim()).filter(Boolean)
    : []);
  const usp = o.usp;
  const sales = o.salesSetup;
  return {
    whatIsSold: typeof o.whatIsSold === 'string' ? o.whatIsSold : '',
    usp: usp && typeof usp === 'object'
      ? { text: String(usp.text || ''), points: toList(usp.points) }
      : { text: typeof usp === 'string' ? usp : '', points: [] },
    salesSetup: sales && typeof sales === 'object'
      ? { have: toList(sales.have), missing: toList(sales.missing) }
      : { have: typeof sales === 'string' && sales.trim() ? [sales.trim()] : [], missing: [] },
    noBrainerOffers: normalizeNoBrainerOffers(o),
  };
};

// Normalisiert die No-Brainer-Offers in eine Liste editierbarer Einträge.
// Eintrag: { title, text, idea } (idea = als Konzept/Idee markiert).
// Back-compat: ältere Reports haben nur den String `noBrainerOffer`.
const normalizeNoBrainerOffers = (o = {}) => {
  if (Array.isArray(o.noBrainerOffers)) {
    return o.noBrainerOffers
      .map((it) => (it && typeof it === 'object'
        ? { title: String(it.title || ''), text: String(it.text || ''), idea: !!it.idea }
        : { title: '', text: String(it || ''), idea: false }))
      .filter((it) => it.title.trim() || it.text.trim());
  }
  const legacy = typeof o.noBrainerOffer === 'string' ? o.noBrainerOffer.trim() : '';
  return legacy ? [{ title: '', text: legacy, idea: false }] : [];
};

// Editierbare Stichpunkt-Liste (für USP-Punkte und Sales have/missing).
// variant: 'have' | 'missing' | undefined (steuert die Markierung/Farbe).
const OfferBullets = ({ items, onChange, placeholder, variant, addLabel = 'Punkt hinzufügen' }) => {
  const update = (i, val) => onChange(items.map((it, idx) => (idx === i ? val : it)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, '']);
  const mark =
    variant === 'have' ? (
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
    ) : variant === 'missing' ? (
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
    ) : null;
  return (
    <div className={`rp-offer-bullets ${variant ? `rp-offer-bullets-${variant}` : ''}`}>
      {items.map((it, i) => (
        <div key={i} className="rp-offer-bullet">
          <span className="rp-offer-bullet-mark">{mark}</span>
          <AutoGrowTextarea
            className="rp-offer-bullet-input"
            value={it}
            placeholder={placeholder}
            onChange={(e) => update(i, e.target.value)}
          />
          <button type="button" className="rp-offer-bullet-del" onClick={() => remove(i)} title="Entfernen" aria-label="Entfernen">×</button>
        </div>
      ))}
      <button type="button" className="rp-offer-bullet-add" onClick={add}>
        <span>+</span> {addLabel}
      </button>
    </div>
  );
};

// Rahmen einer Angebots-Kachel (runder Icon-Container, Label, Nummer, Glow).
const OfferCard = ({ metaKey, num, filled, wide, children }) => {
  const meta = OFFER_META[metaKey];
  return (
    <div
      className={`rp-offer-field ${filled ? 'rp-offer-filled' : 'rp-offer-empty'} ${wide ? 'rp-offer-wide' : ''}`}
      style={{ '--offer-tone': meta.tone }}
    >
      <div className="rp-offer-glow" />
      <div className="rp-offer-head">
        <span className="rp-offer-icon">{meta.icon}</span>
        <span className="rp-offer-label">{meta.label}</span>
        <span className="rp-offer-num">{num}</span>
      </div>
      {children}
    </div>
  );
};

// Editor für die No-Brainer-Offers: mehrere Angebote (Titel + Text), jeweils
// optional als "Idee"/Konzept markierbar. Plus KI-Brainstorming für frische Ideen.
const NoBrainerOffersEditor = ({ items, onChange }) => {
  const list = Array.isArray(items) ? items : [];
  const update = (i, patch) => onChange(list.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => onChange([...list, { title: '', text: '', idea: false }]);
  return (
    <div className="rp-nbo">
      {list.length === 0 && (
        <span className="rp-offer-flag">Noch kein No-Brainer-Offer hinterlegt – manuell ergänzen</span>
      )}
      {list.map((it, i) => (
        <div key={i} className={`rp-nbo-item ${it.idea ? 'rp-nbo-idea' : ''}`}>
          <div className="rp-nbo-item-head">
            <input
              className="rp-nbo-title"
              value={it.title || ''}
              placeholder="Titel des Angebots…"
              onChange={(e) => update(i, { title: e.target.value })}
            />
            <button
              type="button"
              className={`rp-nbo-tag ${it.idea ? 'is-on' : ''}`}
              onClick={() => update(i, { idea: !it.idea })}
              title={it.idea ? 'Als Idee/Konzept markiert – klicken zum Entfernen' : 'Als Idee/Konzept markieren'}
            >
              {it.idea ? 'Idee' : 'als Idee'}
            </button>
            <button type="button" className="rp-nbo-del" onClick={() => remove(i)} title="Entfernen" aria-label="Entfernen">×</button>
          </div>
          <AutoGrowTextarea
            className="rp-offer-input rp-nbo-text"
            value={it.text || ''}
            placeholder="Konkretes Einstiegsangebot, das schwer abzulehnen ist…"
            onChange={(e) => update(i, { text: e.target.value })}
          />
        </div>
      ))}
      <div className="rp-nbo-actions">
        <button type="button" className="rp-offer-bullet-add" onClick={add}>
          <span>+</span> Angebot hinzufügen
        </button>
      </div>
    </div>
  );
};

// ── RESEARCH-TAB (editierbar) ──
const ResearchTab = ({ report, angles, setAngles, offer, setOffer, onOpenImpact }) => {
  const { coldOutreach, channelStrategy } = report.research;
  const updateOffer = (key, val) => setOffer((o) => ({ ...o, [key]: val }));
  const updateUsp = (key, val) => setOffer((o) => ({ ...o, usp: { ...o.usp, [key]: val } }));
  const updateSales = (key, val) => setOffer((o) => ({ ...o, salesSetup: { ...o.salesSetup, [key]: val } }));
  const offerUsp = offer.usp || { text: '', points: [] };
  const offerSales = offer.salesSetup || { have: [], missing: [] };
  const uspFilled = (offerUsp.text || '').trim().length > 0 || (offerUsp.points || []).some((p) => (p || '').trim());
  const salesFilled = (offerSales.have || []).some((p) => (p || '').trim()) || (offerSales.missing || []).some((p) => (p || '').trim());
  const nboList = Array.isArray(offer.noBrainerOffers) ? offer.noBrainerOffers : [];
  const nboFilled = nboList.some((it) => (it.title || '').trim() || (it.text || '').trim());
  const updateAngle = (i, key, val) => setAngles((list) => list.map((a, idx) => (idx === i ? { ...a, [key]: val } : a)));
  const removeAngle = (i) => setAngles((list) => list.filter((_, idx) => idx !== i));
  const addAngle = () => setAngles((list) => [...list, { title: '', body: '' }]);
  const coPct = Math.round((coldOutreach.score / coldOutreach.max) * 100);
  const coTone = fitTone(coPct);

  // E-Mail Cold Outreach ist der wichtigste Kanal (Prio 1): eigenen Wert aus der
  // Kanal-Strategie ziehen und als zweite, hervorgehobene Bar zeigen, auch wenn das
  // Gesamtpotential niedriger ausfällt.
  const strategyChannels = Array.isArray(channelStrategy?.channels) ? channelStrategy.channels : [];
  const emailChannel = strategyChannels.find((c) => /e-?mail/i.test(c?.name || ''));
  const emailFit = emailChannel ? clamp(emailChannel.fit, 0, 100) : null;
  const emailTone = emailFit != null ? fitTone(emailFit) : 'high';

  return (
    <div className="rp-tab">
      <section className="rp-block rp-offer-block">
        <div className="rp-offer-grid">
          {/* 01 – Was wird verkauft? (Kurztext) */}
          <OfferCard metaKey="whatIsSold" num="01" filled={(offer.whatIsSold || '').trim().length > 0}>
            <MarkableText
              className="rp-offer-input"
              value={offer.whatIsSold || ''}
              placeholder="Produkte / Leistungen… (Satz markieren und mit Cmd/Strg+B fett hervorheben)"
              onChange={(e) => updateOffer('whatIsSold', e.target.value)}
            />
            {!(offer.whatIsSold || '').trim() && <span className="rp-offer-flag">Nicht im Info-Dump gefunden</span>}
          </OfferCard>

          {/* 02 – USP's: kurzer Satz + 2-4 Bullet Points */}
          <OfferCard metaKey="usp" num="02" filled={uspFilled}>
            <AutoGrowTextarea
              className="rp-offer-input rp-offer-lead"
              value={offerUsp.text || ''}
              placeholder="Ein kurzer Satz zur Differenzierung…"
              onChange={(e) => updateUsp('text', e.target.value)}
            />
            <OfferBullets
              items={offerUsp.points || []}
              onChange={(pts) => updateUsp('points', pts)}
              placeholder="USP-Stichpunkt…"
              addLabel="USP hinzufügen"
            />
          </OfferCard>

          {/* 03 – Sales-Aufstellung: was vorhanden ist / was fehlt */}
          <OfferCard metaKey="salesSetup" num="03" wide filled={salesFilled}>
            <div className="rp-offer-cols">
              <div className="rp-offer-col rp-offer-col-have">
                <span className="rp-offer-col-head">
                  <span className="rp-offer-col-dot" /> Vorhanden
                </span>
                <OfferBullets
                  items={offerSales.have || []}
                  onChange={(arr) => updateSales('have', arr)}
                  placeholder="Was ist im Vertrieb schon da?…"
                  variant="have"
                  addLabel="Vorhandenes ergänzen"
                />
              </div>
              <div className="rp-offer-divider" aria-hidden="true">
                <span className="rp-offer-vs">VS</span>
              </div>
              <div className="rp-offer-col rp-offer-col-missing">
                <span className="rp-offer-col-head">
                  <span className="rp-offer-col-dot" /> Potentiale
                </span>
                <OfferBullets
                  items={offerSales.missing || []}
                  onChange={(arr) => updateSales('missing', arr)}
                  placeholder="Wo liegt noch ungenutztes Potential?…"
                  variant="missing"
                  addLabel="Potential ergänzen"
                />
              </div>
            </div>
          </OfferCard>

          {/* 04 – No-Brainer-Offer: mehrere Angebote/Ideen + KI-Brainstorming */}
          <OfferCard metaKey="noBrainerOffer" num="04" wide filled={nboFilled}>
            <NoBrainerOffersEditor
              items={nboList}
              onChange={(arr) => updateOffer('noBrainerOffers', arr)}
            />
          </OfferCard>
        </div>
      </section>

      <section className="rp-block">
        <SectionHead eyebrow="Cold Outreach Potential" title="Wie gut lässt sich die Zielgruppe erreichen?" />
        <div className={`rp-outreach-card rp-tone-${coTone}`}>
          <div className="rp-outreach-glow" />
          {emailFit != null && (
            <div className={`rp-email-prio rp-tone-${emailTone}`}>
              <div className="rp-email-prio-head">
                <span className="rp-email-prio-label">E-Mail Cold Outreach</span>
                <span className="rp-email-prio-value">{emailFit}<small>%</small></span>
              </div>
              <div className="rp-outreach-bar rp-email-prio-bar">
                <div className="rp-outreach-fill" style={{ width: `${emailFit}%` }} />
              </div>
              <p className="rp-email-prio-note">
                {emailChannel.recommendation
                  || 'E-Mail Cold Outreach ist für diese Zielgruppe der wichtigste und direkteste Kanal.'}
              </p>
            </div>
          )}

          <ChannelStrategy data={channelStrategy} />
        </div>

        {onOpenImpact && (
          <div className="rp-impact-cta">
            <button type="button" className="rp-impact-cta-btn" onClick={onOpenImpact}>
              <span className="rp-impact-cta-text">
                <span className="rp-impact-cta-title">Impact &amp; Wert berechnen</span>
                <span className="rp-impact-cta-sub">Wirtschaftliche Stellschrauben anpassen und den ROI live durchspielen</span>
              </span>
              <span className="rp-impact-cta-arrow" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="M13 6l6 6-6 6" /></svg>
              </span>
            </button>
          </div>
        )}
      </section>

      <section className="rp-block">
        <SectionHead
          eyebrow="Outreach-Winkel"
          title="Stärkste Angles für die Ansprache"
          sub="Angles bearbeiten, entfernen oder ergänzen – oben im Header speichern."
        />
        <div className="rp-cards rp-cards--equal">
          {angles.map((a, i) => (
            <div key={i} className="rp-card rp-card-editable">
              <button
                type="button"
                className="rp-card-remove"
                onClick={() => removeAngle(i)}
                title="Angle entfernen"
                aria-label="Angle entfernen"
              >
                ×
              </button>
              <span className="rp-card-num">{String(i + 1).padStart(2, '0')}</span>
              <AutoGrowTextarea
                className="rp-card-title-input"
                value={a.title}
                placeholder="Angle-Titel…"
                onChange={(e) => updateAngle(i, 'title', e.target.value)}
              />
              <AutoGrowTextarea
                className="rp-card-body-input"
                value={a.body}
                placeholder="Warum zieht dieser Angle?…"
                onChange={(e) => updateAngle(i, 'body', e.target.value)}
              />
            </div>
          ))}
        </div>
        <button type="button" className="rp-card-add rp-card-add--row" onClick={addAngle}>
          <span className="rp-card-add-icon">+</span>
          Angle hinzufügen
        </button>
      </section>
    </div>
  );
};

// ── Setup-Details Inhalt ──
// Die Inhalte sind voll editierbar und werden mit dem Report gespeichert
// (report.kickoff). Der Default-Inhalt lebt in reportSchema.js (DEFAULT_KICKOFF).
// Struktur: { checklist:[{label,done}], details:[{label,prompt,value,done,
// important,secure,note}], notes:[{title,body}] }.
// Diese Hilfsfunktion stellt sicher, dass ein geladener/Demo-Report immer eine
// valide Struktur hat und migriert alte Reports (intro/sections/outro) sanft.
const normalizeKickoff = (raw) => {
  const base = raw && typeof raw === 'object' ? raw : {};
  const fallback = DEFAULT_KICKOFF;

  // Setup-Checkliste (oben): einfache To-Dos mit Häkchen.
  const checklist = Array.isArray(base.checklist)
    ? base.checklist
        .filter((c) => c && typeof c === 'object')
        .map((c) => ({ label: String(c.label ?? ''), done: !!c.done }))
    : fallback.checklist.map((c) => ({ ...c }));

  // Hochgeladene Dateien eines Detail-Felds robust normalisieren.
  const normAttachments = (arr) =>
    Array.isArray(arr)
      ? arr
          .filter((a) => a && typeof a === 'object' && a.url)
          .map((a) => ({
            name: String(a.name || 'Datei'),
            url: String(a.url),
            type: String(a.type || ''),
            size: Number.isFinite(a.size) ? a.size : 0,
          }))
      : [];

  // Benötigte Infos (Info-Hub): Titel + Hinweis + Wert (Textfeld oder Pills) +
  // optionale Datei-Uploads (z. B. Referenztexte, Signatur als Bild/HTML).
  const normDetail = (d) => {
    const type = d.type === 'pills' ? 'pills' : 'text';
    const tags = Array.isArray(d.tags) ? d.tags.map((t) => String(t)).filter(Boolean) : [];
    return {
      label: String(d.label ?? ''),
      prompt: String(d.prompt ?? ''),
      type,
      value: String(d.value ?? ''),
      tags,
      attachments: normAttachments(d.attachments),
    };
  };
  let details;
  if (Array.isArray(base.details)) {
    details = base.details.filter((d) => d && typeof d === 'object').map(normDetail);
  } else if (Array.isArray(base.sections)) {
    // Migration aus altem Format (sections[].items) → flache Detail-Liste.
    details = base.sections.flatMap((s) =>
      Array.isArray(s?.items)
        ? s.items
            .filter((it) => it && typeof it === 'object')
            .map((it) => normDetail({ label: it.label, prompt: it.text }))
        : []
    );
  } else {
    details = fallback.details.map((d) => ({ ...d, tags: [...(d.tags || [])], attachments: [...(d.attachments || [])] }));
  }

  const notes = Array.isArray(base.notes)
    ? base.notes
        .filter((n) => n && typeof n === 'object')
        .map((n) => ({
          title: String(n.title ?? ''),
          body: String(n.body ?? ''),
          // Stage, aus der die Infos gedroppt wurden (z. B. "Präsentations-Call"),
          // plus Zeitpunkt – optional, nur bei via Call-Info-Drop erzeugten Notes.
          ...(n.stage ? { stage: String(n.stage) } : {}),
          ...(typeof n.droppedAt === 'number' ? { droppedAt: n.droppedAt } : {}),
        }))
    : [];

  return { checklist, details, notes };
};

// Wörter pro E-Mail zählen (für den Wortzähler im Vault)
const countWords = (s = '') => {
  const t = (s || '').trim();
  return t ? t.split(/\s+/).length : 0;
};

// Info-Dump → einzelne Anmerkungen [{ title, body }].
// Erkennt automatisch: Absätze (durch Leerzeilen getrennt), Aufzählungen
// (-, *, •, 1.) sowie „Thema: Inhalt" / „Thema - Inhalt" (Titel wird abgeleitet).
const parseDumpToNotes = (raw) => {
  const text = (raw || '').replace(/\r\n/g, '\n').trim();
  if (!text) return [];
  // 1) primär an Leerzeilen in Blöcke teilen
  let blocks = text.split(/\n\s*\n+/).map((b) => b.trim()).filter(Boolean);
  // 2) ein einziger Block mit mehreren Zeilen → jede Zeile als eigene Anmerkung
  if (blocks.length === 1) {
    const lines = blocks[0].split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines.length > 1) blocks = lines;
  }
  return blocks
    .map((block) => {
      const lines = block
        .split('\n')
        .map((l) => l.replace(/^\s*[-*•·‣◦]+\s*/, '').replace(/^\s*\d+[.)\]]\s*/, '').trim())
        .filter(Boolean);
      if (!lines.length) return null;
      const first = lines[0];
      let title = '';
      let bodyLines = lines;
      const m = first.match(/^([^:–—-]{2,60})\s*[:–—-]\s+(.+)$/);
      if (m) {
        // „Thema: Inhalt" → Titel + Rest als Body
        title = m[1].trim();
        bodyLines = [m[2].trim(), ...lines.slice(1)];
      } else if (lines.length > 1 && first.length <= 64 && !/[.!?]$/.test(first)) {
        // kurze Überschriftszeile ohne Satzzeichen → Titel
        title = first;
        bodyLines = lines.slice(1);
      }
      return { title, body: bodyLines.join('\n').trim() };
    })
    .filter((n) => n && (n.title || n.body));
};

// Pill-Eingabe für Mehrfach-Werte (z. B. Domain-Auswahl): jeder Wert wird eine
// Pill, mehrere lassen sich frei anlegen & einzeln entfernen.
const DetailPills = ({ tags, onChange, placeholder }) => {
  const [draft, setDraft] = useState('');
  const list = Array.isArray(tags) ? tags : [];

  const commit = () => {
    const parts = draft.split(/[,\n;]+/).map((s) => s.trim()).filter(Boolean);
    if (!parts.length) { setDraft(''); return; }
    const next = [...list];
    parts.forEach((p) => {
      if (!next.some((t) => t.toLowerCase() === p.toLowerCase())) next.push(p);
    });
    onChange(next);
    setDraft('');
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));

  return (
    <div className="rp-pillfield">
      {list.map((t, i) => (
        <span key={i} className="rp-pill">
          {t}
          <button type="button" className="rp-pill-x" onClick={() => remove(i)} aria-label={`${t} entfernen`}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </span>
      ))}
      <input
        className="rp-pill-input"
        value={draft}
        placeholder={list.length ? 'Weitere…' : (placeholder || 'Wert eingeben + Enter…')}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(); }
          else if (e.key === 'Backspace' && !draft && list.length) remove(list.length - 1);
        }}
        onBlur={commit}
      />
    </div>
  );
};

// Dateigröße menschenlesbar formatieren (für Attachment-Chips).
const formatBytes = (n) => {
  if (!n || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};
const isImageAttachment = (t) => typeof t === 'string' && t.startsWith('image/');

// Akzeptierte Datei-Endungen/Typen für Setup-Detail-Uploads (Bilder, PDF, HTML,
// gängige Office-/Text-Dokumente). Muss zur Backend-Whitelist passen.
const ATTACH_ACCEPT =
  '.png,.jpg,.jpeg,.gif,.webp,.svg,.bmp,.pdf,.html,.htm,.txt,.md,.csv,.doc,.docx,.xls,.xlsx,.rtf,image/*,application/pdf';

// Datei-Anhänge für ein Setup-Detail-Feld: hochladen, auflisten, entfernen.
// Funktioniert zusätzlich zum Text-/Pills-Feld (Kombination aus Text + Datei),
// z. B. Signatur als Bild/HTML oder Referenz-Mails als PDF. Im Demo-Modus (kein
// onUpload) ist der Upload deaktiviert.
const DetailAttachments = ({ attachments, onChange, onUpload }) => {
  const list = Array.isArray(attachments) ? attachments : [];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (!onUpload) { setError('Uploads sind im Demo-Modus nicht verfügbar.'); return; }
    setError('');
    setBusy(true);
    try {
      const uploaded = [];
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const att = await onUpload(file);
        if (att && att.url) uploaded.push(att);
      }
      if (uploaded.length) onChange([...list, ...uploaded]);
    } catch (e) {
      setError(e.message || 'Upload fehlgeschlagen.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));

  return (
    <div className="rp-attach">
      {list.length > 0 && (
        <div className="rp-attach-list">
          {list.map((a, i) => (
            <div key={i} className="rp-attach-item" title={a.name}>
              {isImageAttachment(a.type) ? (
                <a className="rp-attach-thumb" href={a.url} target="_blank" rel="noreferrer">
                  <img src={a.url} alt={a.name} />
                </a>
              ) : (
                <span className="rp-attach-icon" aria-hidden="true">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6" />
                  </svg>
                </span>
              )}
              <a className="rp-attach-name" href={a.url} target="_blank" rel="noreferrer">{a.name}</a>
              {a.size ? <span className="rp-attach-size">{formatBytes(a.size)}</span> : null}
              <button
                type="button"
                className="rp-attach-del"
                onClick={() => remove(i)}
                title="Datei entfernen"
                aria-label={`${a.name} entfernen`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="rp-attach-actions">
        <button
          type="button"
          className="rp-attach-add"
          onClick={() => inputRef.current && inputRef.current.click()}
          disabled={busy || !onUpload}
          title={onUpload ? 'Datei hochladen (Bild, PDF, HTML oder Dokument)' : 'Im Demo-Modus nicht verfügbar'}
        >
          {busy ? (<><span className="rp-attach-spin" />Lädt …</>) : (<><span className="rp-setup-add-icon">+</span>Datei anhängen</>)}
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ATTACH_ACCEPT}
          className="rp-attach-input"
          onChange={(e) => handleFiles(e.target.files)}
        />
        <span className="rp-attach-hint">Bild, PDF, HTML oder Dokument · max. 5 MB</span>
      </div>
      {error && <p className="rp-attach-error">{error}</p>}
    </div>
  );
};

// Kompakter Fortschrittsbalken (Apple-like) für die Setup-Sektionen.
const SetupMeter = ({ done, total, noun }) => {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="rp-meter" title={`${done} von ${total} ${noun}`}>
      <div className="rp-meter-track">
        <span className={`rp-meter-fill ${pct === 100 ? 'rp-meter-full' : ''}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="rp-meter-label">{done}/{total}</span>
    </div>
  );
};

// Toggle-Button für eine Bulk-Import-Box (im Sektion-Header).
const ImportToggle = ({ open, onClick }) => (
  <button type="button" className={`rp-import-toggle ${open ? 'rp-import-toggle-on' : ''}`} onClick={onClick}>
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
    </svg>
    Info-Dump
  </button>
);

// Wiederverwendbarer Info-Dump-Import: rohen Text einfügen, regelbasiert oder per
// KI in einzelne Blöcke {title, body} sortieren. Mapping/Mergen übernimmt der
// Aufrufer via onApply – so kann dieselbe Box Notizen ODER Detail-Felder befüllen.
const BulkImport = ({ open, onClose, parse, aiSort, onApply, placeholder, noun = 'Block', nounPl = 'Blöcke' }) => {
  const [dump, setDump] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const count = useMemo(() => parse(dump).length, [dump, parse]);
  if (!open) return null;

  const apply = (parsed) => {
    if (!parsed || !parsed.length) return;
    onApply(parsed);
    setDump('');
    setError('');
    onClose();
  };
  const runLocal = () => apply(parse(dump));
  const runAi = async () => {
    if (busy) return;
    if (dump.trim().length < 10) { setError('Bitte etwas mehr Text einfügen.'); return; }
    setBusy(true);
    setError('');
    try {
      const res = await aiSort(dump);
      if (!res.length) setError('Die KI hat nichts erkannt – versuche es regelbasiert.');
      else apply(res);
    } catch (e) {
      setError(e.message || 'KI-Import fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rp-import">
      <AutoGrowTextarea
        className="rp-import-dump"
        value={dump}
        placeholder={placeholder}
        onChange={(e) => { setDump(e.target.value); if (error) setError(''); }}
        disabled={busy}
      />
      {error && <span className="rp-import-error">{error}</span>}
      <div className="rp-import-actions">
        <span className="rp-import-hint">
          {busy
            ? 'KI sortiert den Dump …'
            : count > 0
            ? `${count} ${count === 1 ? noun : nounPl} regelbasiert erkannt`
            : 'Text einfügen und sortieren lassen'}
        </span>
        <div className="rp-import-btns">
          {dump && !busy && (
            <button type="button" className="rp-import-clear" onClick={() => { setDump(''); setError(''); }}>Leeren</button>
          )}
          <button type="button" className="rp-import-secondary" onClick={runLocal} disabled={busy || count === 0} title="Ohne KI, rein regelbasiert sortieren">
            Regelbasiert
          </button>
          <button type="button" className="rp-import-run" onClick={runAi} disabled={busy || dump.trim().length < 10}>
            {busy ? (<><span className="rp-import-spin" />Sortiert …</>) : 'Mit KI sortieren'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Reporting-Ansicht: 30-Tage-Outreach-Report des Outreach-Managers ──
// Kurzes Frageformular (Freitext) + optionale Kennzahlen. Wird über die „+"-
// Ansicht „Reporting" eingeblendet und über den öffentlichen Report-Link
// (Keyword-Passwort) ausgefüllt. Antworten werden direkt im Report gespeichert
// (Auto-Save) und sind intern UND für den Kunden sichtbar.
// Kleine Inline-Icons für die Reporting-Ansicht (jede Kennzahl/Frage bekommt ein
// eigenes Symbol – sorgt zusammen mit den Akzentfarben für mehr Dynamik).
const RP_ICON_PATHS = {
  users: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75',
  reply: 'M9 17l-5-5 5-5 M4 12h11a5 5 0 0 1 5 5v2',
  calendar: 'M8 2v4 M16 2v4 M3 10h18 M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
  overview: 'M3 3h7v7H3z M14 3h7v7h-7z M14 14h7v7h-7z M3 14h7v7H3z',
  trophy: 'M8 21h8 M12 17v4 M7 4h10v5a5 5 0 0 1-10 0V4z M5 9a2 2 0 0 1-2-2V5h2 M19 9a2 2 0 0 0 2-2V5h-2',
  alert: 'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z M12 9v4 M12 17h.01',
  bulb: 'M9 18h6 M10 22h4 M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.3h6c0-1 .4-1.8 1-2.3A7 7 0 0 0 12 2z',
  arrow: 'M5 12h14 M13 6l6 6-6 6',
  link: 'M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1 M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1',
  file: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6',
  chart: 'M3 3v18h18 M7 15l3-4 3 3 5-7',
  rocket: 'M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0 M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5',
  lock: 'M5 11h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2z M7 11V7a5 5 0 0 1 10 0v4',
  check: 'M20 6 9 17l-5-5',
};
const RpIcon = ({ name, size = 16 }) => {
  const d = RP_ICON_PATHS[name];
  if (!d) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
};

// Kennzahlen & Fragen jeweils mit eigener Akzentfarbe (RGB-Triple) und Icon –
// damit die Reporting-Ansicht lebendig wirkt statt uniform grau.
const REPORTING_METRICS = [
  { id: 'contacted', label: 'Nachrichten gesendet', placeholder: 'z. B. 120', accent: '90, 169, 255', icon: 'users', hint: 'inkl. Follow-ups' },
  { id: 'replies', label: 'Antworten', placeholder: 'z. B. 18', accent: '167, 139, 250', icon: 'reply' },
  { id: 'meetings', label: 'Termine', placeholder: 'z. B. 5', accent: '52, 211, 153', icon: 'calendar' },
];
const REPORTING_QUESTIONS = [
  { id: 'summary', label: 'Wie liefen die ersten 30 Tage?', sub: 'Kurzer Überblick über den Start des Outreach.', placeholder: 'Knapper Überblick: Was wurde aufgesetzt, wie ist der Outreach angelaufen …', accent: '90, 169, 255', icon: 'overview' },
  { id: 'wins', label: 'Was hat am besten funktioniert?', sub: 'Erfolgreiche Angles, Kanäle oder Segmente.', placeholder: 'Welche Ansätze, Zielgruppen oder Nachrichten haben am besten gezogen …', accent: '52, 211, 153', icon: 'trophy' },
  { id: 'challenges', label: 'Größte Herausforderungen?', sub: 'Wo gab es Reibung oder schwache Ergebnisse.', placeholder: 'Was hat nicht funktioniert oder war schwieriger als gedacht …', accent: '251, 146, 60', icon: 'alert' },
  { id: 'learnings', label: 'Wichtigste Learnings', sub: 'Was wir jetzt über Zielgruppe & Markt besser wissen.', placeholder: 'Erkenntnisse über die Zielgruppe, Pains, Ansprache, Timing …', accent: '167, 139, 250', icon: 'bulb' },
  { id: 'recommendations', label: 'Empfehlungen & nächste Schritte', sub: 'Was als Nächstes angepasst oder getan werden sollte.', placeholder: 'Konkrete nächste Schritte und Optimierungsvorschläge …', accent: '244, 114, 182', icon: 'arrow' },
];

// ── Zusätzliche Reportings (nach Abschluss) ──
// Erscheint erst, wenn das Reporting als abgeschlossen markiert ist. Erlaubt das
// Hinterlegen weiterer Reportings als PDF/Dokument (Upload) oder als Link.
const ReportingExtras = ({ items, setItems, onUpload, standalone = false }) => {
  const [linkTitle, setLinkTitle] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);
  const list = Array.isArray(items) ? items : [];

  const addLink = () => {
    const url = linkUrl.trim();
    if (!url) { setError('Bitte einen Link einfügen.'); return; }
    const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    setItems((p) => [...(Array.isArray(p) ? p : []), { kind: 'link', title: linkTitle.trim() || normalized, url: normalized, addedAt: Date.now() }]);
    setLinkTitle('');
    setLinkUrl('');
    setError('');
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (!onUpload) { setError('Uploads sind im Demo-Modus nicht verfügbar.'); return; }
    setError('');
    setBusy(true);
    try {
      const uploaded = [];
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const att = await onUpload(file);
        if (att && att.url) uploaded.push({ kind: 'file', title: att.name, url: att.url, name: att.name, type: att.type || '', size: att.size || 0, addedAt: Date.now() });
      }
      if (uploaded.length) setItems((p) => [...(Array.isArray(p) ? p : []), ...uploaded]);
    } catch (e) {
      setError(e.message || 'Upload fehlgeschlagen.');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = (i) => setItems((p) => (Array.isArray(p) ? p : []).filter((_, idx) => idx !== i));

  return (
    <section className="rp-block rp-extra">
      <SectionHead
        eyebrow={standalone ? undefined : 'Nach Abschluss'}
        title={standalone ? 'Reporting hochladen' : 'Zusätzliche Reportings'}
        sub="Reportings als PDF/Dokument hochladen oder als Link hinterlegen – z. B. Folge-Reports, Dashboards oder externe Auswertungen."
      />

      {list.length > 0 && (
        <div className="rp-extra-list">
          {list.map((it, i) => (
            <div key={i} className={`rp-extra-item rp-extra-${it.kind === 'link' ? 'link' : 'file'}`}>
              <span className="rp-extra-ico"><RpIcon name={it.kind === 'link' ? 'link' : 'file'} size={16} /></span>
              <a className="rp-extra-name" href={it.url} target="_blank" rel="noreferrer" title={it.title || it.name || it.url}>
                {it.title || it.name || it.url}
              </a>
              {it.kind === 'file' && it.size
                ? <span className="rp-extra-size">{formatBytes(it.size)}</span>
                : <span className="rp-extra-badge">{it.kind === 'link' ? 'Link' : 'Datei'}</span>}
              <button type="button" className="rp-extra-del" onClick={() => remove(i)} aria-label="Entfernen" title="Entfernen">×</button>
            </div>
          ))}
        </div>
      )}

      <div className="rp-extra-add">
        <div className="rp-extra-linkrow">
          <input
            className="rp-extra-input rp-extra-input-title"
            type="text"
            value={linkTitle}
            placeholder="Titel (optional)"
            onChange={(e) => setLinkTitle(e.target.value)}
          />
          <input
            className="rp-extra-input rp-extra-input-url"
            type="url"
            value={linkUrl}
            placeholder="https://…"
            onChange={(e) => { setLinkUrl(e.target.value); if (error) setError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLink(); } }}
          />
          <button type="button" className="rp-extra-addlink" onClick={addLink}>
            <RpIcon name="link" size={14} />Link speichern
          </button>
        </div>
        <span className="rp-extra-or">oder</span>
        <button
          type="button"
          className="rp-extra-upload"
          onClick={() => fileRef.current && fileRef.current.click()}
          disabled={busy || !onUpload}
          title={onUpload ? 'PDF oder Dokument hochladen (max. 5 MB)' : 'Im Demo-Modus nicht verfügbar'}
        >
          {busy ? (<><span className="rp-attach-spin" />Lädt …</>) : (<><RpIcon name="file" size={14} />PDF hochladen</>)}
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept={ATTACH_ACCEPT}
          className="rp-attach-input"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>
      {error && <p className="rp-attach-error">{error}</p>}
    </section>
  );
};

// Verbleibende Zeit als kompakter Countdown ("3 Tage 4 Std" / "5 Std 12 Min").
const fmtReportingCountdown = (targetMs, now) => {
  const diff = targetMs - now;
  if (diff <= 0) return null;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (d > 0) return `${d} ${d === 1 ? 'Tag' : 'Tage'} ${h} Std`;
  if (h > 0) return `${h} Std ${m} Min`;
  return `${m} Min`;
};

const reportingMonthLabel = (rep) => {
  const ms = rep && (rep.periodEnd || rep.availableAt);
  if (!ms) return '';
  return new Date(ms).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
};

const fmtReportingDate = (ms) =>
  ms ? new Date(ms).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

// ── Eingabe-Formular für ein einzelnes monatliches Reporting ──
// Eigene Komponente, damit der lokale State über den period-key zurückgesetzt wird.
const MonthlyReportingForm = ({ rep, onSubmit, onCancel, uploadAttachment, isHistory }) => {
  // 0 ist ein gültiger Wert (Verträge, bei denen jeder Termin abgerechnet wird).
  const included = typeof rep.included === 'number' ? rep.included : 4;
  const [qualified, setQualified] = useState(
    typeof rep.qualifiedAppointments === 'number' ? String(rep.qualifiedAppointments) : String(included)
  );
  const [submittedBy, setSubmittedBy] = useState(rep.submittedBy || '');
  const [answers, setAnswers] = useState(() => {
    const a = {};
    REPORTING_QUESTIONS.forEach((q) => { a[q.id] = rep[q.id] || ''; });
    return a;
  });
  const [metrics, setMetrics] = useState(() => {
    const m = (rep.metrics && typeof rep.metrics === 'object') ? rep.metrics : {};
    const out = {};
    REPORTING_METRICS.forEach((x) => { out[x.id] = m[x.id] || ''; });
    return out;
  });
  // Optionales Dokument (PDF/Link) – reuse der Extras-Liste, gespeichert wird die erste URL.
  const [docs, setDocs] = useState(() => (rep.reportUrl ? [{ kind: 'file', title: 'Reporting-Dokument', url: rep.reportUrl }] : []));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const q = Math.max(0, Math.round(Number(qualified) || 0));
  const extra = Math.max(0, q - included);

  const submit = async () => {
    if (!onSubmit) return;
    setSaving(true);
    setError('');
    try {
      await onSubmit(rep.periodKey, {
        qualifiedAppointments: q,
        submittedBy: submittedBy.trim(),
        summary: (answers.summary || '').trim(),
        wins: (answers.wins || '').trim(),
        challenges: (answers.challenges || '').trim(),
        learnings: (answers.learnings || '').trim(),
        recommendations: (answers.recommendations || '').trim(),
        metrics: {
          contacted: (metrics.contacted || '').trim(),
          replies: (metrics.replies || '').trim(),
          meetings: (metrics.meetings || '').trim(),
        },
        reportUrl: (docs[0] && docs[0].url) || '',
      });
      if (onCancel) onCancel();
    } catch (e) {
      setError(e.message || 'Speichern fehlgeschlagen.');
      setSaving(false);
    }
  };

  return (
    <section className="rp-block rp-mrep-form">
      <div className="rp-mrep-qual">
        <div className="rp-mrep-qual-head">
          <span className="rp-mrep-qual-label">Qualifizierte Termine in diesem Zeitraum</span>
          <span className="rp-mrep-qual-hint">{included} inklusive · ab dem {included + 1}. zählt extra</span>
        </div>
        <div className="rp-mrep-stepper">
          <button type="button" className="rp-mrep-step" onClick={() => setQualified(String(Math.max(0, q - 1)))} aria-label="Weniger">−</button>
          <input
            className="rp-mrep-qual-input"
            type="number"
            min="0"
            inputMode="numeric"
            value={qualified}
            onChange={(e) => setQualified(e.target.value)}
          />
          <button type="button" className="rp-mrep-step" onClick={() => setQualified(String(q + 1))} aria-label="Mehr">+</button>
        </div>
        {extra > 0 && (
          <span className="rp-mrep-extra-badge">+{extra} Extra-Termin{extra === 1 ? '' : 'e'} → fließt automatisch in Rechnung &amp; Team-Auszahlung</span>
        )}
      </div>

      <div className="rp-reporting-meta">
        <label className="rp-reporting-by">
          <span className="rp-reporting-by-label">Erstellt von</span>
          <input
            type="text"
            value={submittedBy}
            placeholder="Name des Outreach-Managers"
            onChange={(e) => setSubmittedBy(e.target.value)}
          />
        </label>
        <div className="rp-reporting-metrics">
          {REPORTING_METRICS.map((m) => (
            <label key={m.id} className="rp-reporting-metric" style={{ '--m-accent': m.accent }}>
              <span className="rp-reporting-metric-top">
                <span className="rp-reporting-metric-ico"><RpIcon name={m.icon} size={15} /></span>
                <span className="rp-reporting-metric-label">{m.label}</span>
                {m.hint && <span className="rp-reporting-metric-hint">{m.hint}</span>}
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={metrics[m.id] || ''}
                placeholder={m.placeholder}
                onChange={(e) => setMetrics((p) => ({ ...p, [m.id]: e.target.value }))}
              />
            </label>
          ))}
        </div>
      </div>

      <div className="rp-reporting-questions">
        {REPORTING_QUESTIONS.map((qq, idx) => (
          <div key={qq.id} className="rp-reporting-q" style={{ '--q-accent': qq.accent, animationDelay: `${idx * 70}ms` }}>
            <div className="rp-reporting-q-head">
              <span className="rp-reporting-q-ico"><RpIcon name={qq.icon} size={18} /></span>
              <div className="rp-reporting-q-heads">
                <span className="rp-reporting-q-label">{qq.label}</span>
                {qq.sub && <span className="rp-reporting-q-sub">{qq.sub}</span>}
              </div>
              <span className="rp-reporting-q-num">{String(idx + 1).padStart(2, '0')}</span>
            </div>
            <AutoGrowTextarea
              className="rp-reporting-area"
              value={answers[qq.id] || ''}
              placeholder={qq.placeholder}
              onChange={(e) => setAnswers((p) => ({ ...p, [qq.id]: e.target.value }))}
            />
          </div>
        ))}
      </div>

      <ReportingExtras items={docs} setItems={setDocs} onUpload={uploadAttachment} standalone />

      {error && <p className="rp-attach-error">{error}</p>}

      <div className="rp-mrep-actions">
        {isHistory && onCancel && (
          <button type="button" className="rp-mrep-cancel" onClick={onCancel} disabled={saving}>Abbrechen</button>
        )}
        <button type="button" className="rp-mrep-submit" onClick={submit} disabled={saving || !onSubmit}>
          {saving ? 'Wird gespeichert …' : isHistory ? 'Reporting aktualisieren' : 'Reporting einreichen'}
        </button>
      </div>
    </section>
  );
};

// ── Warm-up Report (täglicher Outreach-Hochlauf) ──────────────
// Zweite Reporting-Ansicht (Toggle „Warm-up Report"). Der Outreach-Manager
// trägt JEDEN WERKTAG ein, was lief: tatsächlich gesendete Mails, positive
// Antworten (optional), Bemerkungen und „Ideen für morgen". Versendet wird nur
// an Werktagen (Mo–Fr); der Hochlauf zählt daher in WERKTAGEN, nicht in Kalender-
// tagen. Über WARMUP_RAMP_DAYS Werktage steigt die geplante Mail-Frequenz linear
// von WARMUP_START_VOLUME bis zum einmalig gewählten Tagesziel. 20 Werktage ≈ 4
// Kalenderwochen und passen damit in den ersten 30-Tage-Abrechnungszyklus.
// Das Ziel ist die dauerhafte Tagesmenge ab Monat 2 und frei wählbar; der
// Hochlauf wird daraus dynamisch errechnet. Follow-ups erscheinen im Tagesplan
// als Orientierung (Batch von vor 1 bzw. 2 Wochen = 5 bzw. 10 Werktagen).
// ── Wiederkehrende Aufgaben der Experimentierphase ──
// Neben dem täglichen Eintrag (gesendet bestätigen, Bemerkungen, Ideen) gibt es
// die Pflicht-Aufgabe „Lade deine besten Texte hoch". Sie kommt JEDEN Werktag der
// Experimentierphase (Hochlauf) und verlangt mindestens 2 Texte – der Tag lässt
// sich ohne sie nicht speichern. Im Zielbetrieb danach nur noch wöchentlich
// (1 Text, ohne Pflicht). Rückgabe: { count } oder null.
const experimentTextTask = (dayIndex) => {
  const d = Number(dayIndex) || 0;
  if (d < 1) return null;
  if (d <= WARMUP_RAMP_DAYS) return { count: 2, required: true }; // jeden Werktag der Experimentierphase, Pflicht
  return d % 5 === 0 ? { count: 1 } : null; // Zielbetrieb: ~1x pro Woche, optional
};
// Tag-Label, mit dem ein in der Experimentierphase hochgeladener Text in den
// Account-Setup-E-Mail-Texten markiert wird.
const experimentTextTag = (dayIndex) => (dayIndex ? `Launch Phase · Tag ${dayIndex}` : 'Launch Phase');
// Stabile ID für hochgeladene Texte (für Dedup zwischen Warm-up-Tagen und den
// Account-Setup-E-Mail-Texten).
let emailIdSeq = 0;
const makeEmailId = () => `et_${Date.now().toString(36)}_${(emailIdSeq++).toString(36)}`;

// Stabile ID für Analyse-Firmeneinträge (Frontend-Liste / CSV-Import).
let analyseIdSeq = 0;
const makeAnalyseId = () => `an_${Date.now().toString(36)}_${(analyseIdSeq++).toString(36)}`;

// Mail-Konzepte im Analyse-Tab: Standard sind 2, es können bis zu 5 werden.
// Jede Mail trägt ein Typ-Tag (Intro-Mail vs. Follow-up), das klein angezeigt wird.
const MAX_ANALYSE_EMAILS = 5;
const ANALYSE_MAIL_KINDS = ['intro', 'followup'];
const ANALYSE_MAIL_KIND_LABEL = { intro: 'Intro-Mail', followup: 'Follow-up' };

// veed.io Share-/View-Link (oder eingefügten iframe-Code) in eine einbettbare
// Embed-URL umwandeln. Unterstützt veed.io & veed.dev, view/embed/e-Pfade.
const veedEmbedSrc = (input) => {
  const raw = (input || '').trim();
  if (!raw) return '';
  // Voller iframe-Code eingefügt → src herausziehen.
  const iframeSrc = raw.match(/<iframe[^>]*\ssrc=["']([^"']+)["']/i);
  const candidate = iframeSrc ? iframeSrc[1] : raw;
  // veed-Video-ID aus view/embed/e-Pfad extrahieren.
  const m = candidate.match(/veed\.(?:io|dev)\/(?:view|embed|e)\/([A-Za-z0-9_-]+)/i);
  if (m && m[1]) return `https://www.veed.io/embed/${m[1]}`;
  // Bereits eine veed-Embed-URL ohne erkennbaren Pfad? Dann unverändert nutzen.
  if (/veed\.(?:io|dev)\//i.test(candidate) && /\/embed\//i.test(candidate)) return candidate;
  return '';
};

// Simpler, robuster CSV-Parser: erkennt Trennzeichen (Komma/Semikolon/Tab),
// respektiert Anführungszeichen und liefert { headers, rows } (rows als Objekte
// mit Header-Keys). Für den Firmenlisten-Import im Analyse-Tab.
const parseCsv = (text) => {
  const clean = String(text || '').replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');
  if (!clean.trim()) return { headers: [], rows: [] };
  const firstLine = clean.split('\n')[0] || '';
  const count = (ch) => (firstLine.match(new RegExp(`\\${ch}`, 'g')) || []).length;
  const delim = count(';') > count(',') ? ';' : (count('\t') > count(',') ? '\t' : ',');
  const records = [];
  let field = '';
  let row = [];
  let inQuotes = false;
  for (let i = 0; i < clean.length; i++) {
    const c = clean[i];
    if (inQuotes) {
      if (c === '"') {
        if (clean[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === delim) {
      row.push(field); field = '';
    } else if (c === '\n') {
      row.push(field); records.push(row); field = ''; row = [];
    } else field += c;
  }
  if (field.length || row.length) { row.push(field); records.push(row); }
  const nonEmpty = records.filter((r) => r.some((v) => (v || '').trim()));
  if (!nonEmpty.length) return { headers: [], rows: [] };
  const headers = nonEmpty[0].map((h, i) => (h || '').trim() || `Spalte ${i + 1}`);
  const rows = nonEmpty.slice(1).map((r) => {
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (r[i] || '').trim(); });
    return obj;
  });
  return { headers, rows };
};

// State aus report.analyse aufbereiten (2 bis 5 Mail-Slots; Standard sind 2).
const buildAnalyseState = (raw) => {
  const a = (raw && typeof raw === 'object') ? raw : {};
  const emails = (Array.isArray(a.emails) ? a.emails : [])
    .map((e, i) => ({
      subject: String((e && e.subject) || ''),
      body: String((e && e.body) || ''),
      toName: String((e && e.toName) || ''),
      toCompany: String((e && e.toCompany) || ''),
      // Typ-Tag: 'intro' (Erstansprache) oder 'followup' (Nachfass). Default:
      // erste Mail = Intro, alle weiteren = Follow-up.
      kind: (e && e.kind === 'followup') ? 'followup' : (e && e.kind === 'intro') ? 'intro' : (i === 0 ? 'intro' : 'followup'),
    }))
    .slice(0, MAX_ANALYSE_EMAILS);
  while (emails.length < 2) emails.push({ subject: '', body: '', toName: '', toCompany: '', kind: emails.length === 0 ? 'intro' : 'followup' });
  return {
    video: typeof a.video === 'string' ? a.video : '',
    companies: (Array.isArray(a.companies) ? a.companies : []).map((c) => ({
      id: (c && c.id) || makeAnalyseId(),
      name: String((c && c.name) || ''),
      website: String((c && c.website) || ''),
      description: String((c && c.description) || ''),
      rating: Math.max(0, Math.min(100, Math.round(Number(c && c.rating) || 0))),
      verified: !!(c && c.verified),
      signals: (Array.isArray(c && c.signals) ? c.signals : []).map((s) => ({
        text: String((s && s.text) || ''),
        date: String((s && s.date) || ''),
        source: String((s && s.source) || ''),
        url: String((s && s.url) || ''),
      })).filter((s) => s.text),
    })),
    emails,
  };
};

// Optional per „+" hinzufügbare Kennzahlen neben „Gesendet" (erweiterbar).
const WARMUP_EXTRA_METRICS = [
  { id: 'positive', label: 'Positive Antworten', addLabel: 'Positive Antwort eingetroffen', accent: '52, 211, 153', icon: 'trophy', placeholder: 'z. B. 1' },
];

// ── Eingabe-Formular für genau einen Warm-up-Tag ──
// Eigene Komponente, damit der lokale State über den dayKey zurückgesetzt wird.
const WarmupDayForm = ({ dayKey, dayIndex, planned, followups, existing, onSubmit, isHistory, onCancel, textTask }) => {
  const plannedTotal = (Number(planned) || 0) + ((followups && followups.total) || 0);
  // „Gesendet" ist jetzt eine einfache Bestätigung (Checkbox) statt einer Zahl.
  const [sentDone, setSentDone] = useState(() => !!(existing && (existing.sentConfirmed || Number(existing.sentEmails) > 0)));
  const [positive, setPositive] = useState(existing && typeof existing.positiveReplies === 'number' ? String(existing.positiveReplies) : '');
  // Hochgeladene Texte dieses Tages (Aufgabe „Beste Texte hochladen"). Bestehende
  // Texte werden geladen, damit sie beim erneuten Speichern erhalten bleiben.
  const [texts, setTexts] = useState(() => {
    const list = existing && Array.isArray(existing.uploadedTexts) ? existing.uploadedTexts : [];
    const mapped = list
      .filter((t) => t && typeof t === 'object')
      .map((t) => ({ id: t.id || makeEmailId(), subject: t.subject || '', body: t.body || '' }));
    // Pflicht-Aufgabe: leere Karten vorausfüllen, damit die Felder sofort sichtbar sind.
    const need = (textTask && textTask.count) || 0;
    if (textTask && textTask.required && mapped.length < need) {
      const extra = Array.from({ length: need - mapped.length }, () => ({ id: makeEmailId(), subject: '', body: '' }));
      return [...mapped, ...extra];
    }
    return mapped;
  });
  const taskCount = (textTask && textTask.count) || 0;
  const taskRequired = !!(textTask && textTask.required);
  const showTextTask = taskCount > 0 || texts.length > 0;
  // Vollständiger Text = Betreff UND Inhalt ausgefüllt. Pflicht-Aufgabe blockiert
  // das Speichern, solange weniger als die geforderte Anzahl vorliegt.
  const completeTextCount = texts.filter((t) => (t.subject || '').trim() && (t.body || '').trim()).length;
  const textsMissing = taskRequired && completeTextCount < taskCount;
  const addText = () => setTexts((p) => [...p, { id: makeEmailId(), subject: '', body: '' }]);
  const editText = (i, key, val) => setTexts((p) => p.map((t, idx) => (idx === i ? { ...t, [key]: val } : t)));
  const removeText = (i) => setTexts((p) => p.filter((_, idx) => idx !== i));
  // Welche optionalen Kennzahlen sind eingeblendet (z. B. positive Antworten)?
  const [extras, setExtras] = useState(() => (existing && typeof existing.positiveReplies === 'number' ? ['positive'] : []));
  const [addOpen, setAddOpen] = useState(false);
  const [remarks, setRemarks] = useState((existing && existing.remarks) || '');
  const [quest, setQuest] = useState(() => {
    const q = existing && Array.isArray(existing.tomorrowQuest) ? existing.tomorrowQuest : [];
    return q.map((x) => ({ text: (x && x.text) || '', done: !!(x && x.done) }));
  });
  const [newQuest, setNewQuest] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const addQuest = () => {
    const t = newQuest.trim();
    if (!t) return;
    setQuest((p) => [...p, { text: t, done: false }]);
    setNewQuest('');
  };
  const editQuest = (i, val) => setQuest((p) => p.map((q, idx) => (idx === i ? { ...q, text: val } : q)));
  const removeQuest = (i) => setQuest((p) => p.filter((_, idx) => idx !== i));

  const hasExtra = (id) => extras.includes(id);
  const addExtra = (id) => { setExtras((p) => (p.includes(id) ? p : [...p, id])); setAddOpen(false); };
  const removeExtra = (id) => { setExtras((p) => p.filter((x) => x !== id)); if (id === 'positive') setPositive(''); };
  const availableExtras = WARMUP_EXTRA_METRICS.filter((m) => !extras.includes(m.id));

  const submit = async () => {
    if (!onSubmit) return;
    if (textsMissing) {
      setError(`Bitte lade mindestens ${taskCount} vollständige Texte (Betreff + Inhalt) hoch, um den Tag zu speichern.`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const cleanTexts = texts
        .map((t) => ({ id: t.id || makeEmailId(), subject: t.subject || '', body: t.body || '', tag: experimentTextTag(dayIndex) }))
        .filter((t) => (t.subject || '').trim() || (t.body || '').trim());
      await onSubmit(dayKey, {
        dayIndex,
        sentConfirmed: sentDone,
        sentEmails: sentDone ? plannedTotal : 0,
        positiveReplies: hasExtra('positive') ? Math.max(0, Math.round(Number(positive) || 0)) : 0,
        remarks: remarks.trim(),
        tomorrowQuest: quest.map((q) => ({ text: q.text.trim(), done: !!q.done })).filter((q) => q.text),
        uploadedTexts: cleanTexts,
      });
      if (onCancel) onCancel();
    } catch (e) {
      setError(e.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={`rp-block rp-wu-dayform ${showTextTask ? 'rp-wu-dayform-split' : ''}`}>
      <div className="rp-wu-dayform-cols">
        {showTextTask && (
          <div className="rp-wu-dayform-left">
            <div className="rp-wu-task">
              <div className="rp-wu-task-head">
                <span className="rp-wu-task-ico"><RpIcon name="rocket" size={18} /></span>
                <div className="rp-wu-task-heads">
                  <span className="rp-wu-task-label">
                    Aufgabe · Beste Texte hochladen
                    {taskRequired && (
                      <span className={`rp-wu-task-req ${textsMissing ? 'is-missing' : 'is-ok'}`}>
                        {textsMissing ? `Pflicht · ${completeTextCount}/${taskCount}` : `✓ ${completeTextCount}/${taskCount}`}
                      </span>
                    )}
                  </span>
                  <span className="rp-wu-task-sub">
                    {taskRequired
                      ? `Lade jeden Tag deine ${taskCount} besten Texte hoch (Betreff + Text) – Pflicht, um den Tag zu speichern. Sie landen automatisch im Account Setup unter „E-Mail-Texte" und können dort bewertet werden.`
                      : taskCount > 0
                        ? `Lade deine ${taskCount} besten ${taskCount === 1 ? 'Text' : 'Texte'} hoch (Betreff + Text). Sie landen automatisch im Account Setup unter „E-Mail-Texte" und können dort bewertet werden.`
                        : 'Diese Texte erscheinen im Account Setup unter „E-Mail-Texte" und können dort bewertet werden.'}
                  </span>
                </div>
              </div>
              <div className="rp-wu-task-list">
                {texts.map((t, i) => (
                  <div key={t.id} className="rp-wu-textcard">
                    <div className="rp-wu-textcard-top">
                      <span className="rp-wu-textcard-num">Text {String(i + 1).padStart(2, '0')}</span>
                      <button type="button" className="rp-wu-textcard-del" onClick={() => removeText(i)} aria-label="Text entfernen">×</button>
                    </div>
                    <input
                      className="rp-wu-textcard-subject"
                      type="text"
                      value={t.subject}
                      placeholder="Betreff…"
                      onChange={(e) => editText(i, 'subject', e.target.value)}
                    />
                    <AutoGrowTextarea
                      className="rp-wu-textcard-body"
                      value={t.body}
                      placeholder="E-Mail-Text schreiben…"
                      onChange={(e) => editText(i, 'body', e.target.value)}
                    />
                  </div>
                ))}
                <button type="button" className="rp-wu-task-add" onClick={addText}>
                  <span className="rp-wu-task-add-icon">+</span>
                  Text hinzufügen
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="rp-wu-dayform-right">
          <div className="rp-wu-extra-row">
            {WARMUP_EXTRA_METRICS.map((m) => (hasExtra(m.id) ? (
              <label key={m.id} className="rp-reporting-metric rp-reporting-metric-sm" style={{ '--m-accent': m.accent }}>
                <span className="rp-reporting-metric-top">
                  <span className="rp-reporting-metric-ico"><RpIcon name={m.icon} size={14} /></span>
                  <span className="rp-reporting-metric-label">{m.label}</span>
                  <button type="button" className="rp-reporting-metric-remove" onClick={() => removeExtra(m.id)} aria-label={`${m.label} entfernen`}>×</button>
                </span>
                <input type="text" inputMode="numeric" value={positive} placeholder={m.placeholder} onChange={(e) => setPositive(e.target.value)} />
              </label>
            ) : null))}
            {availableExtras.length > 0 && (
              <div className="rp-reporting-addmetric">
                <button
                  type="button"
                  className="rp-wu-extra-add"
                  onClick={() => (availableExtras.length === 1 ? addExtra(availableExtras[0].id) : setAddOpen((o) => !o))}
                  aria-label="Weitere Angabe hinzufügen"
                  aria-expanded={addOpen}
                >
                  <span className="rp-wu-extra-add-icon">+</span>
                  {availableExtras.length === 1 ? (availableExtras[0].addLabel || availableExtras[0].label) : 'Weitere Angabe'}
                </button>
                {addOpen && availableExtras.length > 1 && (
                  <div className="rp-reporting-addmetric-menu">
                    {availableExtras.map((m) => (
                      <button key={m.id} type="button" className="rp-reporting-addmetric-opt" onClick={() => addExtra(m.id)}>
                        <span className="rp-reporting-addmetric-opt-ico" style={{ '--m-accent': m.accent }}><RpIcon name={m.icon} size={14} /></span>
                        {m.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <label className={`rp-wu-confirm ${sentDone ? 'is-on' : ''}`}>
            <input type="checkbox" checked={sentDone} onChange={(e) => setSentDone(e.target.checked)} />
            <span className="rp-wu-confirm-box" aria-hidden="true">{sentDone ? <RpIcon name="check" size={14} /> : null}</span>
            <span className="rp-wu-confirm-text">
              Ich habe heute meine <strong>{plannedTotal}</strong> {plannedTotal === 1 ? 'Mail' : 'Mails'} gesendet
              {followups && followups.total > 0 ? ' (inkl. Follow-ups)' : ''}
            </span>
          </label>

          <div className="rp-wu-field rp-wu-field-compact" style={{ '--q-accent': '167, 139, 250' }}>
            <div className="rp-wu-field-head">
              <span className="rp-wu-field-ico"><RpIcon name="bulb" size={15} /></span>
              <div className="rp-wu-field-heads">
                <span className="rp-wu-field-label">Bemerkungen</span>
                <span className="rp-wu-field-sub">Was ist dir heute aufgefallen?</span>
              </div>
            </div>
            <AutoGrowTextarea
              className="rp-reporting-area"
              value={remarks}
              placeholder="z. B. auffällig hohe Öffnungsrate bei Betreff X, zwei Interessenten aus der Hotellerie …"
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>

          <div className="rp-wu-field rp-wu-field-compact" style={{ '--q-accent': '52, 211, 153' }}>
            <div className="rp-wu-field-head">
              <span className="rp-wu-field-ico"><RpIcon name="check" size={15} /></span>
              <div className="rp-wu-field-heads">
                <span className="rp-wu-field-label">Ideen für morgen</span>
                <span className="rp-wu-field-sub">Aufgaben, die du dir für morgen vornimmst.</span>
              </div>
            </div>
            <div className="rp-wu-quest">
              {quest.map((q, i) => (
                <div key={i} className="rp-wu-quest-item rp-wu-quest-item-idea">
                  <span className="rp-wu-quest-dot" aria-hidden="true" />
                  <input className="rp-wu-quest-input" type="text" value={q.text} onChange={(e) => editQuest(i, e.target.value)} />
                  <button type="button" className="rp-wu-quest-del" onClick={() => removeQuest(i)} aria-label="Entfernen">×</button>
                </div>
              ))}
              <div className="rp-wu-quest-add">
                <input
                  className="rp-wu-quest-input"
                  type="text"
                  value={newQuest}
                  placeholder="Aufgabe für morgen hinzufügen …"
                  onChange={(e) => setNewQuest(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addQuest(); } }}
                />
                <button type="button" className="rp-wu-quest-addbtn" onClick={addQuest}>+ Hinzufügen</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && <p className="rp-attach-error">{error}</p>}

      <div className="rp-mrep-actions">
        {isHistory && onCancel && (
          <button type="button" className="rp-mrep-cancel" onClick={onCancel} disabled={saving}>Abbrechen</button>
        )}
        <button type="button" className="rp-mrep-submit" onClick={submit} disabled={saving || !onSubmit || textsMissing}>
          {saving ? 'Wird gespeichert …' : (existing && existing.submittedAt) ? 'Tag aktualisieren' : 'Tag speichern'}
        </button>
      </div>
    </section>
  );
};

// ── Warm-up-Ansicht: Start-Panel + täglicher Hochlauf-Report ──
const WarmupReport = ({ warmup, startWarmup, submitWarmupDay, resetWarmup }) => {
  const [now, setNow] = useState(Date.now());
  const [pendingTarget, setPendingTarget] = useState(String(WARMUP_TARGET_DEFAULT));
  const [starting, setStarting] = useState(false);
  const [startErr, setStartErr] = useState('');
  const [editingKey, setEditingKey] = useState(null);
  // Aufgeklappte „Alle Infos" im erledigten Tag (zeigt die Notizen direkt inline).
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Öffnet das Account Setup (E-Mail-Texte) in einem neuen Tab und scrollt dort
  // direkt zu den an diesem Tag hochgeladenen Mails.
  const openEmailTexts = (dayIndex) => {
    try {
      const base = `${window.location.origin}${window.location.pathname}`;
      const url = `${base}?tab=emails${dayIndex ? `&day=${dayIndex}` : ''}`;
      window.open(url, '_blank', 'noopener');
    } catch (e) { /* noop */ }
  };
  // Zurücksetzen (deaktivieren) der Experimentierphase – mit Inline-Bestätigung,
  // damit nicht versehentlich alle Tage gelöscht werden.
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetErr, setResetErr] = useState('');
  const doReset = async () => {
    if (!resetWarmup) return;
    setResetting(true);
    setResetErr('');
    try {
      await resetWarmup();
      setResetConfirm(false);
    } catch (e) {
      setResetErr(e.message || 'Zurücksetzen fehlgeschlagen.');
    } finally {
      setResetting(false);
    }
  };
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  // Sobald sich der Startzeitpunkt ändert (Warm-up gerade gestartet), die Zeitbasis
  // sofort aktualisieren – sonst ist `now` vom Mount veraltet und der „startet in …"-
  // Zustand würde fälschlich greifen, bis der Tab gewechselt wird.
  const startAtRef = warmup && warmup.startAt;
  useEffect(() => { setNow(Date.now()); }, [startAtRef]);

  const started = warmup && typeof warmup.startAt === 'number' && Number(warmup.dailyTarget) > 0;
  const target = started ? Number(warmup.dailyTarget) : 0;
  const days = (warmup && warmup.days && typeof warmup.days === 'object') ? warmup.days : {};

  const doStart = async () => {
    if (!startWarmup) return;
    setStarting(true);
    setStartErr('');
    try {
      await startWarmup(clampWarmupTarget(pendingTarget));
    } catch (e) {
      setStartErr(e.message || 'Start fehlgeschlagen.');
    } finally {
      setStarting(false);
    }
  };

  if (!started) {
    const previewTarget = clampWarmupTarget(pendingTarget);
    return (
      <div className="rp-dna rp-wu-startdna">
        <div className="rp-dna-step rp-dna-intro-step rp-wu-startdna-step">
          <span className="rp-dna-intro-badge"><RpIcon name="rocket" size={44} /></span>
          <div className="rp-dna-intro-copy">
            <h3 className="rp-dna-step-title rp-dna-intro-title">Launch Phase starten</h3>
            <p className="rp-dna-intro-text">Sanfter Hochlauf über {WARMUP_RAMP_DAYS} Tage – für stabile Zustellbarkeit bis zu deinem Tagesziel.</p>
          </div>

          <div className="rp-wu-startgrid">
            <ul className="rp-wu-startfacts">
              <li><span className="rp-wu-startfact-ico"><RpIcon name="calendar" size={14} /></span>Tägliche Updates zum E-Mail-Versand</li>
              <li><span className="rp-wu-startfact-ico"><RpIcon name="overview" size={14} /></span>Voller Einblick in die Kundenansprache</li>
              <li><span className="rp-wu-startfact-ico"><RpIcon name="reply" size={14} /></span>Live-Feedback vom Outreach-Team</li>
            </ul>
            <div className="rp-wu-startgrid-sep" aria-hidden="true" />
            <div className="rp-wu-targetnum">
              <div className="rp-wu-targetnum-row">
                <input
                  id="rp-wu-target-input"
                  type="number"
                  inputMode="numeric"
                  className="rp-wu-targetnum-input"
                  value={pendingTarget}
                  min={WARMUP_TARGET_MIN}
                  max={WARMUP_TARGET_MAX}
                  step={1}
                  onChange={(e) => setPendingTarget(e.target.value)}
                  onBlur={() => setPendingTarget(String(clampWarmupTarget(pendingTarget)))}
                />
                <span className="rp-wu-targetnum-unit">Mails / Tag</span>
              </div>
            </div>
          </div>

          {startErr && <p className="rp-attach-error">{startErr}</p>}
          <div className="rp-dna-intro-actions">
            <button type="button" className="rp-dna-intro-start" onClick={doStart} disabled={starting || !startWarmup}>
              {starting ? 'Wird gestartet …' : `Launch Phase mit ${previewTarget} Mails/Tag starten`}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // „Startschuss" mit Verzögerung: solange startAt in der Zukunft liegt, läuft der
  // Countdown bis zur Freischaltung des ersten Reportings (noch kein Tagesformular).
  // Bewusst gegen Date.now() (nicht das gedrosselte `now`) geprüft, damit ein
  // sofortiger Start nicht für einen Moment als „geplant" erscheint.
  const pending = warmup.startAt > Date.now();
  const todayKey = warmupDateKey(now);
  const todayIndex = warmupDayIndex(warmup.startAt, now);
  // Wochenende: kein Versand – Plan auf 0, kein Hochlauf-Fortschritt.
  const todayWeekend = isWarmupWeekend(now);
  const todayPlanned = todayWeekend ? 0 : warmupPlannedNew(todayIndex, target);
  const todayFollow = todayWeekend ? { f1: 0, f2: 0, total: 0 } : warmupFollowups(todayIndex, target);
  const todayEntry = days[todayKey] || null;
  const todaySubmitted = !!(todayEntry && todayEntry.submittedAt);
  const todaySent = !!(todayEntry && (todayEntry.sentConfirmed || Number(todayEntry.sentEmails) > 0));
  const inRamp = !todayWeekend && todayIndex >= 1 && todayIndex <= WARMUP_RAMP_DAYS;

  const history = Object.keys(days)
    .map((k) => days[k])
    .filter((d) => d && d.dateKey && d.dateKey !== todayKey)
    .sort((a, b) => (a.dateKey < b.dateKey ? 1 : -1));

  const totals = Object.values(days).reduce((s, d) => ({
    sent: s.sent + (Number(d.sentEmails) || 0),
    positive: s.positive + (Number(d.positiveReplies) || 0),
  }), { sent: 0, positive: 0 });

  const maxBar = warmupPlannedNew(WARMUP_RAMP_DAYS, target) + warmupFollowups(WARMUP_RAMP_DAYS, target).total;
  // Beim Bearbeiten existiert i. d. R. ein Eintrag; beim Nachtragen eines verpassten
  // Tages wird ein leerer Eintrag mit korrektem Tagesindex synthetisiert.
  const editingEntry = editingKey
    ? (days[editingKey] || { dateKey: editingKey, dayIndex: warmupDayIndex(warmup.startAt, warmupDateKeyToMs(editingKey)) })
    : null;

  // Verpasste Werktage (vor dem aktuell offenen Tag) ohne Eintrag – dürfen jederzeit
  // nachgetragen werden. An einem Werktag ist „heute" noch offen (nicht verpasst).
  const lastDueIndex = Math.min(WARMUP_RAMP_DAYS, todayWeekend ? todayIndex : todayIndex - 1);
  const missedDays = [];
  for (let d = 1; d <= lastDueIndex; d += 1) {
    const dk = warmupDateKey(warmupWorkdayDate(warmup.startAt, d));
    if (!(days[dk] && days[dk].submittedAt)) missedDays.push({ d, dk });
  }

  // Nächster Hochlauf-Tag nach heute (für die „Tag notiert"-Vorschau).
  const nextIndex = todayIndex + 1;
  const nextDate = nextIndex >= 1 && nextIndex <= WARMUP_RAMP_DAYS ? warmupWorkdayDate(warmup.startAt, nextIndex) : null;

  return (
    <div className="rp-wu">
      <div className="rp-wu-head">
        <div className="rp-wu-progress">
          <span className="rp-wu-progress-day">{pending ? 'Startklar' : todayWeekend ? 'Wochenende' : inRamp ? `Tag ${todayIndex}` : 'Zielbetrieb'}</span>
          <span className="rp-wu-progress-of">{pending ? 'Startschuss gegeben' : todayWeekend ? 'kein Versand' : inRamp ? `von ${WARMUP_RAMP_DAYS} Tagen im Hochlauf` : `Tag ${todayIndex}`}</span>
        </div>
        <div className="rp-wu-target">
          <span className="rp-wu-target-ico"><RpIcon name="rocket" size={16} /></span>
          <span className="rp-wu-target-val">{todayWeekend || pending ? '–' : todayPlanned + todayFollow.total}</span>
          <span className="rp-wu-target-lbl">Heutiges Ziel (Mails)</span>
        </div>
        {!pending && !todayWeekend && todaySent && (
          <span
            className={`rp-wu-doneflag ${warmupSubmittedLate(todayEntry) ? 'is-late' : ''}`}
            title={warmupSubmittedLate(todayEntry) ? 'Nachgetragen – aber erledigt zählt!' : 'Pünktlich erledigt – stark!'}
          >
            <span className="rp-wu-doneflag-ico"><RpIcon name="check" size={12} /></span>
            {warmupSubmittedLate(todayEntry) ? 'Verspätet erledigt' : 'Pünktlich erledigt'}
          </span>
        )}
        <div className="rp-wu-totals">
          <div className="rp-wu-total"><strong>{totals.sent}</strong><span>gesendet</span></div>
          <div className="rp-wu-total rp-wu-total-pos"><strong>{totals.positive}</strong><span>positive Rückmeldung</span></div>
        </div>
      </div>

      <div className="rp-wu-plan">
        <div className="rp-wu-plan-head">
          <span className="rp-wu-plan-title">4 Wochen Launch Phase</span>
          <span className="rp-wu-plan-legend">
            <span className="rp-wu-leg rp-wu-leg-new">Neuer Outreach</span>
            <span className="rp-wu-leg rp-wu-leg-fu">Follow-ups</span>
          </span>
        </div>
        <div className="rp-wu-bars">
          {Array.from({ length: WARMUP_RAMP_DAYS }, (_, i) => i + 1).map((d) => {
            const pn = warmupPlannedNew(d, target);
            const fu = warmupFollowups(d, target).total;
            const dk = warmupDateKey(warmupWorkdayDate(warmup.startAt, d));
            const done = !!(days[dk] && days[dk].submittedAt);
            const positiveCount = days[dk] ? Number(days[dk].positiveReplies) || 0 : 0;
            const hasPositive = positiveCount > 0;
            const fillPct = maxBar ? Math.min(100, ((pn + fu) / maxBar) * 100) : 0;
            const isToday = !todayWeekend && d === todayIndex;
            return (
              <div
                key={d}
                className={`rp-wu-bar ${isToday ? 'is-today' : ''} ${done ? 'is-done' : ''} ${hasPositive ? 'has-positive' : ''}`}
                title={`Tag ${d}: ${pn} neue Mails${fu ? ` + ${fu} Follow-ups` : ''}${hasPositive ? ` · ${positiveCount} positive Antwort${positiveCount === 1 ? '' : 'en'}` : ''}`}
              >
                {hasPositive && (
                  <span className="rp-wu-bar-pos" style={{ bottom: `calc(${fillPct}% + 5px)` }} title={`${positiveCount} positive Antwort${positiveCount === 1 ? '' : 'en'}`}>
                    <RpIcon name="check" size={12} />
                  </span>
                )}
                <span className="rp-wu-bar-stack">
                  <span className="rp-wu-bar-fu" style={{ height: `${maxBar ? (fu / maxBar) * 100 : 0}%` }} />
                  <span className="rp-wu-bar-new" style={{ height: `${maxBar ? (pn / maxBar) * 100 : 0}%` }} />
                </span>
              </div>
            );
          })}
        </div>
        <div className="rp-wu-plan-foot">
          Versand nur Mo–Fr: Start mit {WARMUP_START_VOLUME} Mails/Tag, schrittweise Steigerung bis {target} Mails/Tag
          an Tag {WARMUP_RAMP_DAYS}. Follow-up nach 1 Woche ohne Antwort, danach erneut nach einer weiteren Woche.
        </div>
      </div>

      {pending ? (
        <div className="rp-mrep-locked">
          <span className="rp-mrep-locked-ico"><RpIcon name="lock" size={24} /></span>
          <span className="rp-mrep-locked-cd">{fmtReportingCountdown(warmup.startAt, now) || 'gleich verfügbar'}</span>
          <span className="rp-mrep-locked-lbl">bis zum offiziellen Start</span>
          <span className="rp-mrep-locked-sub">
            Der Startschuss ist gegeben. Sobald der Countdown abgelaufen ist, wird das erste tägliche
            Reporting der Launch Phase freigeschaltet und der {WARMUP_RAMP_DAYS}-Tage-Hochlauf bis {target} Mails/Tag beginnt.
          </span>
        </div>
      ) : editingEntry ? (
        <div className="rp-mrep-active">
          <div className="rp-mrep-banner rp-mrep-banner-edit">
            <div className="rp-mrep-banner-main">
              <span className="rp-mrep-banner-period">{fmtWarmupDate(editingKey)}</span>
              <span className="rp-mrep-banner-meta">
                Tag {editingEntry.dayIndex || warmupDayIndex(warmup.startAt, now)} {editingEntry.submittedAt ? 'bearbeiten' : 'nachtragen'}
              </span>
            </div>
          </div>
          <WarmupDayForm
            key={`edit-${editingKey}`}
            dayKey={editingKey}
            dayIndex={editingEntry.dayIndex || warmupDayIndex(warmup.startAt, now)}
            planned={warmupPlannedNew(editingEntry.dayIndex || 1, target)}
            followups={warmupFollowups(editingEntry.dayIndex || 1, target)}
            textTask={experimentTextTask(editingEntry.dayIndex || warmupDayIndex(warmup.startAt, now))}
            existing={editingEntry}
            onSubmit={submitWarmupDay}
            isHistory
            onCancel={() => setEditingKey(null)}
          />
        </div>
      ) : (todaySubmitted && !todayWeekend) ? (
        <div className="rp-mrep-active rp-wu-daystack">
          {nextDate ? (
            <div className="rp-wu-lockday" title={`Tag ${nextIndex} wird am ${fmtWarmupDayLong(nextDate)} freigeschaltet`}>
              <span className="rp-wu-lockday-ico"><RpIcon name="lock" size={16} /></span>
              <div className="rp-wu-lockday-main">
                <span className="rp-wu-lockday-title">Tag {nextIndex}</span>
                <span className="rp-wu-lockday-sub">Wird am {fmtWarmupDayLong(nextDate)} freigeschaltet</span>
              </div>
              <span className="rp-wu-lockday-badge">Gesperrt</span>
            </div>
          ) : (
            <div className="rp-wu-lockday rp-wu-lockday-final">
              <span className="rp-wu-lockday-ico"><RpIcon name="trophy" size={16} /></span>
              <div className="rp-wu-lockday-main">
                <span className="rp-wu-lockday-title">Hochlauf abgeschlossen</span>
                <span className="rp-wu-lockday-sub">Alle {WARMUP_RAMP_DAYS} Tage geschafft – ab jetzt läuft der Zielbetrieb.</span>
              </div>
            </div>
          )}
          <div className={`rp-wu-done ${detailsOpen ? 'is-open' : ''}`}>
            <div className="rp-wu-done-row">
              <span className="rp-wu-done-ico"><RpIcon name="check" size={22} /></span>
              <div className="rp-wu-done-main">
                <span className="rp-wu-done-title">Tag {todayIndex} notiert</span>
                <span className="rp-wu-done-sub">Stark – dein Eintrag für heute ({fmtWarmupDate(todayKey)}) ist gespeichert.</span>
              </div>
              <div className="rp-wu-done-actions">
                <button type="button" className="rp-wu-done-edit rp-wu-done-edit-icon" onClick={() => setEditingKey(todayKey)} title="Eintrag bearbeiten" aria-label="Eintrag bearbeiten">
                  <i className="fa-solid fa-pen-to-square" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className={`rp-wu-done-toggle ${detailsOpen ? 'is-open' : ''}`}
                  onClick={() => setDetailsOpen((o) => !o)}
                  aria-expanded={detailsOpen}
                >
                  Alle Infos
                  <svg className="rp-wu-done-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
                </button>
              </div>
            </div>
            {detailsOpen && (
              <div className="rp-wu-done-details">
                <div className="rp-wu-done-stats">
                  <span className="rp-wu-done-stat">
                    <RpIcon name="check" size={13} />
                    {todaySent ? `${Number(todayEntry && todayEntry.sentEmails) || (todayPlanned + todayFollow.total)} Mails gesendet` : 'Versand nicht bestätigt'}
                  </span>
                  {Number(todayEntry && todayEntry.positiveReplies) > 0 && (
                    <span className="rp-wu-done-stat rp-wu-done-stat-pos">
                      <RpIcon name="trophy" size={13} />
                      {todayEntry.positiveReplies} positive Rückmeldung{todayEntry.positiveReplies === 1 ? '' : 'en'}
                    </span>
                  )}
                  {Array.isArray(todayEntry && todayEntry.uploadedTexts) && todayEntry.uploadedTexts.length > 0 && (
                    <span className="rp-wu-done-stat">
                      <RpIcon name="file" size={13} />
                      {todayEntry.uploadedTexts.length} {todayEntry.uploadedTexts.length === 1 ? 'Text' : 'Texte'}
                    </span>
                  )}
                </div>
                {todayEntry && todayEntry.remarks && (
                  <div className="rp-wu-done-block">
                    <span className="rp-wu-done-block-lbl">Bemerkungen</span>
                    <p className="rp-wu-done-block-txt">{todayEntry.remarks}</p>
                  </div>
                )}
                {Array.isArray(todayEntry && todayEntry.tomorrowQuest) && todayEntry.tomorrowQuest.filter((q) => q && q.text).length > 0 && (
                  <div className="rp-wu-done-block">
                    <span className="rp-wu-done-block-lbl">Ideen für morgen</span>
                    <ul className="rp-wu-done-ideas">
                      {todayEntry.tomorrowQuest.filter((q) => q && q.text).map((q, i) => (
                        <li key={i}>{q.text}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <button type="button" className="rp-wu-done-mails" onClick={() => openEmailTexts(todayIndex)}>
                  <RpIcon name="rocket" size={14} />
                  Mails im Account Setup ansehen
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17L17 7M9 7h8v8" /></svg>
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="rp-mrep-active">
          <div className={`rp-mrep-banner ${todaySubmitted ? 'rp-mrep-banner-edit' : 'rp-mrep-banner-open'}`}>
            <div className="rp-mrep-banner-main">
              <span className="rp-mrep-banner-period">Heute · {fmtWarmupDate(todayKey)}</span>
              <span className="rp-mrep-banner-meta">
                {todayWeekend
                  ? 'Wochenende · kein Versand geplant'
                  : <>
                      {inRamp ? `Tag ${todayIndex} im Hochlauf` : 'Zielbetrieb'} · Ziel {todayPlanned} neue Mails
                      {todayFollow.total ? ` + ${todayFollow.total} Follow-ups` : ''}
                    </>}
                {todaySubmitted ? ' · bereits eingetragen' : ''}
              </span>
            </div>
          </div>
          <WarmupDayForm
            key={todayKey + (todaySubmitted ? '-s' : '')}
            dayKey={todayKey}
            dayIndex={todayIndex}
            planned={todayPlanned}
            followups={todayFollow}
            textTask={todayWeekend ? null : experimentTextTask(todayIndex)}
            existing={todayEntry}
            onSubmit={submitWarmupDay}
          />
        </div>
      )}

      {!editingEntry && missedDays.length > 0 && (
        <div className="rp-wu-catchup">
          <div className="rp-wu-catchup-head">
            <span className="rp-wu-catchup-ico"><RpIcon name="alert" size={16} /></span>
            <div className="rp-wu-catchup-heads">
              <span className="rp-wu-catchup-title">
                {missedDays.length === 1 ? '1 Tag nachzutragen' : `${missedDays.length} Tage nachzutragen`}
              </span>
              <span className="rp-wu-catchup-sub">Verpasst? Kein Problem – trag die fehlenden Tage einfach nach.</span>
            </div>
          </div>
          <div className="rp-wu-catchup-list">
            {missedDays.map((m) => (
              <button key={m.dk} type="button" className="rp-wu-catchup-item" onClick={() => setEditingKey(m.dk)}>
                <span className="rp-wu-catchup-day">Tag {m.d}</span>
                <span className="rp-wu-catchup-date">{fmtWarmupDate(m.dk)}</span>
                <span className="rp-wu-catchup-go">Nachtragen →</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="rp-mrep-history">
          <h3 className="rp-mrep-history-title">Bisherige Tage</h3>
          <div className="rp-wu-hlist">
            {history.map((d) => {
              const quest = Array.isArray(d.tomorrowQuest) ? d.tomorrowQuest.filter((q) => q && q.text) : [];
              return (
                <div key={d.dateKey} className={`rp-wu-hcard ${editingKey === d.dateKey ? 'rp-wu-hcard-active' : ''}`}>
                  <div className="rp-wu-hcard-top">
                    <span className="rp-wu-hcard-date">{fmtWarmupDate(d.dateKey)}</span>
                    {d.dayIndex ? <span className="rp-wu-hcard-day">Tag {d.dayIndex}</span> : null}
                  </div>
                  <div className="rp-wu-hcard-stats">
                    {d.submittedAt && (
                      <span className={`rp-wu-hstat rp-wu-hstat-when ${warmupSubmittedLate(d) ? 'is-late' : 'is-ontime'}`}>
                        {warmupSubmittedLate(d) ? 'verspätet erledigt' : 'pünktlich erledigt'}
                      </span>
                    )}
                    <span className="rp-wu-hstat">{d.sentConfirmed || Number(d.sentEmails) > 0 ? 'gesendet ✓' : 'nicht bestätigt'}</span>
                    {Number(d.positiveReplies) > 0 && <span className="rp-wu-hstat rp-wu-hstat-pos"><strong>{d.positiveReplies}</strong> positiv</span>}
                    {Array.isArray(d.uploadedTexts) && d.uploadedTexts.length > 0 && (
                      <span className="rp-wu-hstat rp-wu-hstat-txt"><strong>{d.uploadedTexts.length}</strong> {d.uploadedTexts.length === 1 ? 'Text' : 'Texte'}</span>
                    )}
                    {d.submittedBy && <span className="rp-wu-hstat rp-wu-hstat-by">von {d.submittedBy}</span>}
                  </div>
                  {d.remarks && <p className="rp-wu-hremarks">{d.remarks}</p>}
                  {quest.length > 0 && (
                    <ul className="rp-wu-hquest">
                      {quest.map((q, i) => (
                        <li key={i} className={q.done ? 'is-done' : ''}>{q.text}</li>
                      ))}
                    </ul>
                  )}
                  <div className="rp-wu-hcard-foot">
                    {submitWarmupDay && editingKey !== d.dateKey && (
                      <button type="button" className="rp-mrep-hcard-edit" onClick={() => setEditingKey(d.dateKey)}>Bearbeiten</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {resetWarmup && (
        <div className="rp-wu-reset">
          {resetConfirm ? (
            <div className="rp-wu-reset-confirm">
              <span className="rp-wu-reset-q">Launch Phase wirklich zurücksetzen? Alle Tage gehen verloren.</span>
              <div className="rp-wu-reset-actions">
                <button type="button" className="rp-wu-reset-cancel" onClick={() => { setResetConfirm(false); setResetErr(''); }} disabled={resetting}>Abbrechen</button>
                <button type="button" className="rp-wu-reset-go" onClick={doReset} disabled={resetting}>{resetting ? 'Wird zurückgesetzt …' : 'Ja, zurücksetzen'}</button>
              </div>
              {resetErr && <span className="rp-wu-reset-err">{resetErr}</span>}
            </div>
          ) : (
            <button type="button" className="rp-wu-reset-btn" onClick={() => setResetConfirm(true)}>
              Launch Phase zurücksetzen
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ── Ansicht: Reporting (Toggle „Monatliches Reporting" ↔ „Warm-up Report") ──
// Zeigt den Countdown bis zur Freischaltung, das Eingabe-Formular sobald frei-
// geschaltet (Umfrage + optionales PDF) sowie den Verlauf eingereichter Reportings.
// Über den Toggle (gleiches Styling wie der Account-Setup-Toggle) lässt sich auf
// den täglichen Warm-up-Report umschalten.
const ReportingView = ({ reportings, submitReporting, uploadAttachment, warmup, startWarmup, submitWarmupDay, resetWarmup }) => {
  const [now, setNow] = useState(Date.now());
  const [editingKey, setEditingKey] = useState(null);
  // Beim Öffnen des Tabs ist der Warm-up-Report die Standardansicht, solange er
  // relevant ist: noch nicht gestartet (zum Starten) oder mitten im Hochlauf (in
  // Gange). Erst wenn der Werktage-Hochlauf abgeschlossen ist (Ende des ersten
  // Monats), wird das monatliche Reporting zur Standardansicht.
  const warmupStarted = !!(warmup && typeof warmup.startAt === 'number' && Number(warmup.dailyTarget) > 0);
  const warmupFinished = warmupStarted && warmupDayIndex(warmup.startAt, Date.now()) > WARMUP_RAMP_DAYS;
  const [mode, setMode] = useState(warmupFinished ? 'monthly' : 'warmup'); // monthly | warmup
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const list = Array.isArray(reportings) ? reportings : [];
  const sorted = [...list].sort((a, b) => (b.availableAt || 0) - (a.availableAt || 0));
  const open = sorted.filter((r) => r.state === 'open' || r.state === 'overdue').sort((a, b) => (a.availableAt || 0) - (b.availableAt || 0));
  const scheduled = sorted.filter((r) => r.state === 'scheduled').sort((a, b) => (a.availableAt || 0) - (b.availableAt || 0));
  const submitted = sorted.filter((r) => r.state === 'submitted');

  const current = open[0] || null;
  const nextUp = scheduled[0] || null;
  const editingRep = editingKey ? sorted.find((r) => r.periodKey === editingKey) : null;

  const submittedCount = submitted.length;
  const warmupDayCount = (warmup && warmup.days && typeof warmup.days === 'object') ? Object.keys(warmup.days).length : 0;
  const titleByMode = mode === 'warmup' ? 'Launch Phase' : 'Monatliches Reporting';
  const subByMode = mode === 'warmup'
    ? 'Täglicher Outreach-Hochlauf: Der Outreach-Manager bestätigt jeden Tag den Versand, hält Bemerkungen und Ideen für morgen fest und lädt gelegentlich seine besten Texte hoch. So sehen Sie täglich, dass gearbeitet wird.'
    : 'Ihr monatlicher Überblick: Nach jedem 30-Tage-Zyklus fassen wir die Ergebnisse für Sie zusammen – mit allen qualifizierten Terminen, einem kurzen Fazit und auf Wunsch ergänzenden Unterlagen. So sehen Sie transparent, was im vergangenen Monat erreicht wurde.';

  return (
    <div className="rp-tab">
      <section className="rp-block">
        <SectionHead
          eyebrow="Reporting"
          title={titleByMode}
          sub={subByMode}
        />

        <div className="rp-emailhead-actions rp-emailhead-actions-below">
          <div className="rp-emailmode" role="tablist" aria-label="Reporting-Ansicht">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'warmup'}
              className={`rp-emailmode-btn ${mode === 'warmup' ? 'is-on' : ''}`}
              onClick={() => setMode('warmup')}
            >
              Launch Phase
              {warmupDayCount > 0 && <span className="rp-emailmode-badge">{warmupDayCount}</span>}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'monthly'}
              className={`rp-emailmode-btn ${mode === 'monthly' ? 'is-on' : ''}`}
              onClick={() => setMode('monthly')}
            >
              Monatliches Reporting
              {submittedCount > 0 && <span className="rp-emailmode-badge">{submittedCount}</span>}
            </button>
          </div>
        </div>

        {mode === 'warmup' ? (
          <WarmupReport warmup={warmup} startWarmup={startWarmup} submitWarmupDay={submitWarmupDay} resetWarmup={resetWarmup} />
        ) : list.length === 0 ? (
          <div className="rp-mrep-empty">
            <span className="rp-mrep-empty-ico"><RpIcon name="calendar" size={26} /></span>
            <span className="rp-mrep-empty-title">Noch kein Reporting fällig</span>
            <span className="rp-mrep-empty-sub">Sobald der Vertrag aktiv ist, startet hier automatisch der 30-Tage-Countdown bis zum ersten Reporting – zeitgleich mit der ersten Monatsrechnung.</span>
          </div>
        ) : (
          <>
            {/* Aktuell freigeschaltetes Reporting → direkt ausfüllbar */}
            {current && !editingRep && (
              <div className="rp-mrep-active">
                <div className={`rp-mrep-banner ${current.state === 'overdue' ? 'rp-mrep-banner-overdue' : 'rp-mrep-banner-open'}`}>
                  <div className="rp-mrep-banner-main">
                    <span className="rp-mrep-banner-period">{reportingMonthLabel(current)}</span>
                    <span className="rp-mrep-banner-meta">
                      Zeitraum {fmtReportingDate(current.periodStart)} – {fmtReportingDate(current.periodEnd)}
                    </span>
                  </div>
                  <div className="rp-mrep-banner-cd">
                    {current.state === 'overdue' ? (
                      <span className="rp-mrep-cd-overdue">Frist überschritten – bitte nachtragen</span>
                    ) : (
                      <>
                        <span className="rp-mrep-cd-val">{fmtReportingCountdown(current.deadlineAt, now) || 'jetzt'}</span>
                        <span className="rp-mrep-cd-lbl">bis zum Fristende</span>
                      </>
                    )}
                  </div>
                </div>
                <MonthlyReportingForm
                  key={current.periodKey}
                  rep={current}
                  onSubmit={submitReporting}
                  uploadAttachment={uploadAttachment}
                />
              </div>
            )}

            {/* Verlaufseintrag bearbeiten */}
            {editingRep && (
              <div className="rp-mrep-active">
                <div className="rp-mrep-banner rp-mrep-banner-edit">
                  <div className="rp-mrep-banner-main">
                    <span className="rp-mrep-banner-period">{reportingMonthLabel(editingRep)}</span>
                    <span className="rp-mrep-banner-meta">Eingereichtes Reporting bearbeiten</span>
                  </div>
                </div>
                <MonthlyReportingForm
                  key={editingRep.periodKey}
                  rep={editingRep}
                  onSubmit={submitReporting}
                  uploadAttachment={uploadAttachment}
                  onCancel={() => setEditingKey(null)}
                  isHistory
                />
              </div>
            )}

            {/* Nichts offen, aber nächstes Reporting im Countdown */}
            {!current && !editingRep && nextUp && (
              <div className="rp-mrep-locked">
                <span className="rp-mrep-locked-ico"><RpIcon name="lock" size={24} /></span>
                <span className="rp-mrep-locked-cd">{fmtReportingCountdown(nextUp.availableAt, now) || 'gleich verfügbar'}</span>
                <span className="rp-mrep-locked-lbl">bis zur Freischaltung des nächsten Reportings</span>
                <span className="rp-mrep-locked-sub">
                  {reportingMonthLabel(nextUp)} · schaltet frei am {fmtReportingDate(nextUp.availableAt)}. Danach hast du 7 Tage Zeit für dein Fazit.
                </span>
              </div>
            )}

            {/* Nichts offen und nichts geplant – alles erledigt */}
            {!current && !editingRep && !nextUp && submitted.length > 0 && (
              <div className="rp-mrep-done">
                <span className="rp-mrep-done-ico"><RpIcon name="check" size={22} /></span>
                <span>Alle fälligen Reportings sind eingereicht. Das nächste schaltet automatisch frei.</span>
              </div>
            )}

            {/* Verlauf eingereichter Reportings */}
            {submitted.length > 0 && (
              <div className="rp-mrep-history">
                <h3 className="rp-mrep-history-title">Eingereichte Reportings</h3>
                <div className="rp-mrep-history-list">
                  {submitted.map((r) => {
                    const ex = typeof r.extraAppointments === 'number'
                      ? r.extraAppointments
                      : Math.max(0, (Number(r.qualifiedAppointments) || 0) - (typeof r.included === 'number' ? r.included : 4));
                    const repMetrics = (r.metrics && typeof r.metrics === 'object') ? r.metrics : {};
                    const filledMetrics = REPORTING_METRICS.filter((m) => String(repMetrics[m.id] || '').trim());
                    const answered = REPORTING_QUESTIONS.filter((qq) => String(r[qq.id] || '').trim());
                    return (
                      <div key={r.periodKey} className={`rp-mrep-hcard ${editingKey === r.periodKey ? 'rp-mrep-hcard-active' : ''}`}>
                        <div className="rp-mrep-hcard-top">
                          <span className="rp-mrep-hcard-period">{reportingMonthLabel(r)}</span>
                          <span className="rp-mrep-hcard-stamp">eingereicht {fmtReportingDate(r.submittedAt)}</span>
                        </div>
                        <div className="rp-mrep-hcard-stats">
                          <span className="rp-mrep-hstat"><strong>{r.qualifiedAppointments || 0}</strong> qualifizierte Termine</span>
                          {ex > 0 && <span className="rp-mrep-hstat rp-mrep-hstat-extra">+{ex} extra</span>}
                          {r.submittedBy && <span className="rp-mrep-hstat rp-mrep-hstat-by">von {r.submittedBy}</span>}
                        </div>

                        {filledMetrics.length > 0 && (
                          <div className="rp-mrep-hmetrics">
                            {filledMetrics.map((m) => (
                              <div key={m.id} className="rp-mrep-hmetric" style={{ '--m-accent': m.accent }}>
                                <span className="rp-mrep-hmetric-ico"><RpIcon name={m.icon} size={14} /></span>
                                <span className="rp-mrep-hmetric-val">{repMetrics[m.id]}</span>
                                <span className="rp-mrep-hmetric-lbl">{m.label}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {answered.length > 0 && (
                          <div className="rp-mrep-hqa">
                            {answered.map((qq) => (
                              <div key={qq.id} className="rp-mrep-hqa-item" style={{ '--q-accent': qq.accent }}>
                                <div className="rp-mrep-hqa-head">
                                  <span className="rp-mrep-hqa-ico"><RpIcon name={qq.icon} size={15} /></span>
                                  <span className="rp-mrep-hqa-q">{qq.label}</span>
                                </div>
                                <p className="rp-mrep-hqa-a">{r[qq.id]}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="rp-mrep-hcard-foot">
                          {r.reportUrl && (
                            <a className="rp-mrep-hcard-doc" href={r.reportUrl} target="_blank" rel="noreferrer">
                              <RpIcon name="file" size={13} />Dokument öffnen
                            </a>
                          )}
                          {submitReporting && editingKey !== r.periodKey && (
                            <button type="button" className="rp-mrep-hcard-edit" onClick={() => setEditingKey(r.periodKey)}>Bearbeiten</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

// ── Ansicht: Setup-Details (editierbare Kick-Off-Checkliste) ──
// Vollständig per Hand bearbeitbar: Intro-Absätze, Sektionen, Fragen (inkl.
// „wichtig"/„sicher"-Markierung & Hinweis) und Abschluss-Absätze. Persistiert
// über den Header-Speichern-Button in report.kickoff.
const SetupDetailsView = ({ kickoff, setKickoff, aiSortNotes, uploadAttachment, importOpen, setImportOpen }) => {
  const details = Array.isArray(kickoff.details) ? kickoff.details : [];
  const notes = Array.isArray(kickoff.notes) ? kickoff.notes : [];

  // Eine Info gilt als „erfasst", sobald sie einen Wert hat (Text, Pills oder
  // mindestens eine hochgeladene Datei). Der Setup-Fortschritt (technische
  // Checkliste) liegt nicht mehr hier, sondern im Status-Tab (geteilte Quelle
  // mit dem Team Dashboard).
  const isDetailFilled = (d) => {
    if (Array.isArray(d.attachments) && d.attachments.length > 0) return true;
    return d.type === 'pills' ? (Array.isArray(d.tags) && d.tags.length > 0) : !!(d.value || '').trim();
  };

  const detailDone = details.filter(isDetailFilled).length;

  // ── Notizen (freie Anmerkungen) ──
  const setNotesList = (updater) => setKickoff((k) => ({ ...k, notes: updater(Array.isArray(k.notes) ? k.notes : []) }));
  const updateNote = (i, key, val) => setNotesList((list) => list.map((n, idx) => (idx === i ? { ...n, [key]: val } : n)));
  const removeNote = (i) => setNotesList((list) => list.filter((_, idx) => idx !== i));
  const addNote = () => setNotesList((list) => [...list, { title: '', body: '' }]);

  // ── Benötigte Infos / Details (Info-Hub) ──
  const updateDetails = (updater) => setKickoff((k) => ({ ...k, details: updater(Array.isArray(k.details) ? k.details : []) }));
  const setDetail = (i, patch) => updateDetails((list) => list.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  const removeDetail = (i) => updateDetails((list) => list.filter((_, idx) => idx !== i));
  const addDetail = () =>
    updateDetails((list) => [...list, { label: '', prompt: '', type: 'text', value: '', tags: [], attachments: [] }]);

  // Unified Info-Dump (ein Button im Header): jeder erkannte Block wandert dorthin,
  // wo er passt – ein Titel, der zu einem vorhandenen Info-Feld passt, befüllt
  // dieses (Pills werden ergänzt, Textfelder erweitert). Was nirgends passt,
  // landet als freie Anmerkung.
  const normKey = (s) => (s || '').toLowerCase().replace(/[^a-z0-9äöüß]+/gi, '');
  const splitValues = (s) => (s || '').split(/[,\n;]+/).map((x) => x.trim()).filter(Boolean);
  const applyDump = (parsed) => {
    setKickoff((k) => {
      const dets = (Array.isArray(k.details) ? k.details : []).map((d) => ({
        ...d,
        tags: Array.isArray(d.tags) ? [...d.tags] : [],
      }));
      const newNotes = [];
      parsed.forEach(({ title, body }) => {
        const t = (title || '').trim();
        const b = (body || '').trim();
        if (!t && !b) return;
        const key = normKey(t);
        let idx = -1;
        if (key) {
          idx = dets.findIndex((d) => {
            const dl = normKey(d.label);
            return dl && (dl === key || dl.includes(key) || key.includes(dl));
          });
        }
        const val = b || t;
        if (idx >= 0) {
          const ex = dets[idx];
          if (ex.type === 'pills') {
            const tags = [...(ex.tags || [])];
            splitValues(val).forEach((p) => {
              if (!tags.some((x) => x.toLowerCase() === p.toLowerCase())) tags.push(p);
            });
            dets[idx] = { ...ex, tags };
          } else {
            dets[idx] = { ...ex, value: ex.value ? `${ex.value}\n${val}` : val };
          }
        } else {
          newNotes.push({ title: t, body: b });
        }
      });
      const notes = [...(Array.isArray(k.notes) ? k.notes : []), ...newNotes];
      return { ...k, details: dets, notes };
    });
  };

  return (
    <div className="rp-tab">
      {/* Info-Dump (Header-Button steuert importOpen): sortiert einen rohen Dump
          automatisch dorthin, wo er passt – Felder oder Anmerkungen. */}
      <BulkImport
        open={importOpen}
        onClose={() => setImportOpen(false)}
        parse={parseDumpToNotes}
        aiSort={aiSortNotes}
        onApply={applyDump}
        noun="Block"
        nounPl="Blöcke"
        placeholder={'Rohe Infos einfügen … z. B.\n„Absender: Max Mustermann\nDomain: firma.online, firma.de\nWunschkunden: Firma A, Firma B"\n\nDie KI verteilt die Infos automatisch auf die passenden Felder – alles Übrige landet als Anmerkung.'}
      />

      {/* Benötigte Infos: Wert eintragen & abhaken. Der Setup-Fortschritt
          (technische Checkliste) wird im Status-Tab angezeigt. */}
      <section className="rp-block">
        <SectionHead
          eyebrow="Vom Kunden"
          title="Benötigte Infos"
          sub="Ein Hub für alle Infos, die wir vom Kunden brauchen. Antwort direkt eintragen – bei Mehrfach-Werten (z. B. Domains) mehrere Pills anlegen. Zu jedem Punkt lassen sich auch Dateien anhängen (z. B. Referenz-Mails als PDF oder die Signatur als Bild/HTML). Per Info-Dump oben lässt sich alles auf einmal befüllen."
          action={<SetupMeter done={detailDone} total={details.length} noun="erfasst" />}
        />
        <div className="rp-detail-list">
          {details.map((d, i) => (
            <div key={i} className={`rp-detail-card ${isDetailFilled(d) ? 'rp-detail-filled' : ''}`}>
              <div className="rp-detail-head">
                <input
                  className="rp-detail-label"
                  value={d.label}
                  placeholder="Bezeichnung…"
                  onChange={(e) => setDetail(i, { label: e.target.value })}
                />
                <button
                  type="button"
                  className={`rp-detail-mode ${d.type === 'pills' ? 'rp-detail-mode-on' : ''}`}
                  onClick={() => setDetail(i, { type: d.type === 'pills' ? 'text' : 'pills' })}
                  title={d.type === 'pills' ? 'Auf einfaches Textfeld umstellen' : 'Auf Mehrfach-Werte (Pills) umstellen'}
                >
                  {d.type === 'pills' ? 'Mehrfach-Werte' : 'Textfeld'}
                </button>
                <button
                  type="button"
                  className="rp-detail-del"
                  onClick={() => removeDetail(i)}
                  title="Info entfernen"
                  aria-label="Info entfernen"
                >
                  ×
                </button>
              </div>

              <AutoGrowTextarea
                className="rp-detail-prompt"
                value={d.prompt}
                placeholder="Kurzer Hinweis – was genau brauchen wir?"
                onChange={(e) => setDetail(i, { prompt: e.target.value })}
              />

              {d.type === 'pills' ? (
                <DetailPills
                  tags={d.tags || []}
                  onChange={(tags) => setDetail(i, { tags })}
                  placeholder="z. B. firma.online + Enter…"
                />
              ) : (
                <AutoGrowTextarea
                  className="rp-detail-value"
                  value={d.value}
                  placeholder="Antwort eintragen…"
                  onChange={(e) => setDetail(i, { value: e.target.value })}
                />
              )}

              <DetailAttachments
                attachments={d.attachments || []}
                onChange={(attachments) => setDetail(i, { attachments })}
                onUpload={uploadAttachment}
              />
            </div>
          ))}
          <button type="button" className="rp-setup-add-section" onClick={addDetail}>
            <span className="rp-setup-add-icon">+</span>
            Info hinzufügen
          </button>
        </div>
      </section>

      {/* 3) Weitere wichtige Anmerkungen (aus Gesprächen) + Bulk-Import */}
      <section className="rp-block">
        <SectionHead
          eyebrow="Aus Gesprächen"
          title="Weitere wichtige Anmerkungen"
          sub="Zusätzliche Infos, die in Calls oder Chats aufgetaucht sind. Per Info-Dump oben importieren (wird automatisch sortiert) oder manuell ergänzen – jederzeit bearbeitbar."
        />

        <div className="rp-notes">
          {notes.length === 0 && (
            <p className="rp-notes-empty">Noch keine zusätzlichen Anmerkungen. Nutze oben den Info-Dump oder füge manuell eine Anmerkung hinzu.</p>
          )}
          {notes.map((n, i) => (
            <div key={i} className="rp-note-card">
              <button
                type="button"
                className="rp-note-remove"
                onClick={() => removeNote(i)}
                title="Anmerkung entfernen"
                aria-label="Anmerkung entfernen"
              >
                ×
              </button>
              {n.stage && (
                <span className="rp-note-stage" title={n.droppedAt ? `Gedroppt am ${new Date(n.droppedAt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}` : undefined}>
                  {n.stage}
                </span>
              )}
              <AutoGrowTextarea
                className="rp-note-title"
                value={n.title || ''}
                placeholder="Titel (optional)…"
                onChange={(e) => updateNote(i, 'title', e.target.value)}
              />
              <AutoGrowTextarea
                className="rp-note-body"
                value={n.body || ''}
                placeholder="Anmerkung…"
                onChange={(e) => updateNote(i, 'body', e.target.value)}
              />
            </div>
          ))}
          <button type="button" className="rp-note-add" onClick={addNote}>
            <span className="rp-setup-add-icon">+</span>
            Anmerkung hinzufügen
          </button>
        </div>
      </section>
    </div>
  );
};

// ── E-Mail-DNA-Quiz ──
// Optionales, geführtes Quiz (Step-Through), mit dem der Kunde Schritt für Schritt
// festlegt, wie seine Outreach-E-Mails klingen sollen. Jede Frage zeigt zu jeder
// Option ein konkretes Beispiel, damit auch Nicht-Copywriter den Unterschied „hören".
// Jede Antwort ist optional und darf leer bleiben. Persistiert in report.emailSetup.
const EMAIL_DNA_QUESTIONS = [
  {
    id: 'tone',
    label: 'Grundton',
    hint: 'Wie sollen sich die E-Mails anfühlen?',
    icon: 'chat',
    layout: 'list',
    options: [
      { value: 'locker', label: 'Locker & freundschaftlich', desc: 'Nahbar, wie an einen Bekannten', example: 'Hey Max, kurze Frage – hast du Lust, dass wir mal 10 Minuten quatschen?' },
      { value: 'professionell', label: 'Professionell & sachlich', desc: 'Klar und seriös auf den Punkt', example: 'Guten Tag Herr Müller, ich habe einen konkreten Vorschlag, wie Sie mehr Termine gewinnen.' },
      { value: 'formell', label: 'Höflich & formell', desc: 'Zurückhaltend und respektvoll', example: 'Sehr geehrter Herr Müller, gerne möchte ich Ihnen unverbindlich unsere Zusammenarbeit vorstellen.' },
    ],
  },
  {
    id: 'address',
    label: 'Ansprache',
    hint: 'Wie wird der Empfänger angesprochen?',
    icon: 'user',
    layout: 'row',
    options: [
      { value: 'du', label: 'Du', desc: 'Persönlich und direkt', example: 'Ich hab mir dein Angebot angeschaut und glaube, da geht für dich noch mehr.' },
      { value: 'sie', label: 'Sie', desc: 'Klassisch und höflich', example: 'Ich habe mir Ihr Angebot angeschaut und glaube, da geht für Sie noch mehr.' },
    ],
  },
  {
    id: 'length',
    label: 'Länge',
    hint: 'Wie viel Text darf es sein?',
    icon: 'ruler',
    layout: 'list',
    options: [
      { value: 'kurz', label: 'Kurz & knackig', desc: '3–4 Sätze, sofort zum Punkt', example: 'Hi Max, ihr macht X. Wir bringen euch planbar neue Kunden über Cold Outreach. Kurzes Gespräch nächste Woche?' },
      { value: 'mittel', label: 'Ausgewogen', desc: 'Etwas Kontext, dann der Punkt', example: 'Hi Max, mir ist aufgefallen, dass ihr stark auf Empfehlungen setzt. Wir helfen Firmen wie euch, zusätzlich planbar über Cold Outreach an Kunden zu kommen. Hättet ihr nächste Woche 15 Minuten?' },
      { value: 'lang', label: 'Ausführlich', desc: 'Mehr Hintergrund & Beweise', example: 'Hi Max, … (mehrere Absätze mit Kontext, Beispielen und Ergebnissen, bevor die eigentliche Frage kommt).' },
    ],
  },
  {
    id: 'energy',
    label: 'Energie',
    hint: 'Wie viel Verkaufs-Power steckt drin?',
    icon: 'bolt',
    layout: 'row',
    options: [
      { value: 'ruhig', label: 'Ruhig & seriös', desc: 'Dezent, kein Druck', example: 'Falls das spannend für Sie ist, freue ich mich über eine kurze Rückmeldung.' },
      { value: 'ausgewogen', label: 'Ausgewogen', desc: 'Freundlich mit klarem Ziel', example: 'Ich denke, das passt gut zu euch – sollen wir kurz telefonieren?' },
      { value: 'energetisch', label: 'Energetisch', desc: 'Spürbar verkäuferisch, treibend', example: 'Das hebt euren Vertrieb auf ein neues Level – lasst es uns diese Woche angehen!' },
    ],
  },
  {
    id: 'opener',
    label: 'Einstieg',
    hint: 'Womit soll die E-Mail starten?',
    icon: 'flag',
    layout: 'list',
    options: [
      { value: 'beobachtung', label: 'Mit einer Beobachtung', desc: 'Persönlicher Einstieg über den Empfänger', example: 'Mir ist aufgefallen, dass ihr gerade stark im Bereich X wachst …' },
      { value: 'direkt', label: 'Direkt zum Punkt', desc: 'Sofort der Grund der Mail', example: 'Ich melde mich, weil wir euch planbar neue Kunden bringen können.' },
      { value: 'frage', label: 'Mit einer Frage', desc: 'Holt den Leser sofort ab', example: 'Wie gewinnt ihr aktuell neue Kunden – eher über Empfehlungen oder aktiv?' },
      { value: 'problem', label: 'Mit dem Problem', desc: 'Spricht den Schmerzpunkt an', example: 'Die meisten verlassen sich auf Empfehlungen – und stehen ohne sie plötzlich still.' },
    ],
  },
  {
    id: 'cta',
    label: 'Abschluss',
    hint: 'Wie soll die Aufforderung am Ende klingen?',
    icon: 'send',
    layout: 'list',
    options: [
      { value: 'konkret', label: 'Konkreter Vorschlag', desc: 'Klares Angebot mit Termin', example: 'Passt Dienstag um 14 Uhr für ein 15-Minuten-Telefonat?' },
      { value: 'soft', label: 'Sanfte Frage', desc: 'Niedrigschwellig und freundlich', example: 'Wäre das einen kurzen Austausch wert?' },
      { value: 'unverbindlich', label: 'Ganz unverbindlich', desc: 'Kein Druck, Tür bleibt offen', example: 'Melde dich einfach, wenn das für dich interessant klingt.' },
    ],
  },
  {
    id: 'humor',
    label: 'Persönlichkeit',
    hint: 'Wie viel Persönlichkeit & Humor?',
    icon: 'smile',
    layout: 'row',
    options: [
      { value: 'serioes', label: 'Seriös', desc: 'Sachlich, kein Schnickschnack', example: 'Ich halte es kurz und komme direkt zum Punkt.' },
      { value: 'augenzwinkern', label: 'Mit Augenzwinkern', desc: 'Sympathisch, leicht locker', example: 'Versprochen – das ist keine dieser 0815-Akquise-Mails 😉' },
      { value: 'flapsig', label: 'Frech & flapsig', desc: 'Verspielt, mutig, eigen', example: 'Ich weiß, noch eine Mail im Postfach – aber die hier lohnt sich, ehrlich!' },
    ],
  },
  {
    id: 'emoji',
    label: 'Emojis',
    hint: 'Emojis in den E-Mails?',
    icon: 'sparkles',
    layout: 'row',
    options: [
      { value: 'keine', label: 'Keine', desc: 'Reiner Text, ganz ohne', example: 'Ich freue mich auf Ihre Rückmeldung.' },
      { value: 'sparsam', label: 'Sparsam', desc: 'Vereinzelt als Akzent', example: 'Klingt das interessant für euch? 👍' },
      { value: 'gerne', label: 'Gern verwendet', desc: 'Auflockernd und verspielt', example: 'Freu mich riesig auf deine Antwort! 🚀😊' },
    ],
  },
  // ── Zusatzfragen für das externe Account-/Kampagnen-Setup (WorldDB) ──
  // Diese decken Felder ab, die im Ziel-Schema fehlen: Sprache (identity/campaign),
  // Ziel (campaign.goal) und Grußformel (brain.signOff). Keine KI-Beispiele nötig.
  {
    id: 'language',
    label: 'Sprache',
    hint: 'In welcher Sprache sollen die E-Mails verschickt werden?',
    icon: 'globe',
    layout: 'row',
    noAiExamples: true,
    options: [
      { value: 'de', label: 'Deutsch', desc: 'E-Mails auf Deutsch', example: 'Hallo Max, kurze Frage zu eurem Vertrieb …' },
      { value: 'en', label: 'Englisch', desc: 'E-Mails auf Englisch', example: 'Hi Max, quick question about how you win new clients …' },
      { value: 'auto', label: 'Automatisch', desc: 'Passend zur Zielgruppe wählen', example: 'Sprache wird automatisch zur Region des Empfängers gewählt.' },
    ],
  },
  {
    id: 'goal',
    label: 'Ziel',
    hint: 'Was soll die E-Mail erreichen?',
    icon: 'target',
    layout: 'list',
    noAiExamples: true,
    options: [
      { value: 'call', label: 'Termin / Call buchen', desc: 'Klares Ziel: ein Gespräch terminieren', example: 'Passt Dienstag um 14 Uhr für 15 Minuten?' },
      { value: 'reply', label: 'Antwort & Gespräch', desc: 'Erst einmal ins Gespräch kommen', example: 'Wäre das einen kurzen Austausch wert?' },
      { value: 'demo', label: 'Demo / Präsentation', desc: 'Eine Vorführung vereinbaren', example: 'Soll ich dir in 10 Minuten zeigen, wie das aussieht?' },
      { value: 'visit', label: 'Website / Angebot ansehen', desc: 'Auf eine Seite oder ein Angebot leiten', example: 'Schau dir das hier kurz an, dann weißt du, was ich meine.' },
    ],
  },
  {
    id: 'signOff',
    label: 'Grußformel',
    hint: 'Wie soll die E-Mail unterschrieben werden?',
    icon: 'pen',
    layout: 'row',
    noAiExamples: true,
    options: [
      { value: 'auto', label: 'Automatisch passend', desc: 'KI wählt eine zum Ton passende Grußformel', example: 'z. B. „Beste Grüße", „Viele Grüße" oder „Liebe Grüße" je nach Ton.' },
      { value: 'fixed', label: 'Feste Grußformel', desc: 'Immer dieselbe, mit dem Absendernamen', example: 'Beste Grüße, [Dein Vorname]' },
    ],
  },
];

// report.emailSetup -> sauberer State (leere Strings als Default = unausgefüllt).
const normalizeEmailSetup = (raw) => {
  const r = raw && typeof raw === 'object' ? raw : {};
  const out = {};
  EMAIL_DNA_QUESTIONS.forEach((q) => {
    out[q.id] = typeof r[q.id] === 'string' ? r[q.id] : '';
  });
  // „Was unbedingt rein"/„No-Gos" als getrennte Freitextfelder (Altbestand: notes -> dos).
  out.dos = typeof r.dos === 'string' ? r.dos : (typeof r.notes === 'string' ? r.notes : '');
  out.donts = typeof r.donts === 'string' ? r.donts : '';
  // Referenzen/Beweise (Freitext): Kunden, Case Studies, Zahlen – fürs Kampagnen-Setup.
  out.proof = typeof r.proof === 'string' ? r.proof : '';
  // KI-generierte Beispieltexte je Frage/Option: { [questionId]: { [optionValue]: text } }.
  out.examples = {};
  if (r.examples && typeof r.examples === 'object') {
    EMAIL_DNA_QUESTIONS.forEach((q) => {
      const got = r.examples[q.id];
      if (got && typeof got === 'object') {
        const co = {};
        q.options.forEach((o) => {
          if (typeof got[o.value] === 'string' && got[o.value].trim()) co[o.value] = got[o.value];
        });
        if (Object.keys(co).length) out.examples[q.id] = co;
      }
    });
  }
  // Als Favorit markierte Beispiele: [{ category, option, label, text }].
  out.favorites = Array.isArray(r.favorites)
    ? r.favorites
        .filter((f) => f && typeof f === 'object' && typeof f.text === 'string' && f.text.trim())
        .map((f) => ({
          category: String(f.category || ''),
          option: String(f.option || ''),
          label: String(f.label || ''),
          text: String(f.text),
        }))
    : [];
  return out;
};

// Anzahl beantworteter DNA-Fragen (für Badge/Fortschritt).
const countDnaAnswers = (setup) => EMAIL_DNA_QUESTIONS.filter((q) => setup && setup[q.id]).length;

// Schlichte Line-Icons je Frage – geben jedem Schritt eine eigene Optik.
const DnaIcon = ({ name }) => {
  const common = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  switch (name) {
    case 'chat':
      return <svg {...common}><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg>;
    case 'user':
      return <svg {...common}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>;
    case 'ruler':
      return <svg {...common}><path d="M3 8h18M3 8v8h18V8M7 8v3M11 8v4M15 8v3M19 8v4" /></svg>;
    case 'bolt':
      return <svg {...common}><path d="M13 2 3 14h7l-1 8 10-12h-7l1-8z" /></svg>;
    case 'flag':
      return <svg {...common}><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><path d="M4 22V4" /></svg>;
    case 'send':
      return <svg {...common}><path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>;
    case 'smile':
      return <svg {...common}><circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><path d="M9 9h.01M15 9h.01" /></svg>;
    case 'sparkles':
      return <svg {...common}><path d="M12 3l1.6 4.6L18 9.2l-4.4 1.6L12 15l-1.6-4.2L6 9.2l4.4-1.6L12 3z" /><path d="M19 14l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2z" /></svg>;
    case 'shield':
      return <svg {...common}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></svg>;
    case 'list':
      return <svg {...common}><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></svg>;
    case 'helix':
      return <svg {...common}><path d="M9 3c0 3 6 4 6 9s-6 6-6 9" /><path d="M15 3c0 3-6 4-6 9s6 6 6 9" /><path d="M9.5 6h5M8.4 10h7.2M8.4 14h7.2M9.5 18h5" /></svg>;
    case 'globe':
      return <svg {...common}><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>;
    case 'target':
      return <svg {...common}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></svg>;
    case 'pen':
      return <svg {...common}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>;
    default:
      return null;
  }
};

// Geführtes Quiz mit eigenem Intro-Schritt zum KI-Generieren der Beispieltexte,
// einem Schritt pro Frage, einem No-Gos-Schritt und einer Zusammenfassung.
// Auswahl einer Option springt sanft zum nächsten Schritt; über „Zurück" lässt
// sich jede Antwort ändern, ein erneuter Klick hebt sie auf. Beispieltexte können
// pro Option als Favorit markiert werden (Referenzen für künftige KI-Sets).
const EmailDnaQuiz = ({ setup, setSetup, generateExamples }) => {
  const N = EMAIL_DNA_QUESTIONS.length;
  const STEP_INTRO = 0;
  const STEP_NOTES = N + 1;
  const STEP_SUMMARY = N + 2;
  const totalSteps = N + 3;

  // Schon fertig ausgefülltes Quiz öffnet direkt die Zusammenfassung (z. B. nach Reload),
  // statt erneut beim Intro zu starten.
  const [step, setStep] = useState(() => (countDnaAnswers(setup) >= N ? STEP_SUMMARY : 0));
  const [dir, setDir] = useState('fwd');
  const [genState, setGenState] = useState('idle'); // idle | loading | error
  const [genError, setGenError] = useState('');
  const advanceTimer = useRef(null);

  useEffect(() => () => { if (advanceTimer.current) clearTimeout(advanceTimer.current); }, []);

  const go = (next) => {
    const target = Math.max(0, Math.min(totalSteps - 1, next));
    setDir(target >= step ? 'fwd' : 'back');
    setStep(target);
  };

  // Frage-Index aus dem Schritt (Schritt 1..N => Frage 0..N-1).
  const pick = (id, value) => {
    setSetup((s) => ({ ...s, [id]: s[id] === value ? '' : value }));
    if (setup[id] !== value) {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
      advanceTimer.current = setTimeout(() => go(step + 1), 260);
    }
  };

  // Beispieltext: KI-generiert (falls vorhanden), sonst der statische Default.
  const exampleFor = (q, o) => {
    const ex = setup.examples && setup.examples[q.id] && setup.examples[q.id][o.value];
    return (typeof ex === 'string' && ex.trim()) ? ex : o.example;
  };
  const hasExamples = setup.examples && Object.keys(setup.examples).length > 0;

  const isFav = (qid, val) => (setup.favorites || []).some((f) => f.category === qid && f.option === val);
  const toggleFav = (q, o) => setSetup((s) => {
    const favs = Array.isArray(s.favorites) ? s.favorites : [];
    const exists = favs.some((f) => f.category === q.id && f.option === o.value);
    if (exists) return { ...s, favorites: favs.filter((f) => !(f.category === q.id && f.option === o.value)) };
    const ex = s.examples && s.examples[q.id] && s.examples[q.id][o.value];
    const text = (typeof ex === 'string' && ex.trim()) ? ex : o.example;
    return { ...s, favorites: [...favs, { category: q.id, option: o.value, label: o.label, text }] };
  });

  const runGenerate = async () => {
    if (!generateExamples || genState === 'loading') return;
    setGenState('loading');
    setGenError('');
    try {
      const examples = await generateExamples();
      if (!examples || !Object.keys(examples).length) throw new Error('Keine Beispiele erhalten.');
      setSetup((s) => ({ ...s, examples: { ...(s.examples || {}), ...examples } }));
      setGenState('idle');
    } catch (e) {
      setGenError(e && e.message ? e.message : 'Generierung fehlgeschlagen.');
      setGenState('error');
    }
  };

  // Quiz komplett zurücksetzen: alle Antworten + Freitexte leeren und beim Intro neu starten.
  // Favoriten und bereits generierte KI-Beispiele bleiben erhalten.
  const resetQuiz = () => {
    if (typeof window !== 'undefined' && !window.confirm('Quiz wirklich neu starten? Alle Antworten werden gelöscht.')) return;
    setSetup((s) => {
      const cleared = {};
      EMAIL_DNA_QUESTIONS.forEach((q) => { cleared[q.id] = ''; });
      return { ...s, ...cleared, dos: '', donts: '', proof: '' };
    });
    go(0);
  };

  const answered = countDnaAnswers(setup);
  const progressPct = Math.round(((step + 1) / totalSteps) * 100);

  // ── Inhalt des aktuellen Schritts ──
  let body;
  if (step === STEP_INTRO) {
    body = (
      <div className="rp-dna-step rp-dna-intro-step">
        <span className="rp-dna-intro-badge"><DnaIcon name="helix" /></span>
        <div className="rp-dna-intro-copy">
          <h3 className="rp-dna-step-title rp-dna-intro-title">E-Mail DNA Quiz</h3>
          <p className="rp-dna-intro-text">{N} Fragen zu Stil, Sprache und Ziel eurer E-Mails.</p>
        </div>
        <div className="rp-dna-intro-actions">
          <button
            type="button"
            className="rp-dna-intro-start"
            onClick={() => { if (generateExamples && !hasExamples) runGenerate(); go(1); }}
          >
            Quiz starten
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
          </button>
        </div>
        {generateExamples && genState === 'loading' && (
          <div className="rp-dna-genloading" role="status" aria-live="polite">
            <div className="rp-dna-genloading-head">
              <span className="rp-dna-genloading-orb" aria-hidden="true" />
              <span>Beispiele werden geschrieben …</span>
            </div>
            <div className="rp-dna-genshimmer" aria-hidden="true">
              <span className="rp-dna-genline" />
              <span className="rp-dna-genline" />
              <span className="rp-dna-genline" />
            </div>
          </div>
        )}
        {generateExamples && hasExamples && genState !== 'loading' && (
          <span className="rp-dna-intro-hint rp-dna-intro-ok">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
            Auf diesen Prospect zugeschnitten.
          </span>
        )}
        {generateExamples && genState === 'error' && <span className="rp-dna-intro-hint rp-dna-intro-err">{genError}</span>}
        {!generateExamples && (
          <span className="rp-dna-intro-hint">KI-Beispiele gibt es nur auf der Live-Report-Seite – das Quiz läuft auch mit Standard-Beispielen.</span>
        )}
      </div>
    );
  } else if (step <= N) {
    const q = EMAIL_DNA_QUESTIONS[step - 1];
    const isRow = q.layout === 'row';
    body = (
      <div className="rp-dna-step">
        <div className="rp-dna-step-head">
          <span className="rp-dna-step-icon"><DnaIcon name={q.icon} /></span>
          <div>
            <span className="rp-dna-step-eyebrow">Frage {step} / {N}</span>
            <h3 className="rp-dna-step-title">{q.hint}</h3>
          </div>
        </div>
        <div className={`rp-dna-choices ${isRow ? 'rp-dna-choices-row' : ''}`}>
          {q.options.map((o) => {
            const on = setup[q.id] === o.value;
            const fav = isFav(q.id, o.value);
            return (
              <div
                key={o.value}
                role="button"
                tabIndex={0}
                className={`rp-dna-choice ${isRow ? 'rp-dna-choice-row' : ''} ${on ? 'is-on' : ''}`}
                onClick={() => pick(q.id, o.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(q.id, o.value); } }}
              >
                <span className="rp-dna-choice-head">
                  <span className="rp-dna-choice-radio" aria-hidden="true">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                  </span>
                  <span className="rp-dna-choice-label">{o.label}</span>
                </span>
                <span className="rp-dna-choice-desc">{o.desc}</span>
                <span className="rp-dna-choice-example-text">{exampleFor(q, o)}</span>
                <button
                  type="button"
                  className={`rp-dna-fav ${fav ? 'is-on' : ''}`}
                  onClick={(e) => { e.stopPropagation(); toggleFav(q, o); }}
                  title={fav ? 'Aus Favoriten entfernen' : 'Beispiel als Favorit merken'}
                  aria-label={fav ? 'Aus Favoriten entfernen' : 'Beispiel als Favorit merken'}
                  aria-pressed={fav}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill={fav ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1L12 21l7.7-7.6 1.1-1a5.5 5.5 0 0 0 0-7.8z" /></svg>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  } else if (step === STEP_NOTES) {
    body = (
      <div className="rp-dna-step">
        <div className="rp-dna-step-head">
          <span className="rp-dna-step-icon"><DnaIcon name="shield" /></span>
          <div>
            <span className="rp-dna-step-eyebrow">Fast geschafft</span>
            <h3 className="rp-dna-step-title">Was darf rein – und was auf keinen Fall?</h3>
          </div>
        </div>
        <p className="rp-dna-step-sub">Konkrete Wünsche und absolute No-Gos. Beides optional.</p>
        <div className="rp-dna-notesgrid">
          <div className="rp-dna-notesfield">
            <span className="rp-dna-notesfield-label rp-dna-dos">Unbedingt rein</span>
            <AutoGrowTextarea
              className="rp-dna-notes-input"
              value={setup.dos || ''}
              placeholder="z. B. immer mit Vornamen begrüßen, Referenzkunden nennen …"
              onChange={(e) => setSetup((s) => ({ ...s, dos: e.target.value }))}
            />
          </div>
          <div className="rp-dna-notesfield">
            <span className="rp-dna-notesfield-label rp-dna-donts">No-Gos</span>
            <AutoGrowTextarea
              className="rp-dna-notes-input"
              value={setup.donts || ''}
              placeholder={'z. B. keine Floskeln, kein „Sehr geehrte Damen und Herren", keine Rabatte …'}
              onChange={(e) => setSetup((s) => ({ ...s, donts: e.target.value }))}
            />
          </div>
        </div>
        <div className="rp-dna-notesfield" style={{ marginTop: 14 }}>
          <span className="rp-dna-notesfield-label rp-dna-proof">Referenzen & Beweise</span>
          <AutoGrowTextarea
            className="rp-dna-notes-input"
            value={setup.proof || ''}
            placeholder="Worauf dürfen wir uns berufen? z. B. bekannte Kunden, Case Studies, Ergebnisse/Zahlen, Testimonials …"
            onChange={(e) => setSetup((s) => ({ ...s, proof: e.target.value }))}
          />
        </div>
      </div>
    );
  } else {
    const favs = setup.favorites || [];
    const ringPct = N > 0 ? answered / N : 0;
    const ringCirc = 2 * Math.PI * 22;
    const isComplete = answered >= N;
    body = (
      <div className="rp-dna-step rp-dna-summary-step">
        <div className="rp-dna-summary-hero">
          <div className="rp-dna-summary-hero-glow" aria-hidden="true" />
          <div className="rp-dna-summary-hero-meter" aria-hidden="true">
            <svg width="58" height="58" viewBox="0 0 58 58">
              <circle cx="29" cy="29" r="22" className="rp-dna-ring-track" />
              <circle
                cx="29" cy="29" r="22"
                className="rp-dna-ring-fill"
                strokeDasharray={ringCirc}
                strokeDashoffset={ringCirc * (1 - ringPct)}
                transform="rotate(-90 29 29)"
              />
            </svg>
            <span className={`rp-dna-summary-hero-helix ${isComplete ? 'is-complete' : ''}`}>
              {isComplete ? (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
              ) : (
                <DnaIcon name="helix" />
              )}
            </span>
          </div>
          <div className="rp-dna-summary-hero-copy">
            <span className="rp-dna-step-eyebrow">Deine E-Mail-DNA</span>
            <h3 className="rp-dna-summary-hero-title">
              {answered === 0 ? 'Noch nichts festgelegt' : isComplete ? 'E-Mail-DNA steht' : 'Fast fertig'}
            </h3>
            <p className="rp-dna-summary-hero-sub">
              {answered === 0
                ? 'Tippe auf einen Punkt, um deinen Stil festzulegen.'
                : <><strong>{answered}</strong> von {N} Merkmalen bestimmt · jederzeit anpassbar</>}
            </p>
          </div>
        </div>
        <div className="rp-dna-summary">
          {EMAIL_DNA_QUESTIONS.map((q, i) => {
            const chosen = q.options.find((o) => o.value === setup[q.id]);
            return (
              <button key={q.id} type="button" className={`rp-dna-sumrow ${chosen ? 'is-set' : ''}`} onClick={() => go(i + 1)}>
                <span className="rp-dna-sumrow-icon" aria-hidden="true"><DnaIcon name={q.icon} /></span>
                <span className="rp-dna-sumrow-main">
                  <span className="rp-dna-sumrow-label">{q.label}</span>
                  <span className="rp-dna-sumrow-desc">{chosen ? chosen.desc : 'Noch offen'}</span>
                </span>
                <span className={`rp-dna-sumrow-value ${chosen ? '' : 'is-empty'}`}>
                  {chosen ? chosen.label : 'offen'}
                </span>
                <span className="rp-dna-sumrow-chevron" aria-hidden="true">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
                </span>
              </button>
            );
          })}
          {favs.length > 0 && (
            <div className="rp-dna-sumnote">
              <span className="rp-dna-notesfield-label rp-dna-fav-label">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1L12 21l7.7-7.6 1.1-1a5.5 5.5 0 0 0 0-7.8z" /></svg>
                {favs.length} {favs.length === 1 ? 'Favorit' : 'Favoriten'}
              </span>
              {favs.map((f, i) => <p key={i} className="rp-dna-fav-item">{f.text}</p>)}
            </div>
          )}
          {(setup.dos || '').trim() && (
            <div className="rp-dna-sumnote">
              <span className="rp-dna-notesfield-label rp-dna-dos">Unbedingt rein</span>
              <p>{setup.dos}</p>
            </div>
          )}
          {(setup.donts || '').trim() && (
            <div className="rp-dna-sumnote">
              <span className="rp-dna-notesfield-label rp-dna-donts">No-Gos</span>
              <p>{setup.donts}</p>
            </div>
          )}
          {(setup.proof || '').trim() && (
            <div className="rp-dna-sumnote">
              <span className="rp-dna-notesfield-label rp-dna-proof">Referenzen & Beweise</span>
              <p>{setup.proof}</p>
            </div>
          )}
        </div>
        <div className="rp-dna-summary-foot">
          <button type="button" className="rp-dna-reset" onClick={resetQuiz}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2v6h6" /><path d="M3 13a9 9 0 1 0 3-7.7L3 8" /></svg>
            Neu starten
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rp-dna">
      <div className="rp-dna-progress">
        <div className="rp-dna-progress-track">
          <div className="rp-dna-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <span className="rp-dna-progress-label">Schritt {step + 1} / {totalSteps}</span>
      </div>

      <div key={step} className={`rp-dna-stage rp-dna-stage-${dir}`}>
        {body}
      </div>

      {step !== STEP_INTRO && (
        <div className="rp-dna-nav">
          <button
            type="button"
            className="rp-dna-navbtn rp-dna-navbtn-ghost"
            onClick={() => go(step - 1)}
          >
            Zurück
          </button>
          {step < STEP_SUMMARY ? (
            <button type="button" className="rp-dna-navbtn rp-dna-navbtn-primary" onClick={() => go(step + 1)}>
              {step >= 1 && step <= N ? (setup[EMAIL_DNA_QUESTIONS[step - 1].id] ? 'Weiter' : 'Überspringen') : 'Weiter'}
            </button>
          ) : (
            <button type="button" className="rp-dna-navbtn rp-dna-navbtn-primary" onClick={() => go(0)}>
              Von vorne ansehen
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Name des aktuellen Bewerters – pro Browser gemerkt, damit man ihn nicht bei
// jeder Bewertung neu eingeben muss.
const REVIEWER_NAME_KEY = 'rp-reviewer-name';
const readReviewerName = () => {
  try { return localStorage.getItem(REVIEWER_NAME_KEY) || ''; } catch (e) { return ''; }
};
const fmtReviewDate = (ts) => {
  try {
    return new Date(ts).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch (e) {
    return '';
  }
};

// Eine E-Mail-Karte: links (≈60%) der editierbare E-Mail-Text, rechts (≈40%) das
// Bewertungs-Panel. Mehrere Personen können je eine Bewertung (1–10) + Kommentar
// abgeben; oben steht der Durchschnitt über alle abgegebenen Bewertungen.
const MailCard = ({ email, index, onChange, copied, highlight, onCopy, onDelete, author, onAuthorChange }) => {
  const words = countWords(email.body);
  const reviews = Array.isArray(email.reviews) ? email.reviews : [];
  const avg = reviewAverage(reviews, 'rating');
  const subjectAvg = reviewAverage(reviews, 'subjectRating');
  const ratedCount = reviews.filter((r) => r.rating > 0).length;
  const subjectRatedCount = reviews.filter((r) => r.subjectRating > 0).length;

  const [draftRating, setDraftRating] = useState(0);
  const [draftSubjectRating, setDraftSubjectRating] = useState(0);
  const [draftComment, setDraftComment] = useState('');

  const setReviews = (next) => onChange('reviews', next);

  const submit = () => {
    if (!draftRating && !draftSubjectRating && !draftComment.trim()) return;
    const name = author.trim();
    try { if (name) localStorage.setItem(REVIEWER_NAME_KEY, name); } catch (e) { /* ignore */ }
    setReviews([
      ...reviews,
      {
        id: makeReviewId(),
        author: name,
        rating: draftRating,
        subjectRating: draftSubjectRating,
        comment: draftComment.trim(),
        at: Date.now(),
      },
    ]);
    setDraftRating(0);
    setDraftSubjectRating(0);
    setDraftComment('');
  };

  const removeReview = (id) => setReviews(reviews.filter((r) => r.id !== id));

  return (
    <div className={`rp-mailcard ${highlight ? 'rp-mailcard-highlight' : ''}`} id={email.id ? `rp-mail-${email.id}` : undefined}>
      <div className="rp-mailcard-head">
        <span className="rp-mailcard-meta">
          <span className="rp-mailcard-num">E-Mail {String(index + 1).padStart(2, '0')}</span>
          {email.tag && (
            <span className="rp-mailcard-tag" title={email.tag}>
              <RpIcon name="rocket" size={11} />
              {email.tag}
            </span>
          )}
          {typeof email.createdAt === 'number' && email.createdAt > 0 && (
            <span className="rp-mailcard-date" title={new Date(email.createdAt).toLocaleString('de-DE')}>
              {new Date(email.createdAt).toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          )}
        </span>
        <div className="rp-mailcard-tools">
          {avg != null && (
            <span className="rp-mailcard-avgpill" title={`Durchschnitt aus ${ratedCount} ${ratedCount === 1 ? 'Bewertung' : 'Bewertungen'}`}>
              ★ {avg.toFixed(1)}
            </span>
          )}
          <button
            type="button"
            className={`rp-mailcard-tool ${copied ? 'rp-mailcard-tool-done' : ''}`}
            onClick={onCopy}
            title="E-Mail kopieren"
            aria-label="E-Mail kopieren"
          >
            {copied ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>
            )}
          </button>
          {onDelete && (
            <button
              type="button"
              className="rp-mailcard-tool rp-mailcard-tool-del"
              onClick={onDelete}
              title="E-Mail löschen"
              aria-label="E-Mail löschen"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" /><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /></svg>
            </button>
          )}
        </div>
      </div>
      <div className="rp-mailcard-grid">
        <div className="rp-mailcard-main">
          <input
            className="rp-mailcard-subject"
            type="text"
            value={email.subject}
            placeholder="Betreff…"
            onChange={(ev) => onChange('subject', ev.target.value)}
          />
          <AutoGrowTextarea
            className="rp-mailcard-body"
            value={email.body}
            placeholder="E-Mail-Text schreiben…"
            onChange={(ev) => onChange('body', ev.target.value)}
          />
          <div className="rp-mailcard-foot">
            <span className="rp-mailcard-count">{words} {words === 1 ? 'Wort' : 'Wörter'}</span>
          </div>
        </div>
        <aside className="rp-mailcard-side">
          {avg == null && subjectAvg == null ? (
            <div className="rp-review-summary">
              <span className="rp-review-empty">Noch keine Bewertung</span>
            </div>
          ) : (
            <div className="rp-review-summary-grid">
              <div className="rp-review-summary-item">
                <span className="rp-review-summary-label">Betreffzeile</span>
                {subjectAvg != null ? (
                  <>
                    <span className="rp-review-avg">{subjectAvg.toFixed(1)}<small>/10</small></span>
                    <StarRating value={subjectAvg} readOnly />
                    <span className="rp-review-count">{subjectRatedCount} {subjectRatedCount === 1 ? 'Bewertung' : 'Bewertungen'}</span>
                  </>
                ) : (
                  <span className="rp-review-empty">–</span>
                )}
              </div>
              <div className="rp-review-summary-item">
                <span className="rp-review-summary-label">E-Mail</span>
                {avg != null ? (
                  <>
                    <span className="rp-review-avg">{avg.toFixed(1)}<small>/10</small></span>
                    <StarRating value={avg} readOnly />
                    <span className="rp-review-count">{ratedCount} {ratedCount === 1 ? 'Bewertung' : 'Bewertungen'}</span>
                  </>
                ) : (
                  <span className="rp-review-empty">–</span>
                )}
              </div>
            </div>
          )}

          {reviews.length > 0 && (
            <div className="rp-review-list">
              {reviews.map((r) => {
                const name = (r.author || '').trim() || 'Anonym';
                const initial = name.charAt(0).toUpperCase();
                return (
                  <div key={r.id} className="rp-review-item">
                    <span className="rp-review-avatar" aria-hidden="true">{initial}</span>
                    <div className="rp-review-body">
                      <div className="rp-review-item-head">
                        <span className="rp-review-author">{name}</span>
                        <span className="rp-review-date">{fmtReviewDate(r.at)}</span>
                        <button
                          type="button"
                          className="rp-review-del"
                          onClick={() => removeReview(r.id)}
                          title="Bewertung entfernen"
                          aria-label="Bewertung entfernen"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                      </div>
                      {(r.subjectRating > 0 || r.rating > 0) && (
                        <div className="rp-review-chips">
                          {r.subjectRating > 0 && (
                            <span className="rp-review-chip"><span className="rp-review-chip-k">Betreff</span><span className="rp-review-chip-v">★ {r.subjectRating}</span></span>
                          )}
                          {r.rating > 0 && (
                            <span className="rp-review-chip"><span className="rp-review-chip-k">E-Mail</span><span className="rp-review-chip-v">★ {r.rating}</span></span>
                          )}
                        </div>
                      )}
                      {r.comment && <p className="rp-review-comment">{r.comment}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="rp-review-form">
            <span className="rp-mailcard-side-label">Deine Bewertung</span>
            <input
              className="rp-review-name"
              type="text"
              value={author}
              placeholder="Dein Name"
              onChange={(ev) => onAuthorChange(ev.target.value)}
            />
            <div className="rp-review-rate-row">
              <span className="rp-review-rate-label">Betreffzeile</span>
              <StarRating value={draftSubjectRating} onChange={setDraftSubjectRating} />
            </div>
            <div className="rp-review-rate-row">
              <span className="rp-review-rate-label">E-Mail</span>
              <StarRating value={draftRating} onChange={setDraftRating} />
            </div>
            <AutoGrowTextarea
              className="rp-review-input"
              value={draftComment}
              placeholder="Anmerkung / Kommentar …"
              onChange={(ev) => setDraftComment(ev.target.value)}
            />
            <button
              type="button"
              className="rp-review-submit"
              onClick={submit}
              disabled={!draftRating && !draftSubjectRating && !draftComment.trim()}
            >
              Bewertung hinzufügen
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};

// ── Ansicht: E-Mail-Setup (Vault + E-Mail-DNA) ──
// Switch zwischen dem editierbaren „E-Mail Vault" (Outreach-Texte) und dem
// kurzen E-Mail-DNA-Quiz. Beides persistiert über den Header-Speichern-Button
// (report.emails bzw. report.emailSetup).
const EmailTextView = ({
  emails,
  setEmails,
  setup,
  setSetup,
  generateExamples,
  focusDay,
  initialMode,
  // Setup-Details (Kick-Off) – als dritter Sub-Toggle „Setup-Details" eingebettet.
  kickoff,
  setKickoff,
  aiSortNotes,
  uploadAttachment,
  setupImportOpen,
  setSetupImportOpen,
  // Wird hochgezählt, wenn von außen (z. B. Link im Status-Tab) gezielt die
  // Setup-Details geöffnet werden sollen.
  setupModeSignal,
  slug,
}) => {
  const [copiedIdx, setCopiedIdx] = useState(null);
  // Direktlink zu den E-Mail-Texten (?tab=emails) zum Kopieren & Versenden.
  const [linkCopied, setLinkCopied] = useState(false);
  const copyEmailLink = async () => {
    try {
      const url = `${window.location.origin}${window.location.pathname}?tab=emails`;
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1600);
    } catch (err) {
      /* clipboard nicht verfügbar */
    }
  };
  // Name des Bewerters – über alle E-Mail-Karten geteilt und im Browser-Cache
  // gemerkt, damit er beim nächsten Review automatisch vorausgefüllt ist.
  const [reviewerName, setReviewerName] = useState(readReviewerName);
  const onAuthorChange = (val) => {
    setReviewerName(val);
    try { localStorage.setItem(REVIEWER_NAME_KEY, val.trim()); } catch (e) { /* ignore */ }
  };
  // E-Mail-DNA zuerst, solange das Setup noch nicht vollständig ausgefüllt ist.
  const [mode, setMode] = useState(() => {
    if (initialMode) return initialMode; // z. B. ?tab=setup → Setup-Details, ?tab=emails → E-Mail-Texte.
    return countDnaAnswers(setup) >= EMAIL_DNA_QUESTIONS.length ? 'vault' : 'dna';
  }); // vault | dna | setup

  // Ein Link aus dem Status-Tab kann gezielt die Setup-Details öffnen.
  useEffect(() => {
    if (setupModeSignal) setMode('setup');
  }, [setupModeSignal]);

  // Deep-Link ?day=<n>: nach dem Öffnen zu den Mails dieses Launch-Phase-Tages
  // scrollen und sie kurz hervorheben. Läuft genau einmal (sobald die Texte –
  // ggf. erst nach dem Merge der Experimentier-Texte – in der Liste sind).
  const [highlightId, setHighlightId] = useState(null);
  const focusDoneRef = useRef(false);
  useEffect(() => {
    if (!focusDay || focusDoneRef.current) return;
    if (mode !== 'vault') { setMode('vault'); return; }
    const tag = experimentTextTag(focusDay);
    const target = emails.find((e) => e.tag === tag);
    if (!target || !target.id) return;
    focusDoneRef.current = true;
    const t = setTimeout(() => {
      const el = document.getElementById(`rp-mail-${target.id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightId(target.id);
      setTimeout(() => setHighlightId(null), 2600);
    }, 400);
    return () => clearTimeout(t);
  }, [focusDay, mode, emails]);

  const update = (i, key, val) => setEmails((list) => list.map((e, idx) => (idx === i ? { ...e, [key]: val } : e)));
  const add = () => setEmails((list) => [{ id: makeEmailId(), subject: '', body: '', reviews: [], createdAt: Date.now() }, ...list]);
  // Löschen mit Zwischenfrage: erst Index merken (öffnet das Bestätigungs-Modal),
  // dann bei Bestätigung wirklich aus der Liste entfernen.
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState(null);
  const confirmDeleteEmail = confirmDeleteIdx != null ? emails[confirmDeleteIdx] : null;
  const remove = (i) => setEmails((list) => list.filter((_, idx) => idx !== i));

  const copy = async (i) => {
    const e = emails[i];
    const text = `Betreff: ${e.subject || ''}\n\n${e.body || ''}`.trim();
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(i);
      setTimeout(() => setCopiedIdx((c) => (c === i ? null : c)), 1600);
    } catch (err) {
      /* clipboard nicht verfügbar */
    }
  };

  const totalWords = emails.reduce((s, e) => s + countWords(e.body), 0);
  const dnaAnswered = countDnaAnswers(setup);

  // Badge am „Setup-Details"-Toggle: Anteil der erfassten benötigten Infos.
  // Der technische Setup-Fortschritt liegt jetzt im Status-Tab.
  const setupDetails = Array.isArray(kickoff && kickoff.details) ? kickoff.details : [];
  const isSetupDetailFilled = (d) => {
    if (Array.isArray(d.attachments) && d.attachments.length > 0) return true;
    return d.type === 'pills' ? (Array.isArray(d.tags) && d.tags.length > 0) : !!(d.value || '').trim();
  };
  const setupTotal = setupDetails.length;
  const setupDone = setupDetails.filter(isSetupDetailFilled).length;
  const setupPct = setupTotal ? Math.round((setupDone / setupTotal) * 100) : 0;

  const titleByMode = mode === 'vault' ? 'E-Mail-Texte' : mode === 'setup' ? 'Einrichtungsdetails' : 'E-Mail-DNA';
  const subByMode =
    mode === 'vault'
      ? 'Lege hier deine Outreach-E-Mails ab – Betreff und Text pro Mail, jederzeit bearbeitbar. Oben im Header speichern.'
      : mode === 'setup'
      ? 'Alle Infos & Checklisten-Punkte für den Start – sammle hier, was wir vom Kunden brauchen.'
      : 'Kurzes Quiz, das festhält, wie die E-Mails ungefähr klingen sollen – einfach gehalten, vom Kunden auszufüllen.';

  const modeToggle = (
    <div className="rp-emailhead-actions rp-emailhead-actions-below">
      <div className="rp-emailmode" role="tablist" aria-label="Account-Setup-Ansicht">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'dna'}
          className={`rp-emailmode-btn ${mode === 'dna' ? 'is-on' : ''}`}
          onClick={() => setMode('dna')}
        >
          E-Mail-DNA
          {dnaAnswered > 0 && <span className="rp-emailmode-badge">{dnaAnswered}</span>}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'setup'}
          className={`rp-emailmode-btn ${mode === 'setup' ? 'is-on' : ''}`}
          onClick={() => setMode('setup')}
          title={`${setupPct}% bereit · ${setupTotal - setupDone} offen`}
        >
          Setup-Details
          <span className={`rp-emailmode-badge ${setupPct === 100 ? 'rp-emailmode-badge-full' : ''}`}>{setupPct}%</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'vault'}
          className={`rp-emailmode-btn ${mode === 'vault' ? 'is-on' : ''}`}
          onClick={() => setMode('vault')}
        >
          E-Mail-Texte
          {emails.length > 0 && <span className="rp-emailmode-badge">{emails.length}</span>}
        </button>
      </div>
      {mode === 'vault' && (
        <span className="rp-vault-meta">{emails.length} {emails.length === 1 ? 'E-Mail' : 'E-Mails'} · {totalWords} Wörter</span>
      )}
      {mode === 'vault' && slug && (
        <button
          type="button"
          className={`rp-vault-link ${linkCopied ? 'is-copied' : ''}`}
          onClick={copyEmailLink}
          title="Direktlink zu den E-Mail-Texten kopieren"
          aria-label="Direktlink zu den E-Mail-Texten kopieren"
        >
          {linkCopied ? (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
          ) : (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>
          )}
          <span className="rp-vault-link-label">{linkCopied ? 'Link kopiert' : 'Link'}</span>
        </button>
      )}
      {mode === 'setup' && setSetupImportOpen && (
        <ImportToggle open={setupImportOpen} onClick={() => setSetupImportOpen((o) => !o)} />
      )}
    </div>
  );

  return (
    <div className="rp-tab">
      <section className="rp-block">
        <SectionHead
          eyebrow="Outreach Setup"
          title={titleByMode}
          sub={subByMode}
        />
        {modeToggle}
        {mode === 'setup' ? (
          <SetupDetailsView
            kickoff={kickoff}
            setKickoff={setKickoff}
            aiSortNotes={aiSortNotes}
            uploadAttachment={uploadAttachment}
            importOpen={setupImportOpen}
            setImportOpen={setSetupImportOpen}
          />
        ) : mode === 'dna' ? (
          <EmailDnaQuiz setup={setup} setSetup={setSetup} generateExamples={generateExamples} />
        ) : (
        <div className="rp-vault">
          {emails.map((e, i) => (
            <MailCard
              key={i}
              email={e}
              index={i}
              copied={copiedIdx === i}
              highlight={highlightId != null && e.id === highlightId}
              onCopy={() => copy(i)}
              onDelete={() => setConfirmDeleteIdx(i)}
              onChange={(key, val) => update(i, key, val)}
              author={reviewerName}
              onAuthorChange={onAuthorChange}
            />
          ))}
          <button type="button" className="rp-vault-add" onClick={add}>
            <span className="rp-vault-add-icon">+</span>
            E-Mail hinzufügen
          </button>
        </div>
        )}
      </section>

      {confirmDeleteEmail && (
        <div
          className="rp-addview-overlay"
          onClick={() => setConfirmDeleteIdx(null)}
          role="presentation"
        >
          <div
            className="rp-addview-modal rp-confirm-modal"
            onClick={(ev) => ev.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="rp-confirm-del-title"
          >
            <button
              type="button"
              className="rp-addview-modal-close"
              onClick={() => setConfirmDeleteIdx(null)}
              aria-label="Schließen"
            >
              ×
            </button>
            <div className="rp-addview-modal-head">
              <span className="rp-addview-modal-eyebrow">E-Mail-Konzept löschen</span>
              <h3 className="rp-addview-modal-title" id="rp-confirm-del-title">Bist du sicher?</h3>
              <p className="rp-addview-modal-sub">
                {confirmDeleteEmail.subject
                  ? `„${confirmDeleteEmail.subject}" wird dauerhaft aus dem E-Mail-Vault entfernt. Das lässt sich nicht rückgängig machen.`
                  : 'Dieses E-Mail-Konzept wird dauerhaft aus dem E-Mail-Vault entfernt. Das lässt sich nicht rückgängig machen.'}
              </p>
            </div>
            <div className="rp-confirm-actions">
              <button
                type="button"
                className="rp-confirm-cancel"
                onClick={() => setConfirmDeleteIdx(null)}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className="rp-confirm-delete"
                onClick={() => {
                  remove(confirmDeleteIdx);
                  setConfirmDeleteIdx(null);
                }}
              >
                Endgültig löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Zielgruppen-Tiefenrecherche (eigener Tab) ──
// Eigene, separat von der KI erzeugte Tiefenrecherche (report.research.audienceResearch):
// welche Branchen/Zielgruppen lassen sich per Cold Outreach erreichen, aufgeteilt in
// Direktkunden (B2B) und Partner/Multiplikatoren, je mit konkreten Fundorten, plus eine
// Liste real benannter Datenquellen/Plattformen. Voll editierbar und als Text exportierbar.

const POTENTIAL_TONE = { hoch: 'high', mittel: 'mid', niedrig: 'low' };
const POTENTIAL_ORDER = ['hoch', 'mittel', 'niedrig'];

// report.research.audienceResearch -> editierbarer State (robust gegen Altbestand/Demo).
// Normalisiert die Briefing-Hinweise zu einer Liste aus { title, text }-Objekten.
// Neu: KI liefert Objekte mit Titel + Text. Alt: reine Strings (ein Hinweis pro
// Zeile) oder ein langer Fliesstext werden tolerant in Text-Punkte gewandelt.
const collapse = (v) => String(v || '').replace(/\s*\n\s*/g, ' ').trim();
const toBriefingList = (v) => {
  const fromString = (line) => {
    const s = collapse(String(line || '').replace(/^\s*[•\-*\u2013\u2014]\s*/, ''));
    return s ? { title: '', text: s } : null;
  };
  let items = [];
  if (Array.isArray(v)) {
    items = v
      .map((x) => {
        if (x && typeof x === 'object' && !Array.isArray(x)) {
          const title = collapse(x.title);
          const text = collapse(x.text != null ? x.text : x.note);
          return title || text ? { title, text } : null;
        }
        return fromString(x);
      })
      .filter(Boolean);
  } else if (typeof v === 'string') {
    items = v.split(/\r?\n+/).map(fromString).filter(Boolean);
    // Altbestand: alles in EINEM langen Block -> in Sätze auftrennen (Lesbarkeit).
    if (items.length <= 1 && items[0] && items[0].text.length > 180) {
      const sentences = items[0].text
        .split(/(?<=[.!?])\s+(?=[A-ZÄÖÜ0-9])/)
        .map((x) => x.trim())
        .filter(Boolean);
      if (sentences.length > 1) items = sentences.map((s) => ({ title: '', text: s }));
    }
  }
  return items;
};

const buildAudienceState = (ar = {}) => {
  const a = ar && typeof ar === 'object' ? ar : {};
  // Stichpunkte sind einzeilig: eingebettete/umschließende Zeilenumbrüche (aus der
  // KI-Generierung) zu einem Leerzeichen kollabieren, sonst entstehen leere Zeilen.
  const toList = (v) => (Array.isArray(v)
    ? v.map((x) => String(x || '').replace(/\s*\n\s*/g, ' ').trim()).filter(Boolean)
    : []);
  const segments = (Array.isArray(a.segments) ? a.segments : []).map((s) => ({
    name: String((s && s.name) || ''),
    type: (s && s.type) === 'partner' ? 'partner' : 'direct',
    description: String((s && s.description) || ''),
    whyFit: String((s && s.whyFit) || ''),
    companySize: String((s && s.companySize) || ''),
    revenueBand: String((s && s.revenueBand) || ''),
    decisionMakers: toList(s && s.decisionMakers),
    buyingTriggers: toList(s && s.buyingTriggers),
    searchHints: toList(s && s.searchHints),
    sources: (Array.isArray(s && s.sources) ? s.sources : []).map((q) => ({
      name: String((q && q.name) || ''),
      url: String((q && q.url) || ''),
      note: String((q && q.note) || ''),
    })),
    potential: POTENTIAL_ORDER.includes(s && s.potential) ? s.potential : 'mittel',
  }));
  const dataSources = (Array.isArray(a.dataSources) ? a.dataSources : []).map((d) => ({
    name: String((d && d.name) || ''),
    type: String((d && d.type) || ''),
    url: String((d && d.url) || ''),
    note: String((d && d.note) || ''),
    examples: toList(d && d.examples),
  }));
  return {
    summary: String(a.summary || ''),
    outreachNotes: toBriefingList(a.outreachNotes),
    segments,
    dataSources,
  };
};

// Editierbarer State -> reines Report-Objekt (für die Persistenz).
const audienceStateToReport = (st = {}) => {
  const s = st && typeof st === 'object' ? st : {};
  const cl = (v) => (v || '').trim();
  const list = (arr) => (Array.isArray(arr) ? arr.map((x) => cl(x)).filter(Boolean) : []);
  const briefing = (Array.isArray(s.outreachNotes) ? s.outreachNotes : [])
    .map((p) => (p && typeof p === 'object' && !Array.isArray(p)
      ? { title: cl(p.title), text: cl(p.text) }
      : { title: '', text: cl(p) }))
    .filter((p) => p.title || p.text);
  return {
    summary: cl(s.summary),
    outreachNotes: briefing,
    segments: (Array.isArray(s.segments) ? s.segments : [])
      .map((seg) => ({
        name: cl(seg.name),
        type: seg.type === 'partner' ? 'partner' : 'direct',
        description: cl(seg.description),
        whyFit: cl(seg.whyFit),
        companySize: cl(seg.companySize),
        revenueBand: cl(seg.revenueBand),
        decisionMakers: list(seg.decisionMakers),
        buyingTriggers: list(seg.buyingTriggers),
        searchHints: list(seg.searchHints),
        sources: (Array.isArray(seg.sources) ? seg.sources : [])
          .map((q) => ({ name: cl(q.name), url: cl(q.url), note: cl(q.note) }))
          .filter((q) => q.name || q.url || q.note),
        potential: POTENTIAL_ORDER.includes(seg.potential) ? seg.potential : 'mittel',
      }))
      .filter((seg) => seg.name || seg.description),
    dataSources: (Array.isArray(s.dataSources) ? s.dataSources : [])
      .map((d) => ({ name: cl(d.name), type: cl(d.type), url: cl(d.url), note: cl(d.note), examples: list(d.examples) }))
      .filter((d) => d.name || d.note),
  };
};

// Leichtgewichtiger Chip-Editor für reine String-Listen (add / remove).
const ChipInput = ({ items, onChange, placeholder }) => {
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const inputRef = useRef(null);
  const arr = Array.isArray(items) ? items : [];
  const remove = (i) => onChange(arr.filter((_, idx) => idx !== i));
  const commit = () => {
    const t = draft.trim();
    if (!t) { setAdding(false); return; }
    if (!arr.some((x) => x.toLowerCase() === t.toLowerCase())) onChange([...arr, t]);
    setDraft('');
    if (inputRef.current) inputRef.current.focus();
  };
  return (
    <div className="rp-aud-chips">
      {arr.map((it, i) => (
        <span key={i} className="rp-aud-chip">
          <span className="rp-aud-chip-label">{it}</span>
          <button type="button" className="rp-aud-chip-x" onClick={() => remove(i)} title="Entfernen" aria-label="Entfernen">
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </span>
      ))}
      {adding ? (
        <span className="rp-aud-chip-input">
          <input
            ref={inputRef}
            type="text"
            value={draft}
            placeholder={placeholder}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commit(); }
              if (e.key === 'Escape') { setDraft(''); setAdding(false); }
            }}
            onBlur={() => { if (!draft.trim()) setAdding(false); }}
          />
        </span>
      ) : (
        <button type="button" className="rp-aud-chip-add" onClick={() => setAdding(true)} title={placeholder} aria-label={placeholder}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
        </button>
      )}
    </div>
  );
};

// Editor für die konkreten Fundorte eines Segments (name + url + note).
const SourceEditor = ({ items, onChange }) => {
  const arr = Array.isArray(items) ? items : [];
  const update = (i, patch) => onChange(arr.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const remove = (i) => onChange(arr.filter((_, idx) => idx !== i));
  const add = () => onChange([...arr, { name: '', url: '', note: '' }]);
  return (
    <div className="rp-aud-sources">
      {arr.map((q, i) => (
        <div key={i} className="rp-aud-source">
          <div className="rp-aud-source-head">
            <input
              className="rp-aud-source-name"
              value={q.name || ''}
              placeholder="Name der Plattform / des Verzeichnisses…"
              onChange={(e) => update(i, { name: e.target.value })}
            />
            <button type="button" className="rp-aud-source-del" onClick={() => remove(i)} title="Entfernen" aria-label="Entfernen">×</button>
          </div>
          <input
            className="rp-aud-source-url"
            value={q.url || ''}
            placeholder="https://… (optional)"
            onChange={(e) => update(i, { url: e.target.value })}
          />
          <AutoGrowTextarea
            className="rp-aud-source-note"
            value={q.note || ''}
            placeholder="Was findet man dort, wie filtern?…"
            onChange={(e) => update(i, { note: e.target.value })}
          />
        </div>
      ))}
      <button type="button" className="rp-aud-mini-add" onClick={add}>
        <span>+</span> Fundort ergänzen
      </button>
    </div>
  );
};

// Eine große, edle Segment-Karte für die Galerie. In der Vorschau bereits
// teilbearbeitbar (Name & Beschreibung inline), und beim Aufklappen erscheinen
// dynamisch die weiteren Details direkt in der Karte – kein separates Formular.
const AudienceCard = ({ segment, num, expanded, onToggle, onChange, onRemove }) => {
  const s = segment;
  const set = (patch) => onChange({ ...s, ...patch });
  const tone = POTENTIAL_TONE[s.potential] || 'mid';
  const meta = [s.companySize, s.revenueBand].filter((x) => x && x.trim());
  // Nur die blanken Webseiten der Fundorte (ohne Titel), max. 5.
  const cleanUrl = (u) => String(u || '').trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  const sites = s.sources.map((q) => cleanUrl(q && q.url)).filter(Boolean).slice(0, 5);
  return (
    <div className={`rp-aud-card rp-pot-${tone} ${expanded ? 'is-expanded' : ''}`}>
      <span className="rp-aud-card-glow" />

      <div className="rp-aud-card-top">
        <span className="rp-aud-card-num">{String(num).padStart(2, '0')}</span>
      </div>

      <AutoGrowTextarea
        className="rp-aud-card-name-edit"
        value={s.name}
        placeholder="Segment / Nische benennen…"
        onChange={(e) => set({ name: e.target.value })}
      />
      <AutoGrowTextarea
        className="rp-aud-card-desc-edit"
        value={s.description}
        placeholder="Wer ist das konkret, was machen die Firmen?…"
        onChange={(e) => set({ description: e.target.value })}
      />

      {!expanded && (meta.length > 0 || s.decisionMakers.length > 0 || sites.length > 0) && (
        <div className="rp-aud-card-preview">
          {meta.length > 0 && (
            <div className="rp-aud-card-tags">
              {meta.map((m, i) => <span key={`m${i}`} className="rp-aud-card-tag">{m}</span>)}
            </div>
          )}
          {s.decisionMakers.length > 0 && (
            <div className="rp-aud-prevblock">
              <span className="rp-aud-prevlabel">Gesuchte Entscheider</span>
              <div className="rp-aud-card-tags">
                {s.decisionMakers.slice(0, 4).map((d, i) => <span key={`d${i}`} className="rp-aud-card-tag is-soft">{d}</span>)}
                {s.decisionMakers.length > 4 && <span className="rp-aud-card-tag is-muted">+{s.decisionMakers.length - 4}</span>}
              </div>
            </div>
          )}
          {sites.length > 0 && (
            <div className="rp-aud-prevblock">
              <span className="rp-aud-prevlabel">Webseiten</span>
              <div className="rp-aud-sites">
                {sites.map((u, i) => <span key={`s${i}`} className="rp-aud-site">{u}</span>)}
              </div>
            </div>
          )}
        </div>
      )}

      <div className={`rp-aud-card-more ${expanded ? 'is-open' : ''}`}>
        <div className="rp-aud-card-more-inner">
          <div className="rp-aud-card-controls">
            <div className="rp-aud-seg2">
              <button type="button" className={`rp-aud-seg2-btn ${s.type === 'direct' ? 'is-on' : ''}`} onClick={() => set({ type: 'direct' })}>Direktkunde</button>
              <button type="button" className={`rp-aud-seg2-btn ${s.type === 'partner' ? 'is-on' : ''}`} onClick={() => set({ type: 'partner' })}>Partner</button>
            </div>
            <div className="rp-aud-seg2">
              {POTENTIAL_ORDER.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`rp-aud-seg2-btn rp-pot-${POTENTIAL_TONE[p]} ${s.potential === p ? 'is-on rp-pot-on' : ''}`}
                  onClick={() => set({ potential: p })}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="rp-aud-card-grid">
            <div className="rp-aud-meta-cell">
              <label className="rp-aud-field-label">Unternehmensgröße</label>
              <input className="rp-aud-input" value={s.companySize} placeholder="z. B. 10–200 Mitarbeiter" onChange={(e) => set({ companySize: e.target.value })} />
            </div>
            <div className="rp-aud-meta-cell">
              <label className="rp-aud-field-label">Umsatzgröße</label>
              <input className="rp-aud-input" value={s.revenueBand} placeholder="z. B. 2–20 Mio. €" onChange={(e) => set({ revenueBand: e.target.value })} />
            </div>
          </div>

          <label className="rp-aud-field-label">Warum kaufbereit / Bedarf</label>
          <AutoGrowTextarea className="rp-aud-text" value={s.whyFit} placeholder="Warum braucht/kauft diese Zielgruppe das Produkt?…" onChange={(e) => set({ whyFit: e.target.value })} />

          <label className="rp-aud-field-label">Entscheider</label>
          <ChipInput items={s.decisionMakers} onChange={(v) => set({ decisionMakers: v })} placeholder="Rolle hinzufügen…" />

          <label className="rp-aud-field-label">Kauf-Trigger</label>
          <ChipInput items={s.buyingTriggers} onChange={(v) => set({ buyingTriggers: v })} placeholder="Auslöser hinzufügen…" />

          <label className="rp-aud-field-label">Such-Hinweise &amp; Filter</label>
          <ChipInput items={s.searchHints} onChange={(v) => set({ searchHints: v })} placeholder="Keyword / Filter hinzufügen…" />

          <label className="rp-aud-field-label">Konkrete Fundorte</label>
          <SourceEditor items={s.sources} onChange={(v) => set({ sources: v })} />
        </div>
      </div>

      <div className="rp-aud-card-foot">
        <button type="button" className="rp-aud-card-toggle" onClick={onToggle} aria-expanded={expanded}>
          {expanded ? 'weniger' : 'mehr & bearbeiten'}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="rp-aud-card-chev" style={{ transform: expanded ? 'rotate(180deg)' : 'none' }}>
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        {expanded && (
          <button type="button" className="rp-aud-card-del" onClick={onRemove} title="Segment entfernen" aria-label="Segment entfernen">Entfernen</button>
        )}
      </div>
    </div>
  );
};

// Horizontale Galerie mit Slide-Navigation (Pfeile + nativer Touch-/Trackpad-Scroll).
const AudienceGallery = ({ children }) => {
  const scrollerRef = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const updateEdges = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setEdges({ left: el.scrollLeft > 4, right: el.scrollLeft < max - 4 });
  }, []);

  useEffect(() => {
    updateEdges();
    const el = scrollerRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', updateEdges, { passive: true });
    window.addEventListener('resize', updateEdges);
    return () => {
      el.removeEventListener('scroll', updateEdges);
      window.removeEventListener('resize', updateEdges);
    };
  }, [updateEdges, children]);

  const slide = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    // Eine Karte pro Klick weiterschieben (≈ halbe Galeriebreite, da 2 sichtbar).
    const firstCard = el.querySelector('.rp-aud-card, .rp-aud-add-tile');
    const step = firstCard ? firstCard.getBoundingClientRect().width + 16 : el.clientWidth / 2;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <div className={`rp-aud-gallery-wrap ${edges.left ? 'has-left' : ''} ${edges.right ? 'has-right' : ''}`}>
      <button type="button" className="rp-aud-nav rp-aud-nav-l" onClick={() => slide(-1)} aria-label="Zurück" tabIndex={edges.left ? 0 : -1}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
      </button>
      <div className="rp-aud-gallery" ref={scrollerRef}>
        {children}
      </div>
      <button type="button" className="rp-aud-nav rp-aud-nav-r" onClick={() => slide(1)} aria-label="Weiter" tabIndex={edges.right ? 0 : -1}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
      </button>
    </div>
  );
};

// Editierbare Bulletpoint-Liste für das Briefing – übersichtlich, je Punkt eine
// eigene Zeile mit Marker, großzügigem Abstand und Add/Remove.
const BriefingEditor = ({ points, onChange }) => {
  // Punkte sind { title, text }; Alt-Strings werden tolerant gewandelt.
  const arr = (Array.isArray(points) ? points : []).map((p) => (
    p && typeof p === 'object' && !Array.isArray(p)
      ? { title: String(p.title || ''), text: String(p.text || '') }
      : { title: '', text: String(p || '') }
  ));
  const update = (i, patch) => onChange(arr.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const remove = (i) => onChange(arr.filter((_, idx) => idx !== i));
  const add = () => onChange([...arr, { title: '', text: '' }]);
  return (
    <div className="rp-aud-brief">
      <ol className="rp-aud-brief-list">
        {arr.map((p, i) => (
          <li key={i} className="rp-aud-brief-item">
            <span className="rp-aud-brief-marker" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
            <div className="rp-aud-brief-body">
              <input
                className="rp-aud-brief-title"
                value={p.title}
                placeholder="Titel des Hinweises…"
                onChange={(e) => update(i, { title: e.target.value })}
              />
              <AutoGrowTextarea
                className="rp-aud-brief-text"
                value={p.text}
                placeholder="Konkreter, umsetzbarer Hinweis für die Datenbeschaffung…"
                onChange={(e) => update(i, { text: e.target.value })}
              />
            </div>
            <button type="button" className="rp-aud-brief-del" onClick={() => remove(i)} title="Punkt entfernen" aria-label="Punkt entfernen">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </li>
        ))}
      </ol>
      <button type="button" className="rp-aud-mini-add rp-aud-brief-add" onClick={add}>
        <span>+</span> Hinweis ergänzen
      </button>
    </div>
  );
};

// Eine Gruppe (Direktkunden ODER Partner): Galerie aus großen, inline editierbaren
// Karten. Aufklappen passiert direkt in der jeweiligen Karte (kein Drawer mehr).
const AudienceSegmentGroup = ({ group, openIdx, onToggle, onUpdate, onRemove, onAdd, addLabel }) => (
  <div className="rp-aud-group">
    <AudienceGallery>
      {group.map(({ s, i }, n) => (
        <AudienceCard
          key={i}
          segment={s}
          num={n + 1}
          expanded={openIdx === i}
          onToggle={() => onToggle(i)}
          onChange={(next) => onUpdate(i, next)}
          onRemove={() => onRemove(i)}
        />
      ))}
      <button type="button" className="rp-aud-add-tile" onClick={onAdd}>
        <span className="rp-aud-add-tile-icon">+</span>
        <span className="rp-aud-add-tile-label">{addLabel}</span>
      </button>
    </AudienceGallery>
  </div>
);

// Eine Datenquellen-Kachel: elegante, schlanke Vorschau (Name · Kategorie · blanke URL),
// Details (Nutzung, Beispiele, Felder) erscheinen beim Ausklappen.
const DataSourceCard = ({ source, num, onChange, onRemove }) => {
  const d = source;
  const [expanded, setExpanded] = useState(false);
  const set = (patch) => onChange({ ...d, ...patch });
  const cleanUrl = (u) => String(u || '').trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  const hrefUrl = (u) => {
    const t = String(u || '').trim();
    if (!t) return '';
    return /^https?:\/\//i.test(t) ? t : `https://${t}`;
  };
  const toggle = () => setExpanded((e) => !e);
  return (
    <div className={`rp-aud-src ${expanded ? 'is-expanded' : ''}`}>
      {/* Klick auf die Leiste klappt aus; Klick auf die URL öffnet den Link in
          neuem Tab (kein Toggle). Daher div statt button, damit der echte
          Link nicht ungültig im Button verschachtelt ist. */}
      <div
        className="rp-aud-src-bar"
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
        aria-expanded={expanded}
      >
        <span className="rp-aud-src-num">{String(num).padStart(2, '0')}</span>
        <span className="rp-aud-src-info">
          <span className={`rp-aud-src-title ${d.name ? '' : 'is-placeholder'}`}>{d.name || 'Unbenannte Quelle'}</span>
          <span className="rp-aud-src-sub">
            {d.type && <span className="rp-aud-src-type-chip">{d.type}</span>}
            {d.url && (
              <a
                className="rp-aud-src-url-bare rp-aud-src-url-link"
                href={hrefUrl(d.url)}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                title={`${cleanUrl(d.url)} in neuem Tab öffnen`}
              >
                {cleanUrl(d.url)}
              </a>
            )}
          </span>
          {/* Nutzung direkt unter dem Tag – auch im zugeklappten Zustand sichtbar
              und editierbar. stopPropagation verhindert das Auf-/Zuklappen beim Tippen. */}
          <AutoGrowTextarea
            className="rp-aud-src-note"
            value={d.note}
            placeholder="Wie nutzt man die Quelle konkret für diese Zielgruppe?…"
            onChange={(e) => set({ note: e.target.value })}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </span>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="rp-aud-src-chev" style={{ transform: expanded ? 'rotate(180deg)' : 'none' }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>

      <div className={`rp-aud-card-more ${expanded ? 'is-open' : ''}`}>
        <div className="rp-aud-card-more-inner">
          <label className="rp-aud-field-label">Name der Quelle</label>
          <input className="rp-aud-input" value={d.name} placeholder="Name der Quelle…" onChange={(e) => set({ name: e.target.value })} />

          <div className="rp-aud-card-grid">
            <div className="rp-aud-meta-cell">
              <label className="rp-aud-field-label">Kategorie</label>
              <input className="rp-aud-input" value={d.type} placeholder="z. B. Branchenverzeichnis" onChange={(e) => set({ type: e.target.value })} />
            </div>
            <div className="rp-aud-meta-cell">
              <label className="rp-aud-field-label">URL</label>
              <input className="rp-aud-input" value={d.url} placeholder="https://… (optional)" onChange={(e) => set({ url: e.target.value })} />
            </div>
          </div>

          <label className="rp-aud-field-label">Beispiele</label>
          <ChipInput items={d.examples} onChange={(v) => set({ examples: v })} placeholder="Beispiel / Suchbegriff hinzufügen…" />

          <div className="rp-aud-src-foot">
            <button type="button" className="rp-aud-card-del" onClick={onRemove} title="Quelle entfernen" aria-label="Quelle entfernen">Entfernen</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Baut den teilbaren Text-Export der gesamten Zielgruppen-Recherche.
const buildAudienceExportText = (report, st) => {
  const a = audienceStateToReport(st);
  const lines = [];
  const hr = '────────────────────────────────────────';
  const company = (report.company && report.company.name) || 'Unternehmen';
  const website = (report.company && report.company.website) || '';
  lines.push('ZIELGRUPPEN-RECHERCHE FÜR COLD OUTREACH');
  lines.push(company + (website ? `  (${website})` : ''));
  lines.push(`Erstellt: ${new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}`);
  lines.push('');
  lines.push('Diese Recherche dient als Briefing für die Datenbeschaffung: welche Firmen und');
  lines.push('Entscheider gesucht werden und über welche Quellen sie online zu finden sind.');
  lines.push('');

  if (a.summary) {
    lines.push(hr);
    lines.push('ÜBERBLICK');
    lines.push(hr);
    lines.push(a.summary.replace(/\*\*/g, ''));
    lines.push('');
  }

  const renderSeg = (seg, idx) => {
    lines.push(`${idx}. ${seg.name}   [Potenzial: ${seg.potential}]`);
    if (seg.description) lines.push(`   Beschreibung: ${seg.description}`);
    if (seg.whyFit) lines.push(`   Warum kaufbereit: ${seg.whyFit}`);
    if (seg.companySize) lines.push(`   Unternehmensgröße: ${seg.companySize}`);
    if (seg.revenueBand) lines.push(`   Umsatzgröße: ${seg.revenueBand}`);
    if (seg.decisionMakers.length) lines.push(`   Entscheider: ${seg.decisionMakers.join(', ')}`);
    if (seg.buyingTriggers.length) lines.push(`   Kauf-Trigger: ${seg.buyingTriggers.join('; ')}`);
    if (seg.searchHints.length) lines.push(`   Such-Hinweise: ${seg.searchHints.join('; ')}`);
    if (seg.sources.length) {
      lines.push('   Fundorte:');
      seg.sources.forEach((q) => {
        const u = q.url ? ` (${q.url})` : '';
        const n = q.note ? ` – ${q.note}` : '';
        lines.push(`     • ${q.name}${u}${n}`);
      });
    }
    lines.push('');
  };

  const direct = a.segments.filter((s) => s.type === 'direct');
  const partner = a.segments.filter((s) => s.type === 'partner');
  if (direct.length) {
    lines.push(hr);
    lines.push('DIREKTKUNDEN (B2B)');
    lines.push(hr);
    direct.forEach((s, i) => renderSeg(s, i + 1));
  }
  if (partner.length) {
    lines.push(hr);
    lines.push('PARTNER & MULTIPLIKATOREN');
    lines.push(hr);
    partner.forEach((s, i) => renderSeg(s, i + 1));
  }

  if (a.dataSources.length) {
    lines.push(hr);
    lines.push('DATENQUELLEN & PLATTFORMEN');
    lines.push(hr);
    a.dataSources.forEach((d, i) => {
      const t = d.type ? ` [${d.type}]` : '';
      const u = d.url ? `  ${d.url}` : '';
      lines.push(`${i + 1}. ${d.name}${t}${u}`);
      if (d.note) lines.push(`   ${d.note}`);
      if (d.examples.length) lines.push(`   Beispiele: ${d.examples.join('; ')}`);
      lines.push('');
    });
  }

  if (a.outreachNotes.length) {
    lines.push(hr);
    lines.push('HINWEISE FÜR DIE DATENBESCHAFFUNG');
    lines.push(hr);
    a.outreachNotes.forEach((p) => {
      const title = (p && p.title) || '';
      const text = (p && p.text) || '';
      if (title) {
        lines.push(`•  ${title}`);
        if (text) lines.push(`   ${text}`);
      } else if (text) {
        lines.push(`•  ${text}`);
      }
    });
    lines.push('');
  }

  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim() + '\n';
};

// Firmengrößen-Presets für die Zielgruppengrößen-Schätzung (Mehrfachauswahl).
const AUD_SIZE_OPTIONS = [
  { key: 'micro', label: '1–10 Mitarbeiter', desc: 'Kleinst- & Solounternehmen' },
  { key: 'small', label: '11–50 Mitarbeiter', desc: 'Kleine Unternehmen' },
  { key: 'mid', label: '51–200 Mitarbeiter', desc: 'Mittelstand' },
  { key: 'large', label: '201–1.000 Mitarbeiter', desc: 'Gehobener Mittelstand' },
  { key: 'enterprise', label: '1.000+ Mitarbeiter', desc: 'Großunternehmen & Konzerne' },
];

const audIntFmt = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });

// ── Zielgruppengröße schätzen (KI / Opus, auf Knopfdruck) ──
// Fragt vor der Generierung Regionen/Länder (Pflicht) und gewünschte Firmengrößen
// (Mehrfachauswahl) ab, schickt das zusammen mit allen aktuellen Segmenten an die
// Cloud Function und zeigt eine Spanne potenzieller Kunden plus 3 Erweiterungs-Ideen.
const AudienceSizeEstimator = ({ estimateSize, hasSegments }) => {
  const [locations, setLocations] = useState('');
  const [sizes, setSizes] = useState([]);
  const [state, setState] = useState('idle'); // idle | loading | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Nach einem Lauf das alte Ergebnis verwerfen, sobald die Eingrenzung geändert
  // wird – sonst stünde eine Schätzung neben bereits geänderten Eingaben.
  const invalidate = () => setState((s) => (s === 'error' ? 'idle' : s));
  const changeLocations = (v) => { setLocations(v); invalidate(); };
  const toggleSize = (label) => {
    setSizes((prev) => (prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label]));
    invalidate();
  };

  // Schätzung verwerfen -> das Eingabeformular erscheint automatisch wieder
  // (die zuletzt gewählten Eingaben bleiben als Ausgangspunkt erhalten).
  const clearEstimate = () => { setResult(null); setState('idle'); };

  const canRun = estimateSize && hasSegments && locations.trim().length >= 2 && state !== 'loading';

  const run = async () => {
    if (!canRun) return;
    setState('loading');
    setError('');
    try {
      const res = await estimateSize({ locations: locations.trim(), companySizes: sizes });
      setResult(res);
      setState('done');
    } catch (e) {
      setError(e && e.message ? e.message : 'Schätzung fehlgeschlagen.');
      setState('error');
    }
  };

  const est = result && result.estimate ? result.estimate : null;
  const ideas = result && Array.isArray(result.ideas) ? result.ideas : [];
  const rangeLabel = est && (est.label || (est.high ? `ca. ${audIntFmt.format(est.low)} bis ${audIntFmt.format(est.high)}` : ''));

  // Fertige Schätzung: ersetzt das Eingabeformular durch das große Ergebnis,
  // darunter nur ein kleiner Button zum Entfernen (danach kommt das Formular zurück).
  if (state === 'done' && est) {
    return (
      <div className="rp-aud-size">
        <div className="rp-aud-size-result rp-aud-size-result--solo">
          <div className="rp-aud-size-estimate">
            <span className="rp-aud-size-estimate-eyebrow">Geschätzte Zielgruppengröße</span>
            <span className="rp-aud-size-estimate-num">{rangeLabel || '—'}</span>
            {est.reasoning && <p className="rp-aud-size-estimate-why">{est.reasoning}</p>}
          </div>
          {ideas.length > 0 && (
            <div className="rp-aud-size-ideas">
              <span className="rp-aud-size-ideas-head">So lässt sich die Zielgruppe sinnvoll erweitern</span>
              <ul className="rp-aud-size-ideas-list">
                {ideas.map((it, i) => (
                  <li key={i} className="rp-aud-size-idea">
                    {it.title && <span className="rp-aud-size-idea-title">{it.title}</span>}
                    {it.text && <span className="rp-aud-size-idea-text">{it.text}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <span className="rp-aud-size-disclaimer">KI-Schätzung auf Basis der aktuellen Segmente und Eingrenzung – als Größenordnung zu verstehen, keine exakte Zahl.</span>
        </div>
        <button type="button" className="rp-aud-size-clear" onClick={clearEstimate}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
          Schätzung entfernen
        </button>
      </div>
    );
  }

  return (
    <div className="rp-aud-size">
      <div className="rp-aud-size-form">
        <div className="rp-aud-size-field">
          <label className="rp-aud-size-label">In welchen Ländern / Regionen suchen wir?</label>
          <span className="rp-aud-size-hint">Pflicht. Z. B. „Deutschland, Österreich" oder konkrete Städte/Regionen.</span>
          <input
            className="rp-aud-size-input"
            type="text"
            value={locations}
            placeholder="z. B. Deutschland, Österreich, Schweiz (DACH)…"
            onChange={(e) => changeLocations(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); run(); } }}
          />
        </div>

        <div className="rp-aud-size-field">
          <label className="rp-aud-size-label">Wie groß dürfen die Firmen sein?</label>
          <span className="rp-aud-size-hint">Mehrfachauswahl möglich. Ohne Auswahl wird die Größe nicht eingegrenzt.</span>
          <div className="rp-aud-size-opts">
            {AUD_SIZE_OPTIONS.map((o) => {
              const active = sizes.includes(o.label);
              return (
                <button
                  type="button"
                  key={o.key}
                  className={`rp-aud-size-opt ${active ? 'is-active' : ''}`}
                  onClick={() => toggleSize(o.label)}
                  aria-pressed={active}
                >
                  <span className="rp-aud-size-opt-label">{o.label}</span>
                  <span className="rp-aud-size-opt-desc">{o.desc}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="rp-aud-size-actions">
          <button type="button" className="rp-aud-size-run" onClick={run} disabled={!canRun}>
            {state === 'loading' ? (
              <>
                <span className="rp-aud-size-spinner" aria-hidden="true" />
                Schätzung läuft…
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 3v4M3 5h4M6 17v4M4 19h4M13 3l2.5 6.5L22 12l-6.5 2.5L13 21l-2.5-6.5L4 12l6.5-2.5L13 3z" /></svg>
                Zielgruppengröße berechnen
              </>
            )}
          </button>
          {!hasSegments && (
            <span className="rp-aud-size-warn">Lege zuerst mindestens ein Segment an, damit die KI etwas schätzen kann.</span>
          )}
          {!estimateSize && hasSegments && (
            <span className="rp-aud-size-warn">Im Demo-Modus ist die KI-Schätzung nicht verfügbar.</span>
          )}
        </div>

        {state === 'error' && <div className="rp-aud-size-error">{error}</div>}
      </div>
    </div>
  );
};

// ── Ansicht: Zielgruppen-Tiefenrecherche ──
// Read-only Panel: die KI-Kunden-Psychologie (Market Research / Pains der Zielkunden).
// Quelle ist report.research.customerPsychology (separater Opus-Call bei der Generierung).
// Bewusst nur lesend: regeneriert wird ueber den Report-Lauf, nicht hier editiert.
const CustomerPsychologyPanel = ({ report }) => {
  const cp = (report && report.research && report.research.customerPsychology) || {};
  const fields = [
    { key: 'biggestProblem', label: 'Größtes Problem', tone: 'pain' },
    { key: 'urgentCrisis', label: 'Akute Krise (brennt jetzt)', tone: 'pain' },
    { key: 'topFrustration', label: 'Größte Frustration', tone: 'pain' },
    { key: 'keepsAwake', label: 'Was sie nachts wachhält', tone: 'pain' },
    { key: 'humiliation', label: 'Will vermieden werden', tone: 'pain' },
    { key: 'costOfInaction', label: 'Kosten des Nichtstuns', tone: 'pain' },
    { key: 'biggestMistake', label: 'Größter aktueller Fehler', tone: 'pain' },
    { key: 'complaints', label: 'Worüber sie klagen', tone: 'pain' },
    { key: 'biggestResult', label: 'Größtes gewünschtes Ergebnis', tone: 'gain' },
    { key: 'highLevelFix', label: 'Lösungsweg (high level)', tone: 'gain' },
  ];
  const texts = fields.filter((f) => (cp[f.key] || '').trim());
  const successSteps = Array.isArray(cp.successSteps) ? cp.successSteps.filter(Boolean) : [];
  const daily = Array.isArray(cp.dailyFrustrations) ? cp.dailyFrustrations.filter(Boolean) : [];
  const hasAny = (cp.persona || '').trim() || (cp.summary || '').trim() || texts.length || successSteps.length || daily.length;
  if (!hasAny) return null;

  return (
    <section className="rp-block">
      <SectionHead
        eyebrow="Kunden-Psychologie"
        title="Was die Zielkunden wirklich bewegt"
        sub="KI-Market-Research zu den tiefen Pains, Ängsten und Wünschen der idealen Zielkunden. Fundament für unwiderstehliche Einstiegsangebote und treffsichere Cold-Outreach-Angles."
      />
      <div className="rp-psy">
        {(cp.persona || '').trim() && (
          <div className="rp-psy-persona">
            <span className="rp-psy-persona-label">Profil von</span>
            <span className="rp-psy-persona-value">{cp.persona}</span>
          </div>
        )}
        {(cp.summary || '').trim() && <p className="rp-psy-summary">{cp.summary}</p>}

        {(successSteps.length > 0 || daily.length > 0) && (
          <div className="rp-psy-lists">
            {successSteps.length > 0 && (
              <div className="rp-psy-list">
                <span className="rp-psy-list-label">Schritte zum Erfolg</span>
                <ol className="rp-psy-steps">
                  {successSteps.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              </div>
            )}
            {daily.length > 0 && (
              <div className="rp-psy-list">
                <span className="rp-psy-list-label">Tägliche Frustrationen</span>
                <ul className="rp-psy-daily">
                  {daily.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="rp-psy-grid">
          {texts.map((f) => (
            <div key={f.key} className={`rp-psy-card rp-psy-${f.tone}`}>
              <span className="rp-psy-card-label">{f.label}</span>
              <p>{cp[f.key]}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AudienceView = ({ audience, setAudience, report, estimateSize }) => {
  const [copied, setCopied] = useState(false);
  // Index des aktuell geöffneten Segments (null = alle Karten kompakt).
  const [openIdx, setOpenIdx] = useState(null);
  // Vollbild-Modus für das Zielgruppengrößen-Tool (blendet die restliche Seite aus).
  const [sizeMode, setSizeMode] = useState(false);

  const segments = Array.isArray(audience.segments) ? audience.segments : [];
  const dataSources = Array.isArray(audience.dataSources) ? audience.dataSources : [];

  const updateSegment = (globalIdx, next) =>
    setAudience((a) => ({ ...a, segments: a.segments.map((s, i) => (i === globalIdx ? next : s)) }));
  const removeSegment = (globalIdx) => {
    setAudience((a) => ({ ...a, segments: a.segments.filter((_, i) => i !== globalIdx) }));
    setOpenIdx(null);
  };
  const addSegment = (type) => {
    const newIdx = segments.length;
    setAudience((a) => ({
      ...a,
      segments: [...a.segments, { name: '', type, description: '', whyFit: '', companySize: '', revenueBand: '', decisionMakers: [], buyingTriggers: [], searchHints: [], sources: [], potential: 'mittel' }],
    }));
    setOpenIdx(newIdx);
  };
  const toggleOpen = (globalIdx) => setOpenIdx((cur) => (cur === globalIdx ? null : globalIdx));

  const updateSource = (i, next) =>
    setAudience((a) => ({ ...a, dataSources: a.dataSources.map((d, idx) => (idx === i ? next : d)) }));
  const removeSource = (i) =>
    setAudience((a) => ({ ...a, dataSources: a.dataSources.filter((_, idx) => idx !== i) }));
  const addSource = () =>
    setAudience((a) => ({ ...a, dataSources: [...a.dataSources, { name: '', type: '', url: '', note: '', examples: [] }] }));

  // Segmente nach Typ gruppieren, dabei den Original-Index für Updates behalten.
  const indexed = segments.map((s, i) => ({ s, i }));
  const directSegs = indexed.filter((x) => x.s.type === 'direct');
  const partnerSegs = indexed.filter((x) => x.s.type === 'partner');

  const outreachNotes = Array.isArray(audience.outreachNotes) ? audience.outreachNotes : [];
  const isEmpty = segments.length === 0 && dataSources.length === 0 && !audience.summary.trim() && outreachNotes.length === 0;

  const copyText = async () => {
    const text = buildAudienceExportText(report, audience);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) { /* clipboard nicht verfügbar */ }
  };

  // Vollbild-Modus: nur das Zielgruppengrößen-Tool, der Rest der Seite ist ausgeblendet.
  if (sizeMode) {
    return (
      <div className="rp-tab rp-tab--audience">
        <section className="rp-block rp-aud-intro">
          <div className="rp-aud-overview-card">
            <div className="rp-aud-overview-head">
              <span className="rp-aud-overview-label">Zielgruppengröße</span>
              <button type="button" className="rp-aud-export-btn" onClick={() => setSizeMode(false)} title="Zurück zur Zielgruppen-Recherche">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><path d="M12 19l-7-7 7-7" /></svg>
                Zurück zur Recherche
              </button>
            </div>
          </div>
        </section>

        <section className="rp-block">
          <SectionHead eyebrow="Zielgruppengröße" title="Wie groß ist diese Zielgruppe?" sub="Die SalesGod KI schätzt anhand aller definierten Segmente, wie viele potenzielle Kunden es ungefähr gibt. Vorher grenzt du Region und Firmengröße ein." />
          <AudienceSizeEstimator estimateSize={estimateSize} hasSegments={segments.length > 0} />
        </section>
      </div>
    );
  }

  return (
    <div className="rp-tab rp-tab--audience">
      <section className="rp-block rp-aud-intro">
        <div className="rp-aud-overview-card">
          <div className="rp-aud-overview-head">
            <span className="rp-aud-overview-label">Auf einen Blick</span>
            <div className="rp-aud-export">
              <button type="button" className={`rp-aud-export-btn rp-aud-export-icon ${copied ? 'is-done' : ''}`} onClick={copyText} title={copied ? 'Kopiert' : 'Gesamte Seite kopieren'} aria-label="Gesamte Seite kopieren">
                {copied ? (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                ) : (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>
                )}
              </button>
              <button type="button" className="rp-aud-export-btn rp-aud-size-cta" onClick={() => setSizeMode(true)} title="Zielgruppengröße mit der SalesGod KI berechnen">
                <svg className="rp-aud-size-cta-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" />
                  <circle cx="12" cy="12" r="5" />
                  <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
                </svg>
                Zielgruppengröße berechnen
              </button>
            </div>
          </div>

          {isEmpty && (
            <div className="rp-aud-empty">
              Für diese Seite wurde noch keine Zielgruppen-Tiefenrecherche generiert. Du kannst die Segmente und Quellen hier manuell anlegen.
            </div>
          )}

          <div className="rp-aud-overview">
            <MarkableText
              className="rp-aud-text rp-aud-summary"
              value={audience.summary}
              placeholder="Gesamtbild der erreichbaren Zielgruppen… (Satz markieren und mit Cmd/Strg+B fett hervorheben)"
              onChange={(e) => setAudience((a) => ({ ...a, summary: e.target.value }))}
            />
          </div>
        </div>
      </section>

      <CustomerPsychologyPanel report={report} />

      <section className="rp-block">
        <SectionHead eyebrow="Direktkunden" title="Neukunden im B2B-Segment" sub="Unternehmen oder Personen, die das Produkt für ihr Business brauchen, kaufen oder dazu verpflichtet sind. Karte antippen zum Bearbeiten." />
        <AudienceSegmentGroup
          group={directSegs}
          type="direct"
          openIdx={openIdx}
          onToggle={toggleOpen}
          onUpdate={updateSegment}
          onRemove={removeSegment}
          onAdd={() => addSegment('direct')}
          addLabel="Direktkunden-Segment hinzufügen"
        />
      </section>

      <section className="rp-block">
        <SectionHead eyebrow="Partner & Multiplikatoren" title="Partnerschaften als Umsatz-Multiplikator" sub="Akteure mit Zugang zu vielen Zielkunden: Wiederverkäufer, Integratoren, Verbände, Berater, Plattformen. Karte antippen zum Bearbeiten." />
        <AudienceSegmentGroup
          group={partnerSegs}
          type="partner"
          openIdx={openIdx}
          onToggle={toggleOpen}
          onUpdate={updateSegment}
          onRemove={removeSegment}
          onAdd={() => addSegment('partner')}
          addLabel="Partner-Segment hinzufügen"
        />
      </section>

      <section className="rp-block">
        <SectionHead eyebrow="Datenquellen & Plattformen" title="Öffentliche Datenquellen" sub="Real benannte Verzeichnisse, Netzwerke und Tools mit Beispielen, wie der Datenspezialist sie nutzt." />
        <div className="rp-aud-srclist">
          {dataSources.map((d, i) => (
            <DataSourceCard key={i} source={d} num={i + 1} onChange={(next) => updateSource(i, next)} onRemove={() => removeSource(i)} />
          ))}
          <button type="button" className="rp-aud-add" onClick={addSource}>
            <span className="rp-aud-add-icon">+</span> Datenquelle hinzufügen
          </button>
        </div>
      </section>

      <section className="rp-block">
        <SectionHead eyebrow="Briefing" title="Weitere Hinweise" sub="Worauf der Datenspezialist achten sollte: gute Filter, Qualitätskriterien, was eine Firma zum idealen Ziel macht. Jeder Punkt eigene Zeile." />
        <BriefingEditor
          points={outreachNotes}
          onChange={(next) => setAudience((a) => ({ ...a, outreachNotes: next }))}
        />
      </section>
    </div>
  );
};

const BASE_TABS = [
  { id: 'research', label: 'Research', desc: 'Markt' },
  { id: 'audience', label: 'Zielgruppen', desc: 'Wen & wo erreichen?' },
  // Kundenmagnet ("Magnetic Treasury"): fester Platz direkt unter Zielgruppen und
  // über dem (zuschaltbaren) Account Setup. Inhalt ist gesperrt, bis das Zusatzpaket
  // über das Team Dashboard freigeschaltet wird (für den Kunden zeigt sich sonst der
  // edle Locked-Screen).
  { id: 'leadmagnet', label: 'Kundenmagnet', desc: 'Lead-Magnet Treasury' },
];

// Impact ist nicht mehr Teil der Haupt-Navigation, sondern wird über den
// „Impact & Wert berechnen"-Button in der Research-Ansicht geöffnet.
const IMPACT_TAB = { id: 'impact', label: 'Impact', desc: 'Lohnt sich das?' };

// Analyse-Tab: einzige freigeschaltete Ansicht im Research-Preview (Locked-Modus).
// Im Locked-Modus ist dies der einzige sichtbare Tab; alles andere ist gesperrt.
const ANALYSE_TAB = { id: 'analyse', label: 'Analyse', desc: 'Analyse-Übersicht' };

// Gesperrte Vorschau-Ansicht (Research/Zielgruppen im Research-Preview). Zeigt ein
// goldenes Schloss und den Hinweis, dass der Bereich erst in der Vollversion erstellt wird.
const LOCKED_PREVIEW_COPY = {
  research: {
    eyebrow: 'Step 2',
    title: 'Markenrecherche',
    text: 'Die komplette Markt- und Angebots-Analyse wird in der finalen Version freigeschaltet. Hier entsteht dein individueller Research-Report – aktuell ist er noch in Arbeit.',
  },
  audience: {
    eyebrow: 'Step 3',
    title: 'Zielgruppen-Analyse',
    text: 'Die detaillierte Zielgruppen-Recherche wird in der finalen Version freigeschaltet. Hier findest du dann deine segmentierten Zielgruppen – aktuell ist sie noch in Arbeit.',
  },
  leadmagnet: {
    eyebrow: 'Step 4',
    title: 'Kundenmagnete',
    text: 'Deine kuratierten Lead-Magneten werden in der finalen Version freigeschaltet. Hier entstehen die stärksten Magneten für deine Zielgruppe – aktuell sind sie noch in Arbeit.',
  },
};

const LockedPreviewView = ({ tab, companyName }) => {
  const copy = LOCKED_PREVIEW_COPY[tab] || LOCKED_PREVIEW_COPY.research;
  return (
    <div className="rp-locked">
      <div className="rp-locked-orbs" aria-hidden="true">
        <span className="rp-locked-orb rp-locked-orb-1" />
        <span className="rp-locked-orb rp-locked-orb-2" />
      </div>
      <div className="rp-locked-card">
        <span className="rp-locked-eyebrow">{copy.eyebrow}</span>
        <span className="rp-locked-lock" aria-hidden="true">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="10.5" width="16" height="10" rx="2" />
            <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
          </svg>
        </span>
        <h2 className="rp-locked-title">{copy.title}</h2>
        <p className="rp-locked-text">{copy.text}</p>
        <span className="rp-locked-hint">Exklusive Vorschau{companyName ? ` für ${companyName}` : ''}</span>
      </div>
    </div>
  );
};

// Der Status-Verlauf ist nicht mehr Teil der Haupt-Navigation, sondern nur noch
// über die subtile Status-Anzeige im Sidebar-Footer erreichbar.
const PIPELINE_TAB = { id: 'pipeline', label: 'Status', desc: 'Wo stehen wir?' };

// ── Pipeline-Status (read-only) ──
// Erklärt, wo der Kontakt in der Sales-Pipeline steht und was als Nächstes kommt.
// Bewusst nur Anzeige/Erklärung – Bearbeitung passiert ausschließlich im Struktur-Board.

// Kurze, menschliche Erklärung je Überstatus: „Was passiert hier?"
const PIPELINE_STEP_INFO = {
  kennenlern: 'Erstkontakt & Qualifizierung: Wir lernen das Unternehmen kennen, prüfen den Fit und vereinbaren einen Präsentations-Termin.',
  praesentation: 'Präsentations-Call: Wir stellen die Zusammenarbeit im Detail vor und senden im Anschluss das Angebot.',
  angebot_unterzeichnet: 'Angebot gesendet: Das Angebot ist raus – ab hier geht es in die Vorbereitung.',
  setup: 'Setup: Domains, Postfächer, Warm-up und das Team-Briefing werden eingerichtet.',
  kickoff: 'Kick-off-Call: Gemeinsamer Start der Zusammenarbeit – danach geht es operativ los.',
  experimental: 'Experimentalphase: Die ersten Kampagnen laufen, wir testen und optimieren das Outreach.',
  erster_kunde: 'Erster Kunde generiert: Das Ziel ist erreicht – die Zusammenarbeit liefert Ergebnisse.',
};

// Kurze Erklärung je Phase (für die Phasen-Überschriften im Verlauf).
const PIPELINE_PHASE_INFO = {
  sales: 'Vom ersten Kontakt bis zum unterschriebenen Angebot.',
  preparation: 'Alles wird für den Start aufgesetzt – bis zum Kick-off.',
  operative: 'Die Zusammenarbeit läuft – bis der erste Kunde steht.',
};

// ── Kick-off-Action: offizieller Startschuss für das Warm-up Reporting ──
// Erscheint im Status-Tab, solange der Kontakt im „Kick-off-Call"-Status ist.
// Ein Klick startet das Warm-up mit 24-Stunden-Verzögerung (delay), danach wird
// das erste tägliche Reporting freigeschaltet. Ist das Warm-up bereits gestartet
// (egal ob hier oder manuell im Reporting-Tab), zeigt der Button „Erledigt" und
// ist nicht mehr drückbar.
const KICKOFF_WARMUP_DELAY_MS = 24 * 60 * 60 * 1000;
const KickoffWarmupAction = ({ warmup, startWarmup }) => {
  const [now, setNow] = useState(Date.now());
  const [target, setTarget] = useState(String(WARMUP_TARGET_DEFAULT));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const started = warmup && typeof warmup.startAt === 'number' && Number(warmup.dailyTarget) > 0;
  const pending = started && warmup.startAt > now;

  const go = async () => {
    if (!startWarmup || started || busy) return;
    setBusy(true);
    setErr('');
    try {
      await startWarmup(clampWarmupTarget(target), KICKOFF_WARMUP_DELAY_MS);
    } catch (e) {
      setErr(e.message || 'Startschuss fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`rp-pipe-kickact ${started ? 'is-done' : ''}`}>
      <div className="rp-pipe-kickact-head">
        <span className="rp-pipe-kickact-eyebrow"><RpIcon name="rocket" size={14} /> Launch Phase</span>
        <h3 className="rp-pipe-kickact-title">{started ? 'Startschuss gegeben' : 'Offizieller Startschuss'}</h3>
        <p className="rp-pipe-kickact-desc">
          {started
            ? (pending
                ? `Das erste tägliche Reporting der Launch Phase wird in ${fmtReportingCountdown(warmup.startAt, now) || 'Kürze'} freigeschaltet. Danach startet der ${WARMUP_RAMP_DAYS}-Tage-Hochlauf bis ${warmup.dailyTarget} Mails/Tag.`
                : 'Die Launch Phase läuft. Der Outreach-Manager trägt das tägliche Reporting jetzt im Reporting-Tab ein.')
            : `Gib den offiziellen Startschuss: Danach läuft ein 24-Stunden-Timer, dann wird das erste tägliche Reporting der Launch Phase freigeschaltet und der ${WARMUP_RAMP_DAYS}-Tage-Hochlauf beginnt. Alternativ lässt sich die Launch Phase jederzeit direkt im Reporting-Tab starten.`}
        </p>
      </div>

      {started ? (
        <div className="rp-pipe-kickact-done">
          <span className="rp-pipe-kickact-done-ico"><RpIcon name="check" size={15} /></span>
          Erledigt{warmup.dailyTarget ? ` · Ziel ${warmup.dailyTarget} Mails/Tag` : ''}
        </div>
      ) : (
        <>
          <div className="rp-pipe-kickact-targetnum">
            <label className="rp-pipe-kickact-targetnum-label" htmlFor="rp-kickact-target-input">Mails pro Tag ab Monat 2</label>
            <div className="rp-pipe-kickact-targetnum-row">
              <input
                id="rp-kickact-target-input"
                type="number"
                inputMode="numeric"
                className="rp-pipe-kickact-targetnum-input"
                value={target}
                min={WARMUP_TARGET_MIN}
                max={WARMUP_TARGET_MAX}
                step={1}
                onChange={(e) => setTarget(e.target.value)}
                onBlur={() => setTarget(String(clampWarmupTarget(target)))}
              />
              <span className="rp-pipe-kickact-targetnum-unit">Mails / Tag</span>
            </div>
          </div>
          {err && <p className="rp-attach-error">{err}</p>}
          <button type="button" className="rp-pipe-kickact-btn" onClick={go} disabled={busy || !startWarmup}>
            {busy ? 'Startschuss läuft …' : 'Startschuss geben · 24h-Timer'}
          </button>
        </>
      )}
    </section>
  );
};

const PipelineStatusView = ({ pipeline, checklistState, kickoff, isAdmin, onToggleChecklist, onOpenSetup, warmup, startWarmup }) => {
  const hasData = !!(pipeline && pipeline.superStatus);

  // ── Setup-Fortschritt (aus den Setup-Details ausgelagert) ──
  // Geteilte Quelle mit dem Team Dashboard (customers/<slug>/checklist). Der
  // Kunde sieht den Stand transparent; abgehakt wird intern (Team / als
  // eingeloggtes Team auch direkt hier). Dazu: welche benötigten Infos schon
  // eingefügt wurden – mit Link in die Setup-Details.
  const checkState = checklistState || {};
  const setupSteps = SHARED_CHECKLIST_ITEMS;
  const setupComp = accountSetupCompletion(checkState);
  const setupStepsDone = setupComp.done;
  const setupDetails = Array.isArray(kickoff && kickoff.details) ? kickoff.details : [];
  const isSetupDetailFilled = (d) => {
    if (Array.isArray(d.attachments) && d.attachments.length > 0) return true;
    return d.type === 'pills' ? (Array.isArray(d.tags) && d.tags.length > 0) : !!(d.value || '').trim();
  };
  const setupInfoDone = setupDetails.filter(isSetupDetailFilled).length;
  const current = (pipeline && pipeline.superStatus) || DEFAULT_SUPER_STATUS;
  const lost = isLostSuperStatus(current);
  const currentCol = getColumn(current);
  const currentIdx = REGULAR_SUPER_STATUS_ORDER.indexOf(current);

  // Wie weit ist der Kontakt schon gekommen? Im Lost-Fall aus den hinterlegten
  // Verweildauern den am weitesten erreichten regulären Status ableiten.
  const durationsMap = (pipeline && pipeline.statusDurations) || {};
  let reachedIdx = currentIdx;
  if (lost) {
    reachedIdx = -1;
    REGULAR_SUPER_STATUS_ORDER.forEach((id, i) => {
      if (durationsMap[id] != null) reachedIdx = Math.max(reachedIdx, i);
    });
  }

  // Status eines Schritts im Verlauf: erledigt / aktuell / kommt noch.
  const stepState = (idx) => {
    if (lost) return idx <= reachedIdx ? 'done' : 'upcoming';
    if (idx < currentIdx) return 'done';
    if (idx === currentIdx) return 'current';
    return 'upcoming';
  };

  // „Als Nächstes": der nächste reguläre Schritt (nur im normalen Verlauf).
  const isFinal = current === FINAL_SUPER_STATUS;
  const nextId = !lost && currentIdx >= 0 && currentIdx < REGULAR_SUPER_STATUS_ORDER.length - 1
    ? REGULAR_SUPER_STATUS_ORDER[currentIdx + 1]
    : null;
  const nextCol = nextId ? getColumn(nextId) : null;

  // Verweildauer im aktuellen Status (laufend).
  const durations = hasData ? computeStatusDurations(pipeline) : [];
  const activeDuration = durations.find((d) => d.active);

  // Countdown zum nächsten Termin (nur dort relevant, wo er ansteht).
  let countdown = null;
  const phaseId = currentCol.phaseId;
  if (phaseId === SALES_PHASE_ID && pipeline && pipeline.presentationAt) {
    const cd = formatCountdown(pipeline.presentationAt);
    if (cd) countdown = { label: 'Präsentations-Call', value: cd };
  } else if (phaseId === PREPARATION_PHASE_ID && pipeline && pipeline.kickoffAt) {
    const cd = formatCountdown(pipeline.kickoffAt);
    if (cd) countdown = { label: 'Kick-off-Call', value: cd };
  }

  const accent = currentCol.accent;

  return (
    <div className="rp-pipe">
      {!hasData && (
        <div className="rp-pipe-note">
          Für diesen Kontakt liegt noch kein Pipeline-Status vor. Der Verlauf unten zeigt
          beispielhaft, wie eine Zusammenarbeit von Anfang bis zum ersten Kunden abläuft.
        </div>
      )}

      {/* Aktueller Stand */}
      <section
        className={`rp-pipe-hero ${lost ? 'rp-pipe-hero-lost' : ''}`}
        style={{ '--pipe-accent': accent }}
      >
        <div className="rp-pipe-hero-glow" aria-hidden="true" />
        <span className="rp-pipe-hero-eyebrow">
          {lost ? 'Lost-Bereich' : currentCol.phaseLabel} · Aktueller Stand
        </span>
        <h2 className="rp-pipe-hero-title">{currentCol.superStatusLabel}</h2>

        {pipeline && pipeline.subStatus && (
          <span className="rp-pipe-hero-sub">{pipeline.subStatus}</span>
        )}

        <p className="rp-pipe-hero-desc">
          {lost
            ? 'Dieser Kontakt ist aktuell im Lost-Bereich. Es laufen keine weiteren Schritte – der hinterlegte Grund zeigt, warum es hier endete.'
            : (PIPELINE_STEP_INFO[current] || 'Wir arbeiten an diesem Schritt.')}
        </p>

        {(activeDuration || countdown) && hasData && (
          <div className="rp-pipe-hero-metrics">
            {activeDuration && (
              <div className="rp-pipe-metric">
                <span className="rp-pipe-metric-label">In diesem Status seit</span>
                <span className="rp-pipe-metric-value">{formatDuration(activeDuration.ms)}</span>
              </div>
            )}
            {countdown && (
              <div className="rp-pipe-metric">
                <span className="rp-pipe-metric-label">{countdown.label}</span>
                <span className={`rp-pipe-metric-value rp-pipe-cd-${countdown.value.tone}`}>
                  {countdown.value.label}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Als Nächstes */}
        {!lost && (
          <div className="rp-pipe-next">
            {nextCol ? (
              <>
                <span className="rp-pipe-next-label">Als Nächstes</span>
                <span className="rp-pipe-next-step">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                  {nextCol.superStatusLabel}
                </span>
                <span className="rp-pipe-next-desc">{PIPELINE_STEP_INFO[nextId]}</span>
              </>
            ) : isFinal ? (
              <span className="rp-pipe-next-done">🎉 Ziel erreicht – der erste Kunde ist generiert.</span>
            ) : null}
          </div>
        )}
      </section>

      {/* Kick-off-Action: offizieller Startschuss fürs Warm-up Reporting (nur im
          Kick-off-Call-Status). Startet das Warm-up mit 24-Stunden-Timer. */}
      {!lost && current === KICKOFF_SUPER_STATUS && startWarmup && (
        <KickoffWarmupAction warmup={warmup} startWarmup={startWarmup} />
      )}

      {/* Setup-Fortschritt: kundensichtbare Schritte (geteilt mit dem Team
          Dashboard) + Stand der benötigten Infos, mit Link in die Setup-Details. */}
      <section className="rp-pipe-setup">
        <div className="rp-pipe-setup-head">
          <span className="rp-pipe-setup-eyebrow">Account-Setup</span>
          <h3 className="rp-pipe-setup-title">Setup-Fortschritt</h3>
          <p className="rp-pipe-setup-desc">
            {isAdmin
              ? 'Diese Schritte richtet das Team ein. Abhaken wirkt sofort im Team Dashboard (geteilte Checkliste).'
              : 'So weit ist euer Account-Setup. Wir aktualisieren den Stand laufend – ihr seht hier live, was schon erledigt ist.'}
          </p>
        </div>

        <div className="rp-pipe-setup-grid">
          {/* Schritte = shared Checklisten-Punkte aus dem Team Dashboard */}
          <div className="rp-pipe-setup-card">
            <div className="rp-pipe-setup-card-head">
              <span className="rp-pipe-setup-card-title">Was wir einrichten</span>
              <SetupMeter done={setupStepsDone} total={setupSteps.length} noun="erledigt" />
            </div>
            <div className="rp-pipe-setup-phases">
              {ACCOUNT_SETUP_PHASES.map((ph, idx) => {
                const pc = phaseCompletion(ph, checkState);
                const phaseDone = pc.total > 0 && pc.done === pc.total;
                return (
                  <div key={ph.id} className="rp-pipe-setup-phase" style={{ '--rp-setup-accent': ph.accent }}>
                    <div className="rp-pipe-setup-phase-head">
                      <span className="rp-pipe-setup-phase-no">Phase {idx + 1}</span>
                      <span className="rp-pipe-setup-phase-name">{ph.label}</span>
                      <span className={`rp-pipe-setup-phase-prog ${phaseDone ? 'is-ok' : ''}`}>{pc.done}/{pc.total}</span>
                    </div>
                    <ul className="rp-pipe-setup-steps">
                      {ph.steps.map((step) => {
                        const done = !!checkState[step.id];
                        return (
                          <li key={step.id} className={`rp-pipe-setup-step ${done ? 'is-done' : ''} ${isAdmin ? 'is-admin' : ''}`}>
                            <label className="rp-pipe-setup-check">
                              <input
                                type="checkbox"
                                checked={done}
                                disabled={!isAdmin}
                                onChange={isAdmin ? (e) => onToggleChecklist && onToggleChecklist(step.id, e.target.checked) : undefined}
                              />
                              <span className="rp-pipe-setup-tick" aria-hidden="true">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                              </span>
                            </label>
                            <span className="rp-pipe-setup-step-label">{step.clientLabel || step.title}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Benötigte Infos: schon eingefügt? – mit Link in die Setup-Details */}
          <div className="rp-pipe-setup-card">
            <div className="rp-pipe-setup-card-head">
              <span className="rp-pipe-setup-card-title">Eure Infos</span>
              <SetupMeter done={setupInfoDone} total={setupDetails.length} noun="erfasst" />
            </div>
            {setupDetails.length === 0 ? (
              <p className="rp-pipe-setup-empty">Noch keine Infos hinterlegt.</p>
            ) : (
              <ul className="rp-pipe-setup-infos">
                {setupDetails.map((d, i) => {
                  const filled = isSetupDetailFilled(d);
                  return (
                    <li key={i} className={`rp-pipe-setup-info ${filled ? 'is-filled' : ''}`}>
                      <span className="rp-pipe-setup-info-dot" aria-hidden="true">
                        {filled ? (
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                        ) : null}
                      </span>
                      <span className="rp-pipe-setup-info-label">{(d.label || '').trim() || 'Info'}</span>
                      <span className="rp-pipe-setup-info-badge">{filled ? 'eingefügt' : 'fehlt noch'}</span>
                    </li>
                  );
                })}
              </ul>
            )}
            {onOpenSetup && (
              <button type="button" className="rp-pipe-setup-link" onClick={onOpenSetup}>
                Infos im Account Setup ansehen &amp; ergänzen
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Kompletter Verlauf */}
      <section className="rp-pipe-journey">
        <span className="rp-pipe-journey-title">So geht es weiter</span>
        {PHASES.map((phase) => (
          <div
            key={phase.id}
            className="rp-pipe-phase"
            style={{ '--pipe-accent': phase.accent }}
          >
            <div className="rp-pipe-phase-head">
              <span className="rp-pipe-phase-bar" />
              <div className="rp-pipe-phase-text">
                <span className="rp-pipe-phase-label">{phase.label}</span>
                <span className="rp-pipe-phase-desc">{PIPELINE_PHASE_INFO[phase.id] || ''}</span>
              </div>
            </div>
            <ol className="rp-pipe-steps">
              {phase.superStatuses.map((s) => {
                const idx = REGULAR_SUPER_STATUS_ORDER.indexOf(s.id);
                const state = stepState(idx);
                return (
                  <li key={s.id} className={`rp-pipe-step rp-pipe-step-${state}`}>
                    <span className="rp-pipe-step-marker" aria-hidden="true">
                      {state === 'done' ? (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                      ) : (
                        <span className="rp-pipe-step-dot" />
                      )}
                    </span>
                    <div className="rp-pipe-step-body">
                      <div className="rp-pipe-step-top">
                        <span className="rp-pipe-step-label">{s.label}</span>
                        {state === 'current' && <span className="rp-pipe-step-badge">Aktuell hier</span>}
                        {state === 'done' && <span className="rp-pipe-step-badge rp-pipe-step-badge-done">Erledigt</span>}
                      </div>
                      <span className="rp-pipe-step-desc">{PIPELINE_STEP_INFO[s.id]}</span>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        ))}
      </section>
    </div>
  );
};

// Zusatz-Ansichten, die über das „+" rechts angehängt werden können.
// Setup-Details sind in das „Account Setup" eingeflossen (eigener Sub-Toggle in
// der E-Mail-Ansicht) und tauchen nicht mehr als separate Navigation auf.
const EXTRA_VIEWS = {
  // Analyse-Übersicht (Research-Preview). Über das „+" unter der Navigation
  // ein-/ausblendbar (nur fürs Team). Im Locked-Modus für den Kunden fest sichtbar.
  analyse: { id: 'analyse', label: 'Analyse', desc: 'Analyse-Übersicht' },
  email: { id: 'email', label: 'Outreach Setup', desc: 'Setup-Details, DNA & Texte' },
  reporting: { id: 'reporting', label: 'Reporting', desc: 'Monatliches Reporting & Launch Phase' },
};
const EXTRA_VIEW_ORDER = ['analyse', 'email', 'reporting'];

// Deep-Link-Aliase für die Sub-Toggles im Account Setup („email"-Tab).
// Ein Link mit ?tab=setup landet auf den Setup-Details, ?tab=emails (bzw. die
// Aliase texte/mails/vault) direkt auf den E-Mail-Texten. So lassen sich gezielte
// Links versenden, die nach dem Passwort-Login sofort an die richtige Stelle führen.
const EMAIL_TAB_ALIASES = {
  setup: 'setup',
  emails: 'vault',
  'e-mails': 'vault',
  mails: 'vault',
  texte: 'vault',
  'e-mail-texte': 'vault',
  vault: 'vault',
};

// Website unter dem Firmennamen: zeigt den Link, sonst ein „+“ zum Hinzufügen.
// Bearbeitbar inline; der Wert wird mit dem Report gespeichert (company.website).
const HeaderWebsite = ({ value, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);

  const normalize = (v) => {
    const t = (v || '').trim();
    if (!t) return '';
    return /^https?:\/\//i.test(t) ? t : `https://${t}`;
  };
  const commit = () => { onChange(normalize(draft)); setEditing(false); };
  const cancel = () => { setDraft(value || ''); setEditing(false); };
  const href = normalize(value);
  const label = (value || '').replace(/^https?:\/\//i, '').replace(/\/+$/, '');

  const Globe = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
    </svg>
  );

  if (editing) {
    return (
      <div className="rp-web rp-web-editing">
        {Globe}
        <input
          className="rp-web-input"
          autoFocus
          value={draft}
          placeholder="https://firma.de"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            if (e.key === 'Escape') { cancel(); }
          }}
        />
        <button type="button" className="rp-web-icon rp-web-ok" onClick={commit} aria-label="Speichern">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
        </button>
        <button type="button" className="rp-web-icon rp-web-cancel" onClick={cancel} aria-label="Abbrechen">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </div>
    );
  }

  if (!value) {
    return (
      <button type="button" className="rp-web-add" onClick={() => setEditing(true)}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
        Website hinzufügen
      </button>
    );
  }

  return (
    <div className="rp-web">
      <a className="rp-web-link" href={href} target="_blank" rel="noreferrer">
        {Globe}
        <span>{label}</span>
      </a>
      <button type="button" className="rp-web-icon rp-web-edit" onClick={() => setEditing(true)} aria-label="Website bearbeiten">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
      </button>
    </div>
  );
};

// Direkt im Header editierbarer Klartext (Firmenname, Branche, Zielgruppe).
// Klick/Fokus genügt zum Bearbeiten; Blur oder Enter (einzeilig) übernimmt,
// Escape verwirft die Änderung.
const InlineEditableText = ({ value, placeholder, onChange, className = '', tag: Tag = 'span', multiline = false }) => {
  const ref = useRef(null);
  const lastValue = useRef(null);
  const [empty, setEmpty] = useState(!String(value || '').trim());

  // Externe Wertänderungen (Report-Wechsel, KI) ins DOM spiegeln, aber nicht
  // während des Tippens – sonst springt der Cursor.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const incoming = String(value || '');
    if (incoming === lastValue.current) return;
    lastValue.current = incoming;
    el.textContent = incoming;
    setEmpty(!incoming.trim());
  }, [value]);

  const emit = () => {
    const el = ref.current;
    if (!el) return;
    const text = multiline
      ? (el.textContent || '').replace(/\n{2,}/g, '\n')
      : (el.textContent || '').replace(/\s*\n\s*/g, ' ');
    lastValue.current = text;
    setEmpty(!text.trim());
    onChange(text);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      if (ref.current) ref.current.blur();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      const el = ref.current;
      if (el) {
        el.textContent = String(value || '');
        lastValue.current = String(value || '');
        setEmpty(!String(value || '').trim());
        el.blur();
      }
    }
  };

  const onPaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text/plain');
    try { document.execCommand('insertText', false, text); } catch (err) { /* */ }
    emit();
  };

  return (
    <Tag
      ref={ref}
      className={`${className} rp-inline-edit${empty ? ' is-empty' : ''}`}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-label={placeholder}
      data-placeholder={placeholder}
      spellCheck={false}
      onInput={emit}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      onBlur={emit}
    />
  );
};

// ── Analyse-Tab (Research-Preview / Locked-Modus) ──
// Überblicks-Screen: links optionales veed.io-Erklärvideo + eine (per Formular
// oder CSV) hochladbare Beispiel-Firmenliste, rechts ein KI-vorgeneriertes
// Mail-Konzept mit zwei toggelbaren Varianten (Betreff + Text, ohne Bewertung).
// Bearbeitbar nur fürs Team (isAdmin); der Kunde sieht die formatierte Ansicht.
const AnalyseTab = ({ report, companyName, companyIndustry, companyAudience, website, isAdmin, analyse, setAnalyse }) => {
  const name = companyName || (report && report.company && report.company.name) || '';
  const href = website
    ? (/^https?:\/\//i.test(website) ? website : `https://${website}`)
    : null;
  const industry = (companyIndustry || (report && report.company && report.company.industry) || '').trim();
  const audience = (companyAudience || (report && report.company && report.company.audience) || '').trim();
  const companies = Array.isArray(analyse.companies) ? analyse.companies : [];
  // Nach Potenzial (absteigend) sortieren – höchste Chancen oben.
  const sortedCompanies = useMemo(() => {
    return [...companies].sort((a, b) => {
      const ra = Math.max(0, Math.min(100, Math.round(Number(a.rating) || 0)));
      const rb = Math.max(0, Math.min(100, Math.round(Number(b.rating) || 0)));
      if (rb !== ra) return rb - ra;
      return String(a.name || '').localeCompare(String(b.name || ''), 'de');
    });
  }, [companies]);
  const emails = Array.isArray(analyse.emails) ? analyse.emails : [];
  const [activeEmail, setActiveEmail] = useState(0);
  const [videoEditOpen, setVideoEditOpen] = useState(false);
  const embedSrc = veedEmbedSrc(analyse.video);

  // ── Firmenliste ──
  const updateCompany = (id, patch) =>
    setAnalyse((a) => ({ ...a, companies: (a.companies || []).map((c) => (c.id === id ? { ...c, ...patch } : c)) }));
  const addCompany = () =>
    setAnalyse((a) => ({ ...a, companies: [...(a.companies || []), { id: makeAnalyseId(), name: '', website: '', description: '', rating: 0 }] }));
  const removeCompany = (id) =>
    setAnalyse((a) => ({ ...a, companies: (a.companies || []).filter((c) => c.id !== id) }));

  // ── Mail-Konzept ──
  const updateEmail = (idx, patch) =>
    setAnalyse((a) => {
      const next = (a.emails || []).slice();
      while (next.length < 2) next.push({ subject: '', body: '', kind: next.length === 0 ? 'intro' : 'followup' });
      next[idx] = { ...next[idx], ...patch };
      return { ...a, emails: next };
    });
  // Weitere Mail hinzufügen (bis max. 5). Neue Mails sind standardmäßig Follow-ups.
  const addEmail = () =>
    setAnalyse((a) => {
      const next = (a.emails || []).slice();
      if (next.length >= MAX_ANALYSE_EMAILS) return a;
      next.push({ subject: '', body: '', toName: '', toCompany: '', kind: 'followup' });
      return { ...a, emails: next };
    });
  // Mail entfernen (mindestens 2 bleiben erhalten – das ist der Standard).
  const removeEmail = (idx) => {
    setAnalyse((a) => {
      const next = (a.emails || []).slice();
      if (next.length <= 2) return a;
      next.splice(idx, 1);
      return { ...a, emails: next };
    });
    setActiveEmail((cur) => (cur >= idx && cur > 0 ? cur - 1 : cur));
  };
  const setEmailKind = (idx, kind) =>
    updateEmail(idx, { kind: kind === 'followup' ? 'followup' : 'intro' });

  // ── Video ──
  const [videoDraft, setVideoDraft] = useState(analyse.video || '');
  useEffect(() => { setVideoDraft(analyse.video || ''); }, [analyse.video]);

  // ── CSV-Import ──
  const CSV_FIELDS = [
    { key: 'name', label: 'Name des Unternehmens' },
    { key: 'website', label: 'Website' },
    { key: 'description', label: 'Zusammenfassung' },
  ];
  const fileRef = useRef(null);
  const [csv, setCsv] = useState(null); // { headers, rows }
  const [csvMap, setCsvMap] = useState({ name: '', website: '', description: '' });
  const [csvError, setCsvError] = useState('');
  const guessMap = (headers) => {
    const find = (...keys) => headers.find((h) => keys.some((k) => h.toLowerCase().includes(k))) || '';
    return {
      name: find('firm', 'unternehm', 'company', 'betrieb', 'name'),
      website: find('web', 'url', 'seite', 'domain', 'homepage'),
      description: find('beschreib', 'description', 'zusammenfass', 'summary', 'notiz', 'desc', 'kommentar', 'info'),
    };
  };
  const onCsvFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setCsvError('');
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = parseCsv(String(reader.result || ''));
        if (!parsed.headers.length || !parsed.rows.length) {
          setCsvError('Keine verwertbaren Zeilen in der CSV gefunden.');
          setCsv(null);
          return;
        }
        setCsv(parsed);
        setCsvMap(guessMap(parsed.headers));
      } catch (err) {
        setCsvError('CSV konnte nicht gelesen werden.');
        setCsv(null);
      }
    };
    reader.onerror = () => setCsvError('Datei konnte nicht gelesen werden.');
    reader.readAsText(file);
    e.target.value = '';
  };
  const importCsv = (replace) => {
    if (!csv) return;
    const mapped = csv.rows
      .map((r) => ({
        id: makeAnalyseId(),
        name: csvMap.name ? (r[csvMap.name] || '') : '',
        website: csvMap.website ? (r[csvMap.website] || '') : '',
        description: csvMap.description ? (r[csvMap.description] || '') : '',
        rating: 0,
      }))
      .filter((c) => c.name || c.website || c.description);
    if (!mapped.length) { setCsvError('Mit dieser Zuordnung entstehen keine Einträge.'); return; }
    setAnalyse((a) => ({ ...a, companies: replace ? mapped : [...(a.companies || []), ...mapped] }));
    setCsv(null);
    setCsvError('');
  };

  const linkify = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);
  // Potenzial-Rating (0–100) in eine Stufe/Label übersetzen (für Badge-Farbe).
  const ratingTier = (r) => {
    const n = Math.max(0, Math.min(100, Math.round(Number(r) || 0)));
    if (n >= 80) return { tier: 'hot', label: 'Hohes Potenzial' };
    if (n >= 55) return { tier: 'warm', label: 'Gutes Potenzial' };
    return { tier: 'cold', label: 'Basis-Potenzial' };
  };
  return (
    <div className="rp-analyse">
      {/* Dezente, animierte Hintergrund-Orbs (dark-mode-freundlich). */}
      <div className="rp-analyse-orbs" aria-hidden="true">
        <span className="rp-analyse-orb rp-analyse-orb-1" />
        <span className="rp-analyse-orb rp-analyse-orb-2" />
        <span className="rp-analyse-orb rp-analyse-orb-3" />
      </div>
      <header className="rp-analyse-head">
        {isAdmin && (
          <button
            type="button"
            className={`rp-analyse-video-toggle ${videoEditOpen ? 'is-open' : ''}`}
            onClick={() => setVideoEditOpen((o) => !o)}
            title="Einstellungen"
            aria-label="Einstellungen"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>
        )}
        <span className="rp-analyse-eyebrow">Analyse</span>
        <h1 className="rp-analyse-title">{name}</h1>
        {href && (
          <a className="rp-analyse-website" href={href} target="_blank" rel="noreferrer">{website}</a>
        )}
        {(industry || audience) && (
          <div className="rp-analyse-teaser">
            {industry && (
              <span className="rp-analyse-teaser-item">
                <span className="rp-analyse-teaser-label">Branche</span>
                <span className="rp-analyse-teaser-val">{industry}</span>
              </span>
            )}
            {audience && (
              <span className="rp-analyse-teaser-item">
                <span className="rp-analyse-teaser-label">Zielgruppe</span>
                <span className="rp-analyse-teaser-val">{audience}</span>
              </span>
            )}
          </div>
        )}
      </header>

      {/* Video-Link-Eingabe (nur Team): über das Symbol oben rechts ein-/ausklappbar. */}
      {isAdmin && videoEditOpen && (
        <div className="rp-analyse-video-editbar">
          <div className="rp-analyse-video-input">
            <input
              type="text"
              className="rp-analyse-input"
              value={videoDraft}
              placeholder="https://www.veed.io/view/… oder eingebetteten iframe-Code"
              onChange={(e) => setVideoDraft(e.target.value)}
              onBlur={() => setAnalyse((a) => ({ ...a, video: videoDraft.trim() }))}
            />
            {analyse.video && (
              <button type="button" className="rp-analyse-btn-ghost" onClick={() => { setVideoDraft(''); setAnalyse((a) => ({ ...a, video: '' })); }}>
                Entfernen
              </button>
            )}
          </div>
          {videoDraft.trim() && !embedSrc && (
            <p className="rp-analyse-hint rp-analyse-hint-warn">Kein einbettbares veed.io-Video erkannt. Nutze den Share-Link (…/view/…) oder den Embed-Code.</p>
          )}
        </div>
      )}

      <div className="rp-analyse-stack">
        {/* Video (veed.io) – volle Breite, nur wenn einbettbar. */}
        {embedSrc && (
          <section className="rp-analyse-card rp-analyse-video-card">
            <div className="rp-analyse-video">
              <iframe
                src={embedSrc}
                title="Erklärvideo"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                frameBorder="0"
              />
            </div>
          </section>
        )}

        {/* ── Kontaktliste (Excel-Tabelle) ── */}
        <section className="rp-analyse-card rp-analyse-table-card">
          <div className="rp-analyse-card-head">
            <h2 className="rp-analyse-card-title">Signal-basierte Kontaktliste<span className="rp-analyse-title-star" aria-hidden="true">*</span></h2>
            {isAdmin && (
              <div className="rp-analyse-card-actions">
                <button type="button" className="rp-analyse-add-firma" onClick={addCompany} title="Firma hinzufügen" aria-label="Firma hinzufügen">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                </button>
                <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={onCsvFile} />
              </div>
            )}
          </div>

          {/* CSV-Zuordnung */}
          {isAdmin && csv && (
            <div className="rp-analyse-csv">
              <p className="rp-analyse-csv-title">Spalten zuordnen ({csv.rows.length} Zeilen erkannt)</p>
              <div className="rp-analyse-csv-map">
                {CSV_FIELDS.map((f) => (
                  <label key={f.key} className="rp-analyse-csv-row">
                    <span>{f.label}</span>
                    <select
                      value={csvMap[f.key]}
                      onChange={(e) => setCsvMap((m) => ({ ...m, [f.key]: e.target.value }))}
                    >
                      <option value="">— ignorieren —</option>
                      {csv.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </label>
                ))}
              </div>
              <div className="rp-analyse-csv-actions">
                <button type="button" className="rp-analyse-btn" onClick={() => importCsv(false)}>Anhängen</button>
                <button type="button" className="rp-analyse-btn-ghost" onClick={() => importCsv(true)}>Liste ersetzen</button>
                <button type="button" className="rp-analyse-btn-ghost" onClick={() => { setCsv(null); setCsvError(''); }}>Abbrechen</button>
              </div>
            </div>
          )}
          {isAdmin && csvError && <p className="rp-analyse-hint rp-analyse-hint-warn">{csvError}</p>}

          {companies.length === 0 ? (
            <p className="rp-analyse-empty">{isAdmin ? 'Noch keine Firmen. Füge sie manuell hinzu oder importiere eine CSV.' : 'Noch keine Einträge.'}</p>
          ) : (
            <div className="rp-analyse-table-wrap">
              <table className="rp-analyse-table">
                <thead>
                  <tr>
                    <th className="rp-analyse-th rp-analyse-th-num">#</th>
                    <th className="rp-analyse-th rp-analyse-th-company">Unternehmen</th>
                    <th className="rp-analyse-th rp-analyse-th-signal">Signal</th>
                    <th className="rp-analyse-th rp-analyse-th-website">Website</th>
                    <th className="rp-analyse-th rp-analyse-th-rating">Potenzial</th>
                    {isAdmin && <th className="rp-analyse-th rp-analyse-th-act" aria-label="Aktionen" />}
                  </tr>
                </thead>
                <tbody>
                  {sortedCompanies.map((c, idx) => {
                    const { tier, label } = ratingTier(c.rating);
                    return (
                      <tr key={c.id} className="rp-analyse-tr">
                        <td className="rp-analyse-td rp-analyse-td-num">{idx + 1}</td>
                        {isAdmin ? (
                          <>
                            <td className="rp-analyse-td rp-analyse-td-company">
                              <input className="rp-analyse-cell-input rp-analyse-cell-input-company" value={c.name} placeholder="Firma" onChange={(e) => updateCompany(c.id, { name: e.target.value })} />
                            </td>
                            <td className="rp-analyse-td rp-analyse-td-signal">
                              <textarea className="rp-analyse-cell-textarea" value={c.description} placeholder="Signal / Zusammenfassung" rows={4} onChange={(e) => updateCompany(c.id, { description: e.target.value })} />
                            </td>
                            <td className="rp-analyse-td">
                              <input className="rp-analyse-cell-input" value={c.website} placeholder="Website" onChange={(e) => updateCompany(c.id, { website: e.target.value })} />
                            </td>
                            <td className="rp-analyse-td rp-analyse-td-rating">
                              <div className="rp-analyse-cell-rating-edit">
                                <input type="number" min="0" max="100" className="rp-analyse-cell-input rp-analyse-cell-num" value={c.rating || 0} onChange={(e) => updateCompany(c.id, { rating: Math.max(0, Math.min(100, Math.round(Number(e.target.value) || 0))) })} />
                                <span className="rp-analyse-cell-rating-suffix">%</span>
                              </div>
                            </td>
                            <td className="rp-analyse-td rp-analyse-td-act">
                              <button type="button" className="rp-analyse-row-del" onClick={() => removeCompany(c.id)} aria-label="Zeile entfernen">×</button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="rp-analyse-td rp-analyse-td-company">
                              <span className="rp-analyse-cell-name">
                                {c.name || '—'}
                                {c.verified && (
                                  <span className="rp-analyse-item-verified" title="Firma über Google/Impressum verifiziert">
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>
                                  </span>
                                )}
                              </span>
                            </td>
                            <td className="rp-analyse-td rp-analyse-td-signal">
                              {c.description && <p className="rp-analyse-cell-text">{c.description}</p>}
                              {Array.isArray(c.signals) && c.signals.length > 0 && (
                                <ul className="rp-analyse-cell-signals">
                                  {c.signals.slice(0, 2).map((s, i) => (
                                    <li key={i}>
                                      {s.date && <span className="rp-analyse-signal-date">{s.date}</span>}
                                      {s.url ? (
                                        <a className="rp-analyse-signal-text" href={s.url} target="_blank" rel="noreferrer">{s.text}</a>
                                      ) : (
                                        <span className="rp-analyse-signal-text">{s.text}</span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {!c.description && !(Array.isArray(c.signals) && c.signals.length) && '—'}
                            </td>
                            <td className="rp-analyse-td">
                              {c.website ? (
                                <a href={linkify(c.website)} target="_blank" rel="noreferrer" className="rp-analyse-cell-link">{c.website.replace(/^https?:\/\/(www\.)?/i, '')}</a>
                              ) : '—'}
                            </td>
                            <td className="rp-analyse-td rp-analyse-td-rating">
                              {c.rating > 0 ? (
                                <span className={`rp-analyse-cell-rating rp-analyse-rating-${tier}`} title={label}>
                                  <span className="rp-analyse-cell-rating-num">{c.rating}%</span>
                                  <span className="rp-analyse-cell-rating-bar"><span style={{ width: `${c.rating}%` }} /></span>
                                </span>
                              ) : '—'}
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* ── Mail-Konzept ── */}
        <section className="rp-analyse-card rp-analyse-mail-card">
          <div className="rp-analyse-card-head">
            <h2 className="rp-analyse-card-title">Mail-Konzept</h2>
          </div>
          <div className="rp-analyse-mailtabs" role="tablist" aria-label="Mail-Varianten">
            {emails.map((e, i) => {
              const kind = e.kind === 'followup' ? 'followup' : 'intro';
              return (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  aria-selected={activeEmail === i}
                  className={`rp-analyse-mailtab ${activeEmail === i ? 'is-active' : ''}`}
                  onClick={() => setActiveEmail(i)}
                >
                  <span className="rp-analyse-mailtab-label">Variante {i + 1}</span>
                  <span className={`rp-analyse-mailtab-tag rp-analyse-mailtab-tag-${kind}`}>
                    {ANALYSE_MAIL_KIND_LABEL[kind]}
                  </span>
                  {isAdmin && emails.length > 2 && (
                    <span
                      className="rp-analyse-mailtab-del"
                      role="button"
                      tabIndex={0}
                      title="Diese Mail entfernen"
                      onClick={(ev) => { ev.stopPropagation(); removeEmail(i); }}
                      onKeyDown={(ev) => { if (ev.key === 'Enter') { ev.stopPropagation(); removeEmail(i); } }}
                    >
                      ×
                    </span>
                  )}
                </button>
              );
            })}
            {isAdmin && emails.length < MAX_ANALYSE_EMAILS && (
              <button
                type="button"
                className="rp-analyse-mailtab-add"
                onClick={addEmail}
                title="Weitere Mail hinzufügen"
                aria-label="Weitere Mail hinzufügen"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
              </button>
            )}
          </div>
          {(() => {
            const em = emails[activeEmail] || { subject: '', body: '' };
            // Empfaenger: bevorzugt die an der Mail gespeicherten Infos, sonst der
            // passende Kontakt aus der (nach Potenzial sortierten) Liste.
            const fallback = sortedCompanies[activeEmail] || null;
            const rcpt = {
              name: (em.toName || '').trim(),
              company: (em.toCompany || (fallback && fallback.name) || '').trim(),
            };
            const hasRcpt = !!(rcpt.name || rcpt.company);
            const recipientBar = hasRcpt ? (
              <div className="rp-analyse-mail-to">
                <span className="rp-analyse-mail-to-label">An</span>
                <span className="rp-analyse-mail-to-vals">
                  {rcpt.name && <span className="rp-analyse-mail-to-name">{rcpt.name}</span>}
                  {rcpt.company && <span className="rp-analyse-mail-to-company">{rcpt.company}</span>}
                </span>
              </div>
            ) : null;
            if (isAdmin) {
              const fallbackCompany = (fallback && fallback.name) || '';
              const activeKind = em.kind === 'followup' ? 'followup' : 'intro';
              return (
                <div className="rp-analyse-mail-edit">
                  <div className="rp-analyse-mail-kindrow" role="group" aria-label="Mail-Typ">
                    {ANALYSE_MAIL_KINDS.map((k) => (
                      <button
                        key={k}
                        type="button"
                        className={`rp-analyse-mail-kindbtn ${activeKind === k ? 'is-active' : ''}`}
                        onClick={() => setEmailKind(activeEmail, k)}
                      >
                        {ANALYSE_MAIL_KIND_LABEL[k]}
                      </button>
                    ))}
                  </div>
                  <div className="rp-analyse-mail-to rp-analyse-mail-to-edit">
                    <span className="rp-analyse-mail-to-label">An</span>
                    <div className="rp-analyse-mail-to-fields">
                      <input
                        className="rp-analyse-input rp-analyse-mail-to-input"
                        value={em.toCompany || ''}
                        placeholder={fallbackCompany ? `Firma (z. B. ${fallbackCompany})` : 'Firma'}
                        onChange={(e) => updateEmail(activeEmail, { toCompany: e.target.value })}
                      />
                      <input
                        className="rp-analyse-input rp-analyse-mail-to-input"
                        value={em.toName || ''}
                        placeholder="Ansprechpartner (optional)"
                        onChange={(e) => updateEmail(activeEmail, { toName: e.target.value })}
                      />
                    </div>
                  </div>
                  <label className="rp-analyse-field-label">Betreff</label>
                  <input className="rp-analyse-input" value={em.subject} placeholder="Betreffzeile" onChange={(e) => updateEmail(activeEmail, { subject: e.target.value })} />
                  <label className="rp-analyse-field-label">E-Mail-Text</label>
                  <textarea className="rp-analyse-input rp-analyse-mail-body" value={em.body} placeholder="E-Mail-Text" rows={14} onChange={(e) => updateEmail(activeEmail, { body: e.target.value })} />
                </div>
              );
            }
            if (!em.subject && !em.body) {
              return <p className="rp-analyse-empty">Kein Mail-Konzept hinterlegt.</p>;
            }
            return (
              <div className="rp-analyse-mail-view">
                {recipientBar}
                {em.subject && (
                  <div className="rp-analyse-mail-subject">
                    <span className="rp-analyse-mail-subject-label">Betreff</span>
                    <span className="rp-analyse-mail-subject-val">{em.subject}</span>
                  </div>
                )}
                <div className="rp-analyse-mail-text">{em.body}</div>
              </div>
            );
          })()}
        </section>

        {/* Rechtlicher Hinweis zu den (aus oeffentlichen Quellen recherchierten) Daten. */}
        <p className="rp-analyse-disclaimer">
          <span className="rp-analyse-disclaimer-star" aria-hidden="true">*</span>
          Die Unternehmensangaben in dieser signal-basierten Kontaktliste stammen aus
          oeffentlich zugaenglichen Quellen (z. B. Websites, Presse) und dienen als
          unverbindliche Recherche-Vorschau. Es werden keine personenbezogenen Kontaktdaten
          angezeigt. Die Vorschau ist nur fuer den jeweiligen Empfaenger im privaten Rahmen
          bestimmt. Es besteht keine Gewaehr fuer Richtigkeit oder Aktualitaet. Fuer die
          rechtmaessige Nutzung der Informationen ist der Nutzer verantwortlich.
        </p>
      </div>
    </div>
  );
};

const ResearchReport = ({ report, slug, pipeline, isAdmin, reportings: initialReportings = [], warmup: initialWarmup = null, leadMagnetAudit: initialLeadMagnetAudit = null }) => {
  // Start-Tab optional per URL-Query steuerbar (z. B. ?tab=setup aus der Kick-off-Mail).
  // „setup" ist jetzt ein Sub-Toggle im Account Setup → auf den E-Mail-Tab umleiten
  // und die Setup-Details-Ansicht direkt vorselektieren.
  const initialQueryTab = (() => {
    try {
      const t = new URLSearchParams(window.location.search).get('tab');
      return typeof t === 'string' && t.trim() ? t.trim().toLowerCase() : 'research';
    } catch (e) { return 'research'; }
  })();
  // setup/emails/… sind keine eigenen Tabs, sondern Sub-Toggles im „email"-Tab.
  // Erkennen wir so einen Alias, leiten wir auf das Account Setup und selektieren
  // direkt die passende Ansicht (Setup-Details bzw. E-Mail-Texte).
  const initialEmailMode = EMAIL_TAB_ALIASES[initialQueryTab] || null;
  // Research-Preview (Locked-Modus): schlanke Teaser-Navigation. Analyse ist frei,
  // Research/Zielgruppen/Kundenmagnet sind gesperrt (goldenes Schloss), die internen
  // Ansichten (Outreach/Reporting/Status/Connect SalesGod) sind ausgeblendet.
  const researchLocked = !!(pipeline && pipeline.researchMode === 'locked');
  const initialTab = researchLocked ? 'analyse' : (initialEmailMode ? 'email' : initialQueryTab);
  // ?day=<n> (zusammen mit ?tab=emails): nach dem Öffnen direkt zu den Mails dieses
  // Launch-Phase-Tages scrollen und sie kurz hervorheben.
  const initialFocusDay = (() => {
    try {
      const n = parseInt(new URLSearchParams(window.location.search).get('day'), 10);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch (e) { return null; }
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  // Setup-Checklisten-Status (geteilt mit dem Team Dashboard). Initial aus der
  // Pipeline; lokales State, damit ein Admin-Häkchen sofort sichtbar ist.
  const [checklistState, setChecklistState] = useState(() =>
    (pipeline && pipeline.checklist && typeof pipeline.checklist === 'object') ? pipeline.checklist : {}
  );
  useEffect(() => {
    setChecklistState((pipeline && pipeline.checklist && typeof pipeline.checklist === 'object') ? pipeline.checklist : {});
  }, [pipeline]);

  // Live-Synchronisation der Setup-Checkliste über den öffentlich lesbaren
  // Spiegel-Knoten (salesgod-research/setupStatus/<slug>). So sieht der Kunde
  // (und ein eingeloggter Admin) sofort, wenn im Team Dashboard oder über den
  // Setup-Link etwas abgehakt wird – ohne die Seite neu zu laden.
  useEffect(() => {
    if (!slug) return undefined;
    const node = ref(database, `salesgod-research/setupStatus/${slug}/checklist`);
    const unsub = onValue(node, (snap) => {
      const live = snap.val();
      if (!live || typeof live !== 'object') return;
      setChecklistState((prev) => ({ ...prev, ...live }));
    });
    return () => unsub();
  }, [slug]);

  // Nur das eingeloggte Team darf abhaken (schreibt direkt ins Kundenobjekt).
  const toggleChecklistItem = useCallback((itemId, next) => {
    if (!isAdmin || !slug) return;
    setChecklistState((s) => ({ ...s, [itemId]: next }));
    update(ref(database, `salesgod-research/customers/${slug}`), {
      [`checklist/${itemId}`]: next,
      [`checklistAuto/${itemId}`]: false,
      updatedAt: serverTimestamp(),
    }).catch((e) => {
      console.error('Checklisten-Sync fehlgeschlagen:', slug, e);
      setChecklistState((s) => ({ ...s, [itemId]: !next }));
    });
  }, [isAdmin, slug]);
  const [config, setConfig] = useState(report.config);
  const [research, setResearch] = useState(() => buildResearchState(report.research.targetResearch));
  const [angles, setAngles] = useState(() =>
    (report.research.angles || []).map((a) => ({ title: a.title || '', body: a.body || '' }))
  );
  // „Angebot des Kunden"-Analyse (editierbar, persistiert in report.research.offerAnalysis).
  const [offer, setOffer] = useState(() => buildOfferState(report.research && report.research.offerAnalysis));
  // Zielgruppen-Tiefenrecherche (editierbar, persistiert in report.research.audienceResearch).
  const [audience, setAudience] = useState(() => buildAudienceState(report.research && report.research.audienceResearch));
  const [website, setWebsite] = useState(report.company.website || '');
  // Kundenmagnet ("Magnetic Treasury"): individuelle Lead-Magneten (kundensichtbar,
  // persistiert in report.leadMagnet.magnets) + Konkurrenz-Audit (admin-only, kommt
  // separat als Prop, nicht im Report).
  const [magnets, setMagnets] = useState(() =>
    (report.leadMagnet && Array.isArray(report.leadMagnet.magnets)) ? report.leadMagnet.magnets : []
  );
  const [leadMagnetAudit, setLeadMagnetAudit] = useState(initialLeadMagnetAudit);
  const leadMagnetUnlocked = !!(pipeline && pipeline.leadMagnetUnlocked);
  // Analyse-Tab (Research-Preview): Video-Link, Beispiel-Firmenliste und zwei
  // toggelbare Mail-Konzepte. Editierbar nur fürs Team; persistiert im Report.
  const [analyse, setAnalyse] = useState(() => buildAnalyseState(report.analyse));
  // Firmen-Stammdaten im Header: direkt editierbar (Name, Branche, Zielgruppe).
  const [companyName, setCompanyName] = useState(report.company.name || '');
  const [companyIndustry, setCompanyIndustry] = useState(report.company.industry || '');
  const [companyAudience, setCompanyAudience] = useState(report.company.audience || '');
  // Zusätzlich angehängte Ansichten (rechts neben Research/Impact), aus dem Report geladen.
  const [addedViews, setAddedViews] = useState(() => {
    // Alte Reports konnten „setup" als eigene Ansicht haben → in „email" überführen.
    const mapped = (report.addedViews || []).map((id) => (id === 'setup' ? 'email' : id));
    // Analyse-Tab automatisch einblenden, wenn bereits Analyse-Inhalt vorliegt
    // (Video, Firmenliste oder Mail-Konzept). Sonst bleibt er verborgen und ist
    // über das „+" unter der Navigation manuell hinzufügbar.
    const a = report.analyse;
    const analyseHasContent = !!(a && typeof a === 'object' && (
      (a.video && String(a.video).trim()) ||
      (Array.isArray(a.companies) && a.companies.length) ||
      (Array.isArray(a.emails) && a.emails.some((e) => e && ((e.subject || '').trim() || (e.body || '').trim())))
    ));
    if (analyseHasContent) mapped.push('analyse');
    // Wurde der Report über einen ?tab=setup/emails-Link geöffnet, das Account Setup anzeigen.
    if (initialEmailMode) mapped.push('email');
    // Bei aktivem Vertrag (es existieren monatliche Reportings) das Reporting-Tab
    // automatisch einblenden – dort reicht der Outreach-Manager monatlich ein.
    if (Array.isArray(initialReportings) && initialReportings.length) mapped.push('reporting');
    return mapped.filter((id, idx) => EXTRA_VIEWS[id] && mapped.indexOf(id) === idx);
  });
  // Sobald (nachträglich) monatliche Reportings vorliegen, das Tab einblenden.
  useEffect(() => {
    if (Array.isArray(initialReportings) && initialReportings.length) {
      setAddedViews((prev) => (prev.includes('reporting') ? prev : [...prev, 'reporting']));
    }
  }, [initialReportings]);
  // E-Mail Vault: editierbare Outreach-Mails. Startet mit einer leeren Mail.
  const [emails, setEmails] = useState(() => {
    const fromReport = Array.isArray(report.emails)
      ? report.emails
          .filter((e) => e && typeof e === 'object')
          .map((e) => ({
            id: e.id || makeEmailId(),
            subject: e.subject || '',
            body: e.body || '',
            reviews: normalizeReviews(e),
            createdAt: typeof e.createdAt === 'number' ? e.createdAt : 0,
            source: e.source || '',
            tag: e.tag || '',
          }))
      : [];
    // Neueste Mail zuerst: nach Erstellungsdatum absteigend. Mails ohne Datum
    // (Altbestand) landen am Ende, in ihrer urspruenglichen Reihenfolge
    // (stabile Sortierung bei Gleichstand).
    const sorted = fromReport
      .map((e, idx) => ({ e, idx }))
      .sort((a, b) => {
        const ca = a.e.createdAt || 0;
        const cb = b.e.createdAt || 0;
        if (cb !== ca) return cb - ca;
        return a.idx - b.idx;
      })
      .map((x) => x.e);
    return sorted.length ? sorted : [{ id: makeEmailId(), subject: '', body: '', reviews: [], createdAt: Date.now() }];
  });
  // E-Mail-DNA-Setup: optionales Quiz, standardmäßig leer (vom Kunden auszufüllen).
  const [emailSetup, setEmailSetup] = useState(() => normalizeEmailSetup(report.emailSetup));
  // Kick-Off-Checkliste (Setup-Details): editierbar, persistiert in report.kickoff.
  const [kickoff, setKickoff] = useState(() => normalizeKickoff(report.kickoff));
  // Monatliche Reportings (im Rechnungstakt). Quelle: getResearchReport (gemergte
  // gespeicherte + aus dem Vertrag berechnete Perioden). Lokaler State, damit eine
  // Einreichung sofort sichtbar ist, ohne die Seite neu zu laden.
  const [monthlyReportings, setMonthlyReportings] = useState(() => (Array.isArray(initialReportings) ? initialReportings : []));
  useEffect(() => { setMonthlyReportings(Array.isArray(initialReportings) ? initialReportings : []); }, [initialReportings]);
  const [warmup, setWarmup] = useState(initialWarmup || null);
  useEffect(() => { setWarmup(initialWarmup || null); }, [initialWarmup]);
  // Übernahme der in der Experimentierphase hochgeladenen Texte in die
  // Account-Setup-E-Mail-Texte: Sobald ein Warm-up-Tag Texte enthält, die noch
  // nicht in der E-Mail-Liste sind (Dedup über die stabile id), werden sie mit
  // ihrem Tag oben einsortiert. Sie werden dann normal mitgespeichert und sind
  // im Account Setup zu bewerten.
  useEffect(() => {
    const days = warmup && warmup.days;
    if (!days || typeof days !== 'object') return;
    const incoming = [];
    Object.values(days).forEach((d) => {
      const txts = Array.isArray(d && d.uploadedTexts) ? d.uploadedTexts : [];
      txts.forEach((t) => {
        if (t && t.id && ((t.subject || '').trim() || (t.body || '').trim())) {
          incoming.push({
            id: t.id,
            subject: t.subject || '',
            body: t.body || '',
            createdAt: typeof t.at === 'number' ? t.at : Date.now(),
            tag: t.tag || experimentTextTag(d.dayIndex),
          });
        }
      });
    });
    if (!incoming.length) return;
    setEmails((list) => {
      const known = new Set(list.map((e) => e.id).filter(Boolean));
      const toAdd = incoming
        .filter((t) => !known.has(t.id))
        .map((t) => ({ id: t.id, subject: t.subject, body: t.body, reviews: [], createdAt: t.createdAt, source: 'experiment', tag: t.tag }));
      if (!toAdd.length) return list;
      return [...toAdd, ...list];
    });
  }, [warmup]);
  // SalesGod / WorldDB-Export: Optionen + zuletzt vorbereiteter Payload (Entwurf).
  // Persistiert in report.worldDbSetup, damit ein fehlgeschlagener Versand erneut
  // gesendet werden kann. Der Account-Key wird NICHT hier gespeichert.
  const [worldDb, setWorldDb] = useState(() => {
    const w = (report.worldDbSetup && typeof report.worldDbSetup === 'object') ? report.worldDbSetup : {};
    const o = (w.options && typeof w.options === 'object') ? w.options : {};
    return {
      options: {
        bodyMin: typeof o.bodyMin === 'number' ? o.bodyMin : 7,
        subjectMin: typeof o.subjectMin === 'number' ? o.subjectMin : 7,
        skipLeadResearch: o.skipLeadResearch !== false,
        activateCloneTemplate: o.activateCloneTemplate !== false,
        skipTemplates: o.skipTemplates === true,
        forceFullSetup: o.forceFullSetup === true,
      },
      payload: w.payload && typeof w.payload === 'object' ? w.payload : null,
      draftSavedAt: typeof w.draftSavedAt === 'number' ? w.draftSavedAt : 0,
      lastSentAt: typeof w.lastSentAt === 'number' ? w.lastSentAt : 0,
      lastRunId: typeof w.lastRunId === 'string' ? w.lastRunId : '',
      lastStatus: typeof w.lastStatus === 'string' ? w.lastStatus : '',
      lastError: typeof w.lastError === 'string' ? w.lastError : '',
    };
  });
  const [salesGodOpen, setSalesGodOpen] = useState(false);
  // Entwurf/Versand-Ergebnis in den Report mergen → Auto-Save persistiert es.
  const handleWorldDbDraft = useCallback((partial) => {
    setWorldDb((prev) => ({ ...prev, ...partial, options: partial.options || prev.options }));
  }, []);
  const [picker, setPicker] = useState(false);
  // Ein-/ausfahrbare Navigation: auf Desktop offen, auf Mobile als Drawer geschlossen.
  const [navOpen, setNavOpen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth > 900 : true
  );
  // Scroll-Status der Sidebar-Navigation (für sanftes Aus-Faden oben/unten).
  const tabnavRef = useRef(null);
  const [tabScroll, setTabScroll] = useState({ left: false, right: false });
  // Info-Dump der Setup-Details-Ansicht: ein Button im Header öffnet/schließt die Box.
  const [setupImportOpen, setSetupImportOpen] = useState(false);
  // Zähler, mit dem ein Link (z. B. aus dem Status-Tab) gezielt die Setup-Details öffnet.
  const [setupModeSignal, setSetupModeSignal] = useState(initialEmailMode === 'setup' ? 1 : 0);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [savedId, setSavedId] = useState(null);
  // „Gespeichert"-Bestätigung nach 3 Sekunden wieder ausblenden.
  const [savedFlash, setSavedFlash] = useState(false);
  useEffect(() => {
    if (saveState !== 'saved') { setSavedFlash(false); return undefined; }
    setSavedFlash(true);
    const t = setTimeout(() => setSavedFlash(false), 3000);
    return () => clearTimeout(t);
  }, [saveState]);

  // Ist ein Tab (aktuell) gesperrt? Kundenmagnet ist gesperrt, solange nicht als
  // Zusatzpaket freigeschaltet. Im Research-Preview sind zusätzlich Research und
  // Zielgruppen gesperrt (werden erst in der Vollversion generiert).
  const isTabLocked = useCallback((id) => {
    if (id === 'leadmagnet') return !leadMagnetUnlocked;
    if (researchLocked && (id === 'research' || id === 'audience')) return true;
    return false;
  }, [leadMagnetUnlocked, researchLocked]);

  // Sichtbare Tabs. Im Research-Preview zeigen wir eine schlanke Teaser-Navigation:
  // Analyse (frei) plus Research, Zielgruppen und Kundenmagnet als gesperrte Vorschau
  // (goldenes Schloss). Interne Ansichten (Outreach/Reporting) bleiben hier aussen vor.
  const tabs = useMemo(() => {
    if (researchLocked) return [ANALYSE_TAB, ...BASE_TABS];
    // Analyse steht IMMER an Position 1 (sofern sichtbar), danach die Basis-Ansichten
    // und die restlichen Extras (Outreach/Reporting) in ihrer Reihenfolge.
    const extras = addedViews.filter((id) => id !== 'analyse').map((id) => EXTRA_VIEWS[id]).filter(Boolean);
    const base = [...BASE_TABS, ...extras];
    const showAnalyse = addedViews.includes('analyse');
    return showAnalyse ? [ANALYSE_TAB, ...base] : base;
  }, [addedViews, researchLocked]);
  // Im „+"-Menü anbietbare Ansichten. Im Research-Preview nichts (feste Teaser-Nav).
  // Der Analyse-Tab ist sonst nur fürs Team (isAdmin) hinzufügbar.
  const availableViews = researchLocked ? [] : EXTRA_VIEW_ORDER.filter((id) => {
    if (addedViews.includes(id)) return false;
    if (id === 'analyse') return isAdmin;
    return true;
  });
  // Deep-Links (?tab=…), die im Preview nicht existieren (z. B. Outreach/Status),
  // auf den Analyse-Tab zurückführen. Gesperrte Teaser-Tabs bleiben aufrufbar.
  useEffect(() => {
    if (researchLocked && !tabs.some((t) => t.id === activeTab)) setActiveTab('analyse');
  }, [researchLocked, activeTab, tabs]);
  const activeTabDef =
    tabs.find((t) => t.id === activeTab) ||
    (activeTab === 'pipeline' ? PIPELINE_TAB : activeTab === 'impact' ? IMPACT_TAB : BASE_TABS[0]);

  // Aktueller Pipeline-Stand für die subtile Status-Anzeige im Footer.
  const footSuperStatus = (pipeline && pipeline.superStatus) || DEFAULT_SUPER_STATUS;
  const footStatusCol = getColumn(footSuperStatus);

  // Auf Mobile fährt der Drawer nach einer Auswahl automatisch wieder ein.
  const isMobileNav = () => typeof window !== 'undefined' && window.innerWidth <= 900;
  const selectTab = (id) => {
    setActiveTab(id);
    if (isMobileNav()) setNavOpen(false);
  };

  // Bei jedem Tab-Wechsel an den Seitenanfang springen (ohne Animation), damit man
  // nicht mitten in der vorherigen Ansicht landet. Das Dokument selbst scrollt.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [activeTab]);
  const addView = (id) => {
    // Nur bekannte, noch nicht angehängte Zusatz-Ansichten zulassen. Die
    // Dedup-Prüfung läuft im Functional-Update gegen den aktuellen State,
    // damit schnelle Doppelklicks keine Duplikate erzeugen.
    if (!EXTRA_VIEW_ORDER.includes(id)) return;
    let added = false;
    setAddedViews((prev) => {
      if (prev.includes(id)) return prev;
      added = true;
      return [...prev, id];
    });
    setActiveTab(id);
    setPicker(false);
    if (added && isMobileNav()) setNavOpen(false);
  };
  const removeView = (id) => {
    setAddedViews((v) => v.filter((x) => x !== id));
    setActiveTab((cur) => (cur === id ? 'research' : cur));
  };
  // Öffnet das Account Setup direkt auf den Setup-Details (z. B. über den Link
  // im Status-Tab). Stellt sicher, dass die Ansicht angehängt und aktiv ist.
  const openSetup = () => {
    setAddedViews((v) => (v.includes('email') ? v : [...v, 'email']));
    setActiveTab('email');
    setSetupModeSignal((n) => n + 1);
    if (isMobileNav()) setNavOpen(false);
  };

  // Sanftes Aus-Faden der Sidebar-Navigation oben/unten je nach Scroll-Position.
  // left = oben überläuft, right = unten überläuft (Variablennamen beibehalten).
  const updateTabScroll = useCallback(() => {
    const el = tabnavRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    setTabScroll({
      left: el.scrollTop > 1,
      right: el.scrollTop < max - 1,
    });
  }, []);

  useEffect(() => {
    updateTabScroll();
    const el = tabnavRef.current;
    if (!el) return undefined;
    window.addEventListener('resize', updateTabScroll);
    return () => window.removeEventListener('resize', updateTabScroll);
  }, [updateTabScroll, tabs.length]);

  // KI-gestützter Bulk-Import der Setup-Anmerkungen: Info-Dump an die Cloud
  // Function schicken, die ihn semantisch in [{title, body}] sortiert. Auth läuft
  // über das in der Session gespeicherte Report-Passwort (oder offenen Report).
  const aiSortNotes = useCallback(async (dumpText) => {
    if (!slug) {
      const err = new Error('Im Demo-Modus ist der KI-Import nicht verfügbar – nutze „Regelbasiert".');
      err.code = 'no-slug';
      throw err;
    }
    let pw = '';
    try { pw = localStorage.getItem(pwStorageKey(slug)) || ''; } catch (e) { /* ignore */ }
    const token = await getAdminToken();
    const resp = await fetch(NOTES_FN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ slug, password: pw, dump: dumpText }),
    });
    if (!resp.ok) {
      const d = await resp.json().catch(() => ({}));
      throw new Error(d.error || `KI-Import fehlgeschlagen (HTTP ${resp.status}).`);
    }
    const data = await resp.json();
    return Array.isArray(data.notes)
      ? data.notes.map((n) => ({ title: n.title || '', body: n.body || '' }))
      : [];
  }, [slug]);

  // KI generiert die E-Mail-DNA-Beispieltexte zugeschnitten auf diesen Prospect.
  // Schickt die Quiz-Kategorien + aktuelle Favoriten (für den globalen Referenz-Pool)
  // an die Cloud Function. Auth wie aiSortNotes. Liefert { qid: { value: text } }.
  const generateDnaExamples = useCallback(async () => {
    if (!slug) {
      const err = new Error('Im Demo-Modus ist die KI-Generierung nicht verfügbar.');
      err.code = 'no-slug';
      throw err;
    }
    let pw = '';
    try { pw = localStorage.getItem(pwStorageKey(slug)) || ''; } catch (e) { /* ignore */ }
    // Reine Setup-/Kampagnen-Fragen (Sprache, Ziel, Grußformel) brauchen keine
    // KI-Stilbeispiele – nur die Voice-/Stil-Fragen werden generiert.
    const categories = EMAIL_DNA_QUESTIONS.filter((q) => !q.noAiExamples).map((q) => ({
      id: q.id,
      label: q.label,
      hint: q.hint,
      options: q.options.map((o) => ({ value: o.value, label: o.label, desc: o.desc, example: o.example })),
    }));
    const favorites = Array.isArray(emailSetup.favorites) ? emailSetup.favorites : [];
    const token = await getAdminToken();
    const resp = await fetch(DNA_FN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ slug, password: pw, categories, favorites }),
    });
    if (!resp.ok) {
      const d = await resp.json().catch(() => ({}));
      throw new Error(d.error || `Generierung fehlgeschlagen (HTTP ${resp.status}).`);
    }
    const data = await resp.json();
    return (data && data.examples && typeof data.examples === 'object') ? data.examples : {};
  }, [slug, emailSetup.favorites]);

  // KI schätzt auf Knopfdruck die Größe der aktuell definierten Zielgruppe.
  // Bekommt die vom Nutzer pro Lauf gesetzten Eingrenzungen (Regionen + gewünschte
  // Firmengrößen) plus alle aktuellen (auch ungespeicherten) Segmente und gibt eine
  // Spanne potenzieller Kunden + 3 Erweiterungs-Ideen zurück. Auth wie aiSortNotes.
  const estimateAudienceSize = useCallback(async ({ locations, companySizes }) => {
    if (!slug) {
      const err = new Error('Im Demo-Modus ist die KI-Schätzung nicht verfügbar.');
      err.code = 'no-slug';
      throw err;
    }
    let pw = '';
    try { pw = localStorage.getItem(pwStorageKey(slug)) || ''; } catch (e) { /* ignore */ }
    const token = await getAdminToken();
    const resp = await fetch(SIZE_FN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({
        slug,
        password: pw,
        locations,
        companySizes,
        audience: audienceStateToReport(audience),
        company: { name: companyName, industry: companyIndustry, audience: companyAudience },
      }),
    });
    if (!resp.ok) {
      const d = await resp.json().catch(() => ({}));
      throw new Error(d.error || `Schätzung fehlgeschlagen (HTTP ${resp.status}).`);
    }
    const data = await resp.json();
    if (!data || !data.result) throw new Error('Schätzung fehlgeschlagen.');
    return data.result;
  }, [slug, audience, companyName, companyIndustry, companyAudience]);

  // Datei-Upload für ein Setup-Detail-Feld (z. B. Referenztexte, Signatur).
  // Lädt die Datei als base64 zur Cloud Function, die sie in Firebase Storage
  // ablegt und eine URL zurückgibt. Auth wie aiSortNotes (Admin-Token oder
  // gespeichertes Report-Passwort). Liefert das Attachment-Objekt zurück.
  const uploadAttachment = useCallback(async (file) => {
    if (!slug) {
      const err = new Error('Im Demo-Modus sind Datei-Uploads nicht verfügbar.');
      err.code = 'no-slug';
      throw err;
    }
    if (!file) throw new Error('Keine Datei ausgewählt.');
    if (file.size > ATTACH_MAX_BYTES) throw new Error('Datei zu groß (max. 5 MB).');
    const dataBase64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden.'));
      reader.readAsDataURL(file);
    });
    let pw = '';
    try { pw = localStorage.getItem(pwStorageKey(slug)) || ''; } catch (e) { /* ignore */ }
    const token = await getAdminToken();
    const resp = await fetch(UPLOAD_FN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ slug, password: pw, fileName: file.name, contentType: file.type, dataBase64 }),
    });
    if (!resp.ok) {
      const d = await resp.json().catch(() => ({}));
      throw new Error(d.error || `Upload fehlgeschlagen (HTTP ${resp.status}).`);
    }
    const data = await resp.json();
    if (!data || !data.attachment || !data.attachment.url) throw new Error('Upload fehlgeschlagen.');
    return data.attachment;
  }, [slug]);

  // Kundenmagnet: weltweiten Konkurrenz-Audit per KI erzeugen (admin-only, Cloud
  // Function prüft den Admin-Token serverseitig). Speichert serverseitig und gibt
  // das Audit-Objekt zurück, das wir direkt in den State übernehmen.
  const generateLeadMagnetAuditFn = useCallback(async (discovery, sections) => {
    if (!slug) throw new Error('Im Demo-Modus ist der Audit nicht verfügbar.');
    const token = await getAdminToken();
    if (!token) throw new Error('Nur für das eingeloggte Team verfügbar.');
    const resp = await fetch(LEADMAGNET_AUDIT_FN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ slug, discovery: discovery || {}, ...(Array.isArray(sections) && sections.length ? { sections } : {}) }),
    });
    if (!resp.ok) {
      const d = await resp.json().catch(() => ({}));
      throw new Error(d.error || `Audit fehlgeschlagen (HTTP ${resp.status}).`);
    }
    const data = await resp.json();
    if (!data || !data.audit) throw new Error('Audit fehlgeschlagen.');
    setLeadMagnetAudit(data.audit);
    return data.audit;
  }, [slug]);

  // KI-Richtungsvorschlaege (guenstiges Modell): liest den Research-Kontext und liefert
  // kurze, waehlbare Richtungen. Das Team klickt Favoriten, die dann die Generierung steuern.
  const generateLeadMagnetDirectionsFn = useCallback(async () => {
    if (!slug) throw new Error('Im Demo-Modus nicht verfügbar.');
    const token = await getAdminToken();
    if (!token) throw new Error('Nur für das eingeloggte Team verfügbar.');
    const resp = await fetch(LEADMAGNET_DIRECTIONS_FN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ slug }),
    });
    if (!resp.ok) {
      const d = await resp.json().catch(() => ({}));
      throw new Error(d.error || `Richtungsvorschläge fehlgeschlagen (HTTP ${resp.status}).`);
    }
    const data = await resp.json();
    return Array.isArray(data.directions) ? data.directions : [];
  }, [slug]);

  // Kundenmagnet: KI-Vorschaubild für einen Magneten erzeugen (admin-only). Liefert
  // ein Attachment ({ name, url, type }) zurück, das wir wie einen normalen Upload
  // in den Magneten übernehmen. Stil ist serverseitig fest verdrahtet (konsistent).
  const generateLeadMagnetImageFn = useCallback(async ({ title, description } = {}) => {
    if (!slug) throw new Error('Im Demo-Modus ist die Bild-Generierung nicht verfügbar.');
    const token = await getAdminToken();
    if (!token) throw new Error('Nur für das eingeloggte Team verfügbar.');
    const resp = await fetch(GENERATE_IMAGE_FN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ slug, title: title || '', description: description || '' }),
    });
    if (!resp.ok) {
      const d = await resp.json().catch(() => ({}));
      throw new Error(d.error || `Bild-Generierung fehlgeschlagen (HTTP ${resp.status}).`);
    }
    const data = await resp.json();
    if (!data || !data.attachment || !data.attachment.url) throw new Error('Bild-Generierung fehlgeschlagen.');
    return data.attachment;
  }, [slug]);

  // Monatliches Reporting einreichen (passwortgeschützte Cloud Function). Schreibt
  // das Reporting serverseitig in den reportings-Knoten; Extra-Termine fließen über
  // die Admin-Reconciliation automatisch in Rechnung und Team-Auszahlung.
  const submitMonthlyReporting = useCallback(async (periodKey, payload) => {
    if (!slug) throw new Error('Im Demo-Modus ist das Reporting nicht verfügbar.');
    let pw = '';
    try { pw = localStorage.getItem(pwStorageKey(slug)) || ''; } catch (e) { /* ignore */ }
    const token = await getAdminToken();
    const resp = await fetch(REPORTING_FN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ slug, password: pw, periodKey, ...payload }),
    });
    if (!resp.ok) {
      const d = await resp.json().catch(() => ({}));
      throw new Error(d.error || `Reporting konnte nicht gespeichert werden (HTTP ${resp.status}).`);
    }
    const data = await resp.json();
    const rep = data && data.reporting;
    if (rep && rep.periodKey) {
      setMonthlyReportings((prev) => {
        const rest = (Array.isArray(prev) ? prev : []).filter((r) => r.periodKey !== rep.periodKey);
        return [rep, ...rest].sort((a, b) => (b.availableAt || 0) - (a.availableAt || 0));
      });
    }
    return rep;
  }, [slug]);

  // Warm-up starten (Tagesziel einmalig festlegen). Optionales delayMs verzögert
  // den Start (z. B. 24 h „offizieller Startschuss" aus der Kick-off-Phase) – das
  // erste Reporting wird erst nach Ablauf freigeschaltet. Im Demo-Modus (kein slug)
  // nur lokaler State, damit die Ansicht trotzdem vorschaubar bleibt.
  const startWarmup = useCallback(async (dailyTarget, delayMs = 0) => {
    const target = Math.max(0, Math.round(Number(dailyTarget) || 0));
    const delay = Math.max(0, Math.round(Number(delayMs) || 0));
    if (!slug) {
      setWarmup((w) => ({ ...(w || {}), dailyTarget: target, startAt: (w && w.startAt) || (Date.now() + delay), status: 'active', days: (w && w.days) || {} }));
      return;
    }
    let pw = '';
    try { pw = localStorage.getItem(pwStorageKey(slug)) || ''; } catch (e) { /* ignore */ }
    const token = await getAdminToken();
    const resp = await fetch(WARMUP_FN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ slug, password: pw, action: 'start', dailyTarget: target, delayMs: delay }),
    });
    if (!resp.ok) {
      const d = await resp.json().catch(() => ({}));
      throw new Error(d.error || `Warm-up konnte nicht gestartet werden (HTTP ${resp.status}).`);
    }
    const data = await resp.json();
    if (data && data.warmup) setWarmup(data.warmup);
    return data && data.warmup;
  }, [slug]);

  // Einen Warm-up-Tag speichern/aktualisieren. Optimistisches lokales Update +
  // (falls slug) Persistenz über die Cloud Function.
  const submitWarmupDay = useCallback(async (dateKey, payload) => {
    if (!slug) {
      setWarmup((w) => {
        const base = w || { dailyTarget: WARMUP_TARGET_DEFAULT, startAt: Date.now(), status: 'active' };
        const days = { ...(base.days || {}) };
        days[dateKey] = { ...(days[dateKey] || {}), ...payload, dateKey, submittedAt: Date.now() };
        return { ...base, days };
      });
      return;
    }
    let pw = '';
    try { pw = localStorage.getItem(pwStorageKey(slug)) || ''; } catch (e) { /* ignore */ }
    const token = await getAdminToken();
    const resp = await fetch(WARMUP_FN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ slug, password: pw, action: 'day', dateKey, ...payload }),
    });
    if (!resp.ok) {
      const d = await resp.json().catch(() => ({}));
      throw new Error(d.error || `Warm-up-Tag konnte nicht gespeichert werden (HTTP ${resp.status}).`);
    }
    const data = await resp.json();
    if (data && data.warmup) setWarmup(data.warmup);
    return data && data.warmup;
  }, [slug]);

  // Experimentierphase zurücksetzen (deaktivieren): löscht Start, Ziel und alle
  // Tage, sodass sie komplett neu gestartet werden kann. Optimistisch lokal +
  // (falls slug) serverseitig über die Cloud Function (action „reset").
  const resetWarmup = useCallback(async () => {
    if (!slug) { setWarmup(null); return; }
    let pw = '';
    try { pw = localStorage.getItem(pwStorageKey(slug)) || ''; } catch (e) { /* ignore */ }
    const token = await getAdminToken();
    const resp = await fetch(WARMUP_FN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ slug, password: pw, action: 'reset' }),
    });
    if (!resp.ok) {
      const d = await resp.json().catch(() => ({}));
      throw new Error(d.error || `Launch Phase konnte nicht zurückgesetzt werden (HTTP ${resp.status}).`);
    }
    setWarmup(null);
  }, [slug]);

  // Auswahl-Modal per Escape schließen (Backdrop-Klick schließt direkt im JSX).
  useEffect(() => {
    if (!picker) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setPicker(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [picker]);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = `${report.product} ${report.reportType} – ${report.company.name}`;
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow, noarchive, nosnippet';
    document.head.appendChild(meta);
    return () => {
      document.title = prevTitle;
      if (meta.parentNode) meta.parentNode.removeChild(meta);
    };
  }, [report]);

  const derived = useMemo(() => {
    const cfg = { ...config, valueBasedPricingEnabled: true };
    const proj = computeProjection(cfg);
    const deal = clamp(cfg.revenuePerClose, 100, 100000);
    const margin = clamp(cfg.profitMargin, 0.1, 1.0);

    // Neukunden/Jahr je Szenario folgen den oben gewählten Terminen × "Abschlüsse pro Termin".
    // Dieser Slider (closeRate) treibt AUSSCHLIESSLICH den ROI / die Szenarien – NICHT den
    // Termin-Preis (der nutzt eine feste Referenz-Quote im pricingEngine).
    const apptsPerYear = proj.expectedAppts * 12;
    const closeRate = clamp(cfg.closeRate ?? 0.30, 0.01, 1.0);
    const rates = {
      conservative: clamp(closeRate * SCENARIO_SPREAD.conservative, 0, 1),
      realistic: closeRate,
      strong: clamp(closeRate * SCENARIO_SPREAD.strong, 0, 1),
    };
    const scenarioDefs = [
      { name: 'Konservativ', closes: Math.round(apptsPerYear * rates.conservative) },
      { name: 'Realistisch', closes: Math.round(apptsPerYear * rates.realistic), highlight: true },
      { name: 'Stark', closes: Math.round(apptsPerYear * rates.strong) },
    ];
    const scenarios = scenarioDefs.map((s) => ({
      ...s,
      revenue: s.closes * deal,
      profit: s.closes * deal * margin,
    }));

    const baseCloses = scenarioDefs[1].closes;
    const realisticClosesPerMonth = proj.expectedAppts * closeRate;
    const baseRevenue = baseCloses * deal;
    const baseProfit = baseCloses * deal * margin;
    const roiMultiple = baseProfit / proj.investmentYear1;
    const feeToProfitRatio = proj.investmentYear1 / baseProfit;
    const breakEvenCloses = Math.ceil(proj.investmentYear1 / proj.profitPerClose);

    // ── Jahr 2: Retainer-/Bestandseffekt ──
    // Die in Jahr 1 gewonnenen Kunden sind wiederkehrend. Im 2. Jahr bleibt ein
    // fester Anteil erhalten (Retention) – diese Kunden bringen weiter Umsatz/Profit,
    // OHNE dass dafür erneut akquiriert (= Fee gezahlt) werden muss. Daher ist der
    // Bestandsgewinn in Jahr 2 reiner Zusatz-Profit auf die ursprüngliche Investition.
    const RETENTION_Y2 = 0.70; // fest: 70 % bleiben im 2. Jahr (moderater Churn)
    const retainedClosesY2 = Math.round(baseCloses * RETENTION_Y2);
    const retainedRevenueY2 = retainedClosesY2 * deal;
    const retainedProfitY2 = retainedRevenueY2 * margin;
    const twoYearProfit = baseProfit + retainedProfitY2;
    // 2-Jahres-ROI: kumulierter Profit (Jahr 1 + Bestand Jahr 2) gegen die EINMALIGE
    // Investition aus Jahr 1 – Jahr 2 kostet für den reinen Bestand nichts extra.
    const twoYearRoi = twoYearProfit / proj.investmentYear1;

    // Best Case vs Worst Case
    const worstCaseMonths = report.riskFraming?.worstCaseMonths ?? 3;
    const worstCaseCost = worstCaseMonths * proj.monthlyPrice + proj.setup;
    const bestCase = scenarios[2]; // starkes Szenario
    const upsideVsRisk = bestCase.profit / worstCaseCost;

    // ── Dynamischer Fit-Score (2 parallele Systeme) ──
    // 1) Wirtschaftlich (live, Haupttreiber, max 70): ROI-Fit (40) + Kundenwert (30)
    //    -> kann bei schwacher Ökonomie auch sehr niedrig werden.
    // ROI-Fit: sättigende Kurve (abnehmender Grenznutzen) statt hartem Cap, damit auch
    // sehr starke Cases oben noch differenzieren und nicht alle bei ROI ≥ X gleich sind.
    const roiFitScore = clamp(Math.round(40 * (roiMultiple / (roiMultiple + 0.6))), 0, 40);
    const kundenwertScore = clamp(Math.round(30 * (proj.profitPerClose / 6500)), 0, 30);
    // 2) Research (fix, KI-Bonus, max 30): ein paar Zusatzpunkte unabhängig von der Ökonomie
    const researchRows = report.researchScores;
    const researchSum = researchRows.reduce((s, r) => s + r.score, 0);
    const researchMax = researchRows.reduce((s, r) => s + r.max, 0);

    const economicSum = roiFitScore + kundenwertScore;
    const economicMax = 70;
    const totalScore = clamp(economicSum + researchSum, 0, 100);

    const scoreGroups = [
      {
        name: 'Wirtschaftlichkeit (live)',
        dynamic: true,
        sum: economicSum,
        max: economicMax,
        rows: [
          { label: 'Wirtschaftlicher ROI-Fit', score: roiFitScore, max: 40, note: 'Reagiert live auf ROI & Deal-Ökonomie.' },
          { label: 'Kundenwert pro Abschluss', score: kundenwertScore, max: 30, note: 'Profit pro Neukunde im Verhältnis zum Marktstandard.' },
        ],
      },
      {
        name: 'Research-Fit (KI)',
        dynamic: false,
        sum: researchSum,
        max: researchMax,
        rows: researchRows,
      },
    ];

    return {
      ...proj,
      revenuePerClose: deal,
      profitMargin: margin,
      scenarios,
      baseCloses,
      realisticClosesPerMonth,
      baseRevenue,
      baseProfit,
      roiMultiple,
      feeToProfitRatio,
      breakEvenCloses,
      retentionY2: RETENTION_Y2,
      retainedClosesY2,
      retainedRevenueY2,
      retainedProfitY2,
      twoYearProfit,
      twoYearRoi,
      worstCaseMonths,
      worstCaseCost,
      bestCase,
      upsideVsRisk,
      payoffMax: Math.max(proj.investmentYear1, baseProfit),
      totalScore,
      scoreGroups,
      verdict: verdictForScore(totalScore),
    };
  }, [config, report.researchScores]);

  // ── Save ──
  //  Live-Seite (mit slug): Änderungen (Config + Zielgruppen-Chips) werden zurück
  //  in GENAU diesen Report unter salesgod-research/reports/<slug> gespeichert.
  //  Demo (ohne slug): Snapshot ins Team-Archiv (salesgod-research/entries).
  // Baut aus dem aktuellen Bearbeitungszustand das vollständige Report-Objekt,
  // das in salesgod-research/reports/<slug> persistiert wird. Wird sowohl vom
  // manuellen „Speichern"-Button als auch vom Auto-Save verwendet.
  const buildUpdatedReport = useCallback(() => {
    const nboOffers = (Array.isArray(offer.noBrainerOffers) ? offer.noBrainerOffers : [])
      .map((o) => ({ title: (o.title || '').trim(), text: (o.text || '').trim(), idea: !!o.idea }))
      .filter((o) => o.title || o.text);
    const cleanMagnets = (Array.isArray(magnets) ? magnets : [])
      .map((m) => ({
        id: m.id || makeEmailId(),
        title: (m.title || '').trim(),
        titleVariants: Array.isArray(m.titleVariants)
          ? m.titleVariants.map((t) => String(t).trim()).filter(Boolean).slice(0, 3)
          : [],
        hook: (m.hook || '').trim(),
        description: (m.description || '').trim(),
        // Strukturierte Offer-Foundations (kundenrelevant): am Magnet erhalten, damit
        // sie auf der Karte eigenständig gerendert werden statt im Fließtext zu versumpfen.
        foundations: (m.foundations && typeof m.foundations === 'object')
          ? {
              tangibleResult: String(m.foundations.tangibleResult || '').trim(),
              timeline: String(m.foundations.timeline || '').trim(),
              guarantee: String(m.foundations.guarantee || '').trim(),
              riskReversal: String(m.foundations.riskReversal || '').trim(),
            }
          : { tangibleResult: '', timeline: '', guarantee: '', riskReversal: '' },
        notes: (m.notes || '').trim(),
        type: m.type === 'ideas' ? 'ideas' : 'dfy',
        tags: Array.isArray(m.tags) ? m.tags.map((t) => String(t).trim()).filter(Boolean) : [],
        preview: (m.preview && m.preview.url)
          ? { url: String(m.preview.url), name: String(m.preview.name || ''), type: String(m.preview.type || '') }
          : null,
        attachments: Array.isArray(m.attachments)
          ? m.attachments.filter((a) => a && a.url).map((a) => ({
              name: (String(a.name || '').trim()) || 'Datei',
              url: String(a.url),
              type: String(a.type || ''),
              size: Number(a.size) || 0,
            }))
          : [],
        createdAt: typeof m.createdAt === 'number' && m.createdAt > 0 ? m.createdAt : Date.now(),
      }))
      .filter((m) => m.title || m.description || m.hook || m.attachments.length || (m.preview && m.preview.url));
    const cleanAnalyse = {
      video: (analyse.video || '').trim(),
      companies: (analyse.companies || [])
        .map((c) => ({
          id: c.id || makeAnalyseId(),
          name: (c.name || '').trim(),
          website: (c.website || '').trim(),
          description: (c.description || '').trim(),
          rating: Math.max(0, Math.min(100, Math.round(Number(c.rating) || 0))),
          verified: !!c.verified,
          signals: (Array.isArray(c.signals) ? c.signals : []).map((s) => ({
            text: (s && s.text ? String(s.text) : '').trim(),
            date: (s && s.date ? String(s.date) : '').trim(),
            source: (s && s.source ? String(s.source) : '').trim(),
            url: (s && s.url ? String(s.url) : '').trim(),
          })).filter((s) => s.text),
        }))
        .filter((c) => c.name || c.website || c.description),
      emails: (() => {
        const list = (analyse.emails || []).map((e, i) => ({
          subject: (e.subject || '').trim(),
          body: (e.body || '').trim(),
          toName: (e.toName || '').trim(),
          toCompany: (e.toCompany || '').trim(),
          kind: e.kind === 'followup' ? 'followup' : (e.kind === 'intro' ? 'intro' : (i === 0 ? 'intro' : 'followup')),
        })).slice(0, MAX_ANALYSE_EMAILS);
        while (list.length < 2) list.push({ subject: '', body: '', toName: '', toCompany: '', kind: list.length === 0 ? 'intro' : 'followup' });
        return list;
      })(),
    };
    return {
      ...report,
      config,
      addedViews,
      worldDbSetup: worldDb,
      analyse: cleanAnalyse,
      leadMagnet: { ...(report.leadMagnet || {}), magnets: cleanMagnets },
      emails: emails
        .map((e) => ({
          id: e.id || makeEmailId(),
          subject: (e.subject || '').trim(),
          body: e.body || '',
          createdAt: typeof e.createdAt === 'number' && e.createdAt > 0 ? e.createdAt : Date.now(),
          ...(e.source ? { source: e.source } : {}),
          ...(e.tag ? { tag: e.tag } : {}),
          reviews: (Array.isArray(e.reviews) ? e.reviews : [])
            .filter((r) => r && (r.rating > 0 || r.subjectRating > 0 || (r.comment || '').trim()))
            .map((r) => ({
              id: r.id || makeReviewId(),
              author: (r.author || '').trim(),
              rating: Number.isFinite(r.rating) ? r.rating : 0,
              subjectRating: Number.isFinite(r.subjectRating) ? r.subjectRating : 0,
              comment: (r.comment || '').trim(),
              at: typeof r.at === 'number' ? r.at : Date.now(),
            })),
        }))
        .filter((e) => e.subject || (e.body || '').trim() || e.reviews.length),
      emailSetup: {
        ...EMAIL_DNA_QUESTIONS.reduce((acc, q) => ({ ...acc, [q.id]: (emailSetup[q.id] || '').trim() }), {}),
        dos: (emailSetup.dos || '').trim(),
        donts: (emailSetup.donts || '').trim(),
        proof: (emailSetup.proof || '').trim(),
        examples: (emailSetup.examples && typeof emailSetup.examples === 'object') ? emailSetup.examples : {},
        favorites: Array.isArray(emailSetup.favorites)
          ? emailSetup.favorites
              .filter((f) => f && typeof f.text === 'string' && f.text.trim())
              .map((f) => ({ category: String(f.category || ''), option: String(f.option || ''), label: String(f.label || ''), text: String(f.text).trim() }))
              .slice(0, 60)
          : [],
      },
      kickoff: {
        // checklist liegt jetzt im Kundenobjekt (Team Dashboard, geteilte Quelle) –
        // nicht mehr im Report. Hier nur noch benötigte Infos + Anmerkungen.
        details: (kickoff.details || [])
          .map((d) => ({
            label: (d.label || '').trim(),
            prompt: (d.prompt || '').trim(),
            type: d.type === 'pills' ? 'pills' : 'text',
            value: (d.value || '').trim(),
            tags: Array.isArray(d.tags) ? d.tags.map((t) => String(t).trim()).filter(Boolean) : [],
            attachments: Array.isArray(d.attachments)
              ? d.attachments
                  .filter((a) => a && a.url)
                  .map((a) => ({
                    name: (String(a.name || '').trim()) || 'Datei',
                    url: String(a.url),
                    type: String(a.type || ''),
                    size: Number(a.size) || 0,
                  }))
              : [],
          }))
          .filter((d) => d.label || d.prompt || d.value || (d.tags && d.tags.length) || (d.attachments && d.attachments.length)),
        notes: (kickoff.notes || [])
          .map((n) => ({
            title: (n.title || '').trim(),
            body: (n.body || '').trim(),
            ...(n.stage ? { stage: String(n.stage).trim() } : {}),
            ...(typeof n.droppedAt === 'number' ? { droppedAt: n.droppedAt } : {}),
          }))
          .filter((n) => n.title || n.body),
      },
      company: {
        ...report.company,
        name: (companyName || '').trim() || report.company.name,
        industry: (companyIndustry || '').trim(),
        audience: (companyAudience || '').trim(),
        website: (website || '').trim(),
      },
      research: {
        ...report.research,
        angles: angles
          .map((a) => ({ title: (a.title || '').trim(), body: (a.body || '').trim() }))
          .filter((a) => a.title || a.body),
        targetResearch: { ...report.research.targetResearch, ...chipsToArrays(research) },
        audienceResearch: audienceStateToReport(audience),
        offerAnalysis: {
          whatIsSold: (offer.whatIsSold || '').trim(),
          usp: {
            text: ((offer.usp && offer.usp.text) || '').trim(),
            points: ((offer.usp && offer.usp.points) || []).map((s) => (s || '').trim()).filter(Boolean),
          },
          salesSetup: {
            have: ((offer.salesSetup && offer.salesSetup.have) || []).map((s) => (s || '').trim()).filter(Boolean),
            missing: ((offer.salesSetup && offer.salesSetup.missing) || []).map((s) => (s || '').trim()).filter(Boolean),
          },
          noBrainerOffers: nboOffers,
          // Back-compat: erstes nicht-Idee-Angebot als String mitspeichern.
          noBrainerOffer: (nboOffers.find((o) => !o.idea) || nboOffers[0] || {}).text || '',
        },
      },
    };
  }, [report, config, addedViews, emails, emailSetup, kickoff, website, companyName, companyIndustry, companyAudience, angles, research, audience, offer, worldDb, magnets, analyse]);

  // Schreibt den aktuellen Report-Zustand zurück in die Live-Seite (nur mit slug).
  const persistSlugReport = useCallback(async () => {
    const updatedReport = buildUpdatedReport();
    await update(ref(database, `salesgod-research/reports/${slug}`), {
      slug,
      companyName: updatedReport.company.name,
      json: JSON.stringify(updatedReport).slice(0, 250000),
      updatedAt: serverTimestamp(),
    });
    return updatedReport;
  }, [buildUpdatedReport, slug]);

  const handleSave = async () => {
    if (saveState === 'saving') return;
    setSaveState('saving');

    if (slug) {
      try {
        await persistSlugReport();
        setSavedId(slug);
        setSaveState('saved');
      } catch (e) {
        console.error('research page save error', e);
        setSaveState('error');
      }
      return;
    }

    try {
      const entriesRef = ref(database, 'salesgod-research/entries');
      const res = await push(entriesRef, {
        product: report.product,
        reportType: report.reportType,
        company: {
          name: (companyName || '').trim() || report.company.name,
          industry: (companyIndustry || '').trim() || report.company.industry,
          audience: (companyAudience || '').trim() || report.company.audience,
        },
        config: {
          revenuePerClose: config.revenuePerClose,
          profitMargin: config.profitMargin,
          tier: config.tier,
          valueBasedPricingEnabled: true,
          setup: config.setup,
          quality: config.quality,
          closeRate: config.closeRate,
          apptValueMode: config.apptValueMode,
          apptValueManual: config.apptValueManual,
          monthlyPriceMode: config.monthlyPriceMode,
          monthlyPriceManual: config.monthlyPriceManual,
          expectedApptsOverride: derived.expectedAppts,
        },
        appointments: {
          expectedPerMonth: derived.expectedAppts,
          billablePerMonth: derived.billableApptsPerMonth,
        },
        snapshot: {
          totalScore: derived.totalScore,
          verdict: derived.verdict.label,
          monthlyPrice: derived.monthlyPrice,
          appointmentValue: derived.appointmentValue,
          investmentYear1: derived.investmentYear1,
          roiMultiple: Number(derived.roiMultiple.toFixed(2)),
          profitPerClose: derived.profitPerClose,
        },
        researchSelection: JSON.stringify(research).slice(0, 8000),
        createdAt: serverTimestamp(),
      });
      setSavedId(res.key);
      setSaveState('saved');
    } catch (e) {
      console.error('research save error', e);
      setSaveState('error');
    }
  };

  // ── Auto-Save (nur Live-Seiten mit slug) ──
  // Jede Änderung (Config, Research, hinzugefügte Ansichten, Setup-Details, …)
  // wird nach kurzer Ruhepause automatisch zurück in den Report geschrieben, damit
  // z. B. neu hinzugefügte Ansichten von da an dauerhaft oben erhalten bleiben.
  const autoSaveReady = useRef(false);
  useEffect(() => {
    // Demo-Modus (ohne slug): kein Auto-Save, nur Save-Status zurücksetzen.
    if (!slug) {
      setSaveState('idle');
      setSavedId(null);
      return undefined;
    }
    // Initiales Laden überspringen – sonst würde der frisch geladene Report
    // sofort wieder gespeichert werden.
    if (!autoSaveReady.current) {
      autoSaveReady.current = true;
      return undefined;
    }
    setSaveState('saving');
    const timer = setTimeout(async () => {
      try {
        await persistSlugReport();
        setSavedId(slug);
        setSaveState('saved');
      } catch (e) {
        console.error('research page auto-save error', e);
        setSaveState('error');
      }
    }, 1200);
    return () => clearTimeout(timer);
  }, [config, research, angles, offer, audience, website, companyName, companyIndustry, companyAudience, addedViews, emails, emailSetup, kickoff, worldDb, magnets, analyse, slug, persistSlugReport]);

  return (
    <div className={`rp-wrapper ${navOpen ? 'rp-nav-open' : 'rp-nav-closed'}`}>
      <button
        type="button"
        className="rp-nav-fab"
        onClick={() => setNavOpen(true)}
        aria-label="Navigation öffnen"
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
      </button>

      <div className="rp-nav-backdrop" onClick={() => setNavOpen(false)} aria-hidden="true" />

      <aside className="rp-sidebar">
        <div className="rp-sidebar-head">
          <img src="/img/sg_bonusclub_combo_white.png" alt="SalesGod · BonusClub" className="rp-sidebar-logo" />
          <button
            type="button"
            className="rp-nav-collapse"
            onClick={() => setNavOpen(false)}
            aria-label="Navigation einklappen"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
        </div>

        <nav
          className={`rp-sidenav ${tabScroll.left ? 'rp-sidenav-fade-t' : ''} ${tabScroll.right ? 'rp-sidenav-fade-b' : ''}`}
          ref={tabnavRef}
          onScroll={updateTabScroll}
        >
          {tabs.map((tab, i) => {
            const isExtra = !!EXTRA_VIEWS[tab.id];
            return (
              <button
                key={tab.id}
                className={`rp-tabnav-btn ${activeTab === tab.id ? 'rp-tabnav-active' : ''} ${isExtra ? 'rp-tabnav-extra' : ''}`}
                onClick={() => selectTab(tab.id)}
              >
                <span className="rp-tabnav-text">
                  <span className="rp-tabnav-label">{tab.label}</span>
                  <span className="rp-tabnav-desc">{tab.desc}</span>
                </span>
                <span className="rp-tabnav-end">
                  {isTabLocked(tab.id) ? (
                    <span className="rp-tabnav-idx rp-tabnav-lock" title="Gesperrt – wird in der Vollversion freigeschaltet" aria-label="Gesperrt">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="4" y="10.5" width="16" height="10" rx="2" />
                        <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
                      </svg>
                    </span>
                  ) : (
                    <span className="rp-tabnav-idx">{String(i + 1).padStart(2, '0')}</span>
                  )}
                  {isExtra ? (
                    <span
                      className="rp-tabnav-remove"
                      role="button"
                      tabIndex={0}
                      title="Ansicht ausblenden"
                      aria-label="Ansicht ausblenden"
                      onClick={(e) => { e.stopPropagation(); removeView(tab.id); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); removeView(tab.id); } }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <path d="M6.61 6.61A18.45 18.45 0 0 0 2 12s3 8 10 8a9.12 9.12 0 0 0 5.39-1.61" />
                        <line x1="2" y1="2" x2="22" y2="22" />
                      </svg>
                    </span>
                  ) : (
                    <svg className="rp-tabnav-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  )}
                </span>
              </button>
            );
          })}

          {availableViews.length > 0 && (
            <div className="rp-addview">
              <button
                className={`rp-addview-btn ${picker ? 'rp-addview-btn-open' : ''}`}
                onClick={() => setPicker((o) => !o)}
                title="Ansicht hinzufügen"
                aria-label="Ansicht hinzufügen"
                aria-haspopup="dialog"
                aria-expanded={picker}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                <span className="rp-addview-btn-label">Ansicht hinzufügen</span>
              </button>
              {picker && createPortal(
                <div
                  className="rp-addview-overlay"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Ansicht hinzufügen"
                  onClick={() => setPicker(false)}
                >
                  <div className="rp-addview-modal" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="rp-addview-modal-close"
                      onClick={() => setPicker(false)}
                      aria-label="Schließen"
                    >
                      ×
                    </button>
                    <div className="rp-addview-modal-head">
                      <span className="rp-addview-modal-eyebrow">Navigation</span>
                      <h3 className="rp-addview-modal-title">Ansicht hinzufügen</h3>
                      <p className="rp-addview-modal-sub">Wähle eine zusätzliche Ansicht für diesen Report.</p>
                    </div>
                    <div className="rp-addview-modal-opts">
                      {availableViews.map((id) => (
                        <button
                          key={id}
                          type="button"
                          className="rp-addview-opt"
                          onClick={() => addView(id)}
                        >
                          <span className="rp-addview-opt-label">{EXTRA_VIEWS[id].label}</span>
                          <span className="rp-addview-opt-desc">{EXTRA_VIEWS[id].desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>,
                document.body
              )}
            </div>
          )}
        </nav>

        <div className="rp-sidebar-foot">
        {(!slug || saveState === 'saving' || saveState === 'error' || savedFlash) && (
            <button
              type="button"
              className={`rp-foot-save rp-foot-save-${saveState}`}
              onClick={handleSave}
              disabled={saveState === 'saving' || saveState === 'saved'}
              title={slug ? 'Änderungen werden automatisch gespeichert' : 'Aktuelles Szenario im Team-Archiv speichern'}
            >
              <span className="rp-foot-save-dot" />
              <span className="rp-foot-save-text">
                {saveState === 'saving'
                  ? 'Speichert…'
                  : saveState === 'saved'
                  ? 'Gespeichert'
                  : saveState === 'error'
                  ? 'Erneut versuchen'
                  : 'Speichern'}
              </span>
            </button>
          )}
       
          {isAdmin && slug && !researchLocked && (
            <button
              type="button"
              className="rp-foot-salesgod"
              onClick={() => setSalesGodOpen(true)}
              title="Account-Setup nach Salesbot exportieren"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg>
              <span className="rp-foot-salesgod-text">Connect SalesGod</span>
            </button>
          )}
 

          {!researchLocked && (
          <button
            type="button"
            className={`rp-foot-status ${activeTab === 'pipeline' ? 'rp-foot-status-active' : ''}`}
            onClick={() => selectTab('pipeline')}
            title="Zum Status-Verlauf"
            style={{ '--foot-accent': footStatusCol.accent }}
          >
            <span className="rp-foot-status-dot" />
            <span className="rp-foot-status-text">
              <span className="rp-foot-status-label">Status</span>
              <span className="rp-foot-status-value">{isLostSuperStatus(footSuperStatus) ? 'Inaktiv' : footStatusCol.superStatusLabel}</span>
            </span>
            <svg className="rp-foot-status-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
          )}

          {researchLocked && (
            <div className="rp-foot-preview">
              <span className="rp-foot-preview-lock" aria-hidden="true">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="10.5" width="16" height="10" rx="2" />
                  <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
                </svg>
              </span>
              <span className="rp-foot-preview-text">
                <span className="rp-foot-preview-label">Exklusive Vorschau</span>
                <span className="rp-foot-preview-name">für {companyName || report.company.name || 'dich'}</span>
              </span>
            </div>
          )}
        </div>
      </aside>

      <div className="rp-content">

      {activeTab !== 'email' && activeTab !== 'reporting' && activeTab !== 'leadmagnet' && activeTab !== 'analyse' && !(researchLocked && (activeTab === 'research' || activeTab === 'audience')) && (
      <header className="rp-header">
        <div className="rp-header-glow rp-header-glow-1" />
        <div className="rp-header-glow rp-header-glow-2" />
        <div className="rp-header-inner">
          {(activeTab === 'research' || activeTab === 'audience' || activeTab === 'pipeline') && (
            <span className="rp-header-eyebrow">{activeTab === 'audience' ? (companyName || report.company.name) : activeTabDef.label}</span>
          )}
          {activeTab === 'research' ? (
            <h1 className="rp-header-title">
              <InlineEditableText
                value={companyName}
                placeholder="Firmenname…"
                onChange={setCompanyName}
              />
            </h1>
          ) : (
            <h1 className="rp-header-title">{activeTab === 'audience' || activeTab === 'impact' ? activeTabDef.label : (companyName || report.company.name)}</h1>
          )}
          {activeTab === 'research' && (
            <HeaderWebsite value={website} onChange={setWebsite} />
          )}

          {activeTab === 'research' && (
            <div className="rp-header-meta">
              <div className="rp-header-meta-row">
                <span className="rp-header-meta-label">Branche</span>
                <InlineEditableText
                  className="rp-header-meta-value"
                  value={companyIndustry}
                  placeholder="Branche eintragen…"
                  onChange={setCompanyIndustry}
                  multiline
                />
              </div>
              <div className="rp-header-meta-row">
                <span className="rp-header-meta-label">Zielgruppe</span>
                <InlineEditableText
                  className="rp-header-meta-value"
                  value={companyAudience}
                  placeholder="Zielgruppe eintragen…"
                  onChange={setCompanyAudience}
                  multiline
                />
              </div>
            </div>
          )}

          {activeTab === 'impact' && (
            <div className="rp-econ-grid">
              <div className="rp-econ-card rp-econ-card-feature">
                <span className="rp-econ-value">{formatMultiple(derived.roiMultiple)}</span>
                <span className="rp-econ-label">ROI auf Profitbasis · Jahr 1</span>
              </div>
              <div className="rp-econ-card">
                <span className="rp-econ-value">{formatMultiple(derived.upsideVsRisk)}</span>
                <span className="rp-econ-label">Upside im Verhältnis zum Risiko</span>
              </div>
              <div className="rp-econ-card">
                <span className="rp-econ-value" style={{ color: derived.verdict.color }}>
                  {derived.totalScore}<span className="rp-econ-value-suffix">/ 100</span>
                </span>
                <span className="rp-econ-label">Gesamtbewertung · {derived.verdict.label}</span>
              </div>
            </div>
          )}
        </div>
      </header>
      )}

      {/* Live-Configurator: nur in der Impact-Sicht (wirtschaftliche Stellschrauben) */}
      {activeTab === 'impact' && (
        <div className="rp-cfg-wrap">
          <Configurator
            config={config}
            setConfig={setConfig}
            derived={derived}
            defaultConfig={report.config}
          />
        </div>
      )}

        <main className={`rp-main ${(activeTab === 'email' || activeTab === 'reporting' || activeTab === 'leadmagnet' || activeTab === 'analyse' || (researchLocked && (activeTab === 'research' || activeTab === 'audience'))) ? 'rp-main-noheader' : ''} ${activeTab === 'leadmagnet' ? 'rp-main-lm' : ''}`}>
        {activeTab === 'analyse' && <AnalyseTab report={report} companyName={companyName} companyIndustry={companyIndustry} companyAudience={companyAudience} website={website} isAdmin={isAdmin} analyse={analyse} setAnalyse={setAnalyse} />}
        {activeTab === 'impact' && <ImpactTab report={report} derived={derived} />}
        {activeTab === 'research' && (isTabLocked('research')
          ? <LockedPreviewView tab="research" companyName={companyName || report.company.name} />
          : <ResearchTab report={report} angles={angles} setAngles={setAngles} offer={offer} setOffer={setOffer} onOpenImpact={() => selectTab('impact')} />)}
        {activeTab === 'audience' && (isTabLocked('audience')
          ? <LockedPreviewView tab="audience" companyName={companyName || report.company.name} />
          : <AudienceView audience={audience} setAudience={setAudience} report={report} estimateSize={slug ? estimateAudienceSize : null} />)}
        {activeTab === 'leadmagnet' && (researchLocked ? (
          <LockedPreviewView tab="leadmagnet" companyName={companyName || report.company.name} />
        ) : (
          <LeadMagnetView
            isAdmin={isAdmin}
            unlocked={leadMagnetUnlocked}
            magnets={magnets}
            setMagnets={setMagnets}
            audit={leadMagnetAudit}
            generateAudit={slug ? generateLeadMagnetAuditFn : null}
            generateDirections={slug ? generateLeadMagnetDirectionsFn : null}
            uploadAttachment={slug ? uploadAttachment : null}
            generateImage={slug ? generateLeadMagnetImageFn : null}
            companyName={companyName || report.company.name}
            customerPsychology={(report.research && report.research.customerPsychology) || null}
          />
        ))}
        {activeTab === 'reporting' && (
          <ReportingView
            reportings={monthlyReportings}
            submitReporting={slug ? submitMonthlyReporting : null}
            uploadAttachment={slug ? uploadAttachment : null}
            warmup={warmup}
            startWarmup={startWarmup}
            submitWarmupDay={submitWarmupDay}
            resetWarmup={resetWarmup}
          />
        )}
        {activeTab === 'email' && (
          <EmailTextView
            emails={emails}
            setEmails={setEmails}
            setup={emailSetup}
            setSetup={setEmailSetup}
            generateExamples={slug ? generateDnaExamples : null}
            initialMode={initialEmailMode}
            focusDay={initialFocusDay}
            kickoff={kickoff}
            setKickoff={setKickoff}
            aiSortNotes={aiSortNotes}
            uploadAttachment={slug ? uploadAttachment : null}
            setupImportOpen={setupImportOpen}
            setSetupImportOpen={setSetupImportOpen}
            setupModeSignal={setupModeSignal}
            slug={slug}
          />
        )}
        {activeTab === 'pipeline' && (
          <PipelineStatusView
            pipeline={pipeline}
            checklistState={checklistState}
            kickoff={kickoff}
            isAdmin={isAdmin}
            onToggleChecklist={toggleChecklistItem}
            onOpenSetup={openSetup}
            warmup={warmup}
            startWarmup={startWarmup}
          />
        )}
      </main>

      <footer className="rp-footer">
        <img src="/img/bonusclub_logo.svg" alt="BonusClub" className="rp-footer-logo" />
        <span className="rp-footer-text">
          Diese Analyse ist eine ehrliche Einschätzung des Outbound-Potentials und kein Verkaufsversprechen.
          KI-gestützt erstellt, finale Einschätzung durch das SalesGod-Team.
        </span>
      </footer>
      </div>

      {isAdmin && slug && (
        <SalesGodLaunch
          open={salesGodOpen}
          onClose={() => setSalesGodOpen(false)}
          slug={slug}
          draft={worldDb}
          onSaveDraft={handleWorldDbDraft}
          getToken={getAdminToken}
        />
      )}
    </div>
  );
};

// ── Loader: lädt einen Report per :slug aus Firebase ──
//    /research          → Demo (sampleReport)
//    /research/:slug     → gespeicherter Report aus salesgod-research/reports
// Reports sind nicht mehr oeffentlich lesbar – sie werden ueber diese Function
// geladen und (falls gesetzt) per Passwort freigeschaltet.
const GET_FN_URL =
  process.env.REACT_APP_RESEARCH_GET_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/getResearchReport';
// Zaehlt einen Seitenaufruf hoch (reiner Counter, keine personenbezogenen Daten).
const VIEW_FN_URL =
  process.env.REACT_APP_RESEARCH_VIEW_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/incrementResearchView';
const NOTES_FN_URL =
  process.env.REACT_APP_RESEARCH_NOTES_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/sortNotesFromDump';
const UPLOAD_FN_URL =
  process.env.REACT_APP_RESEARCH_UPLOAD_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/uploadKickoffFile';
const REPORTING_FN_URL =
  process.env.REACT_APP_RESEARCH_REPORTING_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/submitMonthlyReporting';
const WARMUP_FN_URL =
  process.env.REACT_APP_RESEARCH_WARMUP_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/submitWarmup';
const DNA_FN_URL =
  process.env.REACT_APP_RESEARCH_DNA_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/generateEmailDnaExamples';
const SIZE_FN_URL =
  process.env.REACT_APP_RESEARCH_SIZE_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/estimateAudienceSize';
const LEADMAGNET_AUDIT_FN_URL =
  process.env.REACT_APP_RESEARCH_LEADMAGNET_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/generateLeadMagnetAudit';
const GENERATE_IMAGE_FN_URL =
  process.env.REACT_APP_RESEARCH_LEADMAGNET_IMAGE_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/generateLeadMagnetImage';
const LEADMAGNET_DIRECTIONS_FN_URL =
  process.env.REACT_APP_RESEARCH_LEADMAGNET_DIRECTIONS_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/generateLeadMagnetDirections';
// Max. Dateigröße pro Upload (muss zum Backend-Limit passen).
const ATTACH_MAX_BYTES = 5 * 1024 * 1024;
const pwStorageKey = (slug) => `rp-pw-${slug}`;
// View-Zaehler: pro Browser hoechstens 1x alle 15 Minuten feuern (Throttle rein
// clientseitig via localStorage – wir speichern KEINE IP o. ae., nur den Zeitstempel).
const viewThrottleKey = (slug) => `rp-view-ts-${slug}`;
const VIEW_THROTTLE_MS = 15 * 60 * 1000;

// Wartet einmalig, bis Firebase den Login-Status wiederhergestellt hat (sonst
// ist auth.currentUser beim ersten Render evtl. noch null).
const waitForAuthUser = () => new Promise((resolve) => {
  if (auth.currentUser) { resolve(auth.currentUser); return; }
  const unsub = auth.onAuthStateChanged((u) => { unsub(); resolve(u); });
});

// Master-Admin-Token holen (falls eingeloggt) – damit das Reporting ohne Passwort lädt.
const getAdminToken = async () => {
  try {
    const user = await waitForAuthUser();
    return user ? await user.getIdToken() : null;
  } catch (e) {
    return null;
  }
};

// ── Cryptische „Entschlüssel"-Animation für die Gate-Überschrift ──
// Der Zieltext baut sich Zeichen für Zeichen aus zufälligem Rauschen auf.
const SCRAMBLE_GLYPHS = '!<>-_\\/[]{}=+*^?#·•01ABCDEF';
const ScrambleText = ({ text }) => {
  const [display, setDisplay] = useState(text);
  useEffect(() => {
    let frame = 0;
    const totalFrames = 46;
    let raf;
    const tick = () => {
      frame += 1;
      const revealed = Math.floor((frame / totalFrames) * text.length);
      const next = text
        .split('')
        .map((ch, i) => {
          if (ch === ' ') return ' ';
          if (i < revealed) return ch;
          return SCRAMBLE_GLYPHS[Math.floor(Math.random() * SCRAMBLE_GLYPHS.length)];
        })
        .join('');
      setDisplay(next);
      if (frame < totalFrames) raf = requestAnimationFrame(tick);
      else setDisplay(text);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [text]);
  return <span className="rp-scramble">{display}</span>;
};

const ResearchPage = () => {
  const { slug } = useParams();
  const [state, setState] = useState(slug ? { status: 'loading' } : { status: 'ready', report: sampleReport });
  const [pwInput, setPwInput] = useState('');
  const [pwError, setPwError] = useState('');
  const [verifying, setVerifying] = useState(false);

  const fetchReport = useCallback(async (password) => {
    // Eingeloggter Master-Admin: Token mitschicken -> Backend überspringt das Passwort.
    const token = await getAdminToken();
    return fetch(GET_FN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ slug, password: password || '' }),
    });
  }, [slug]);

  const applyResponse = useCallback(async (resp) => {
    const data = await resp.json().catch(() => ({}));
    let parsed;
    try {
      parsed = typeof data.json === 'string' ? JSON.parse(data.json) : data.json;
    } catch (e) {
      return { error: 'Report-JSON konnte nicht gelesen werden.' };
    }
    if (!parsed) return { error: 'Report-Daten fehlen.' };
    return {
      report: normalizeReport(parsed),
      pipeline: data.pipeline || null,
      isAdmin: !!data.isAdmin,
      reportings: Array.isArray(data.reportings) ? data.reportings : [],
      warmup: (data.warmup && typeof data.warmup === 'object') ? data.warmup : null,
      leadMagnetAudit: (data.leadMagnetAudit && typeof data.leadMagnetAudit === 'object') ? data.leadMagnetAudit : null,
    };
  }, []);

  useEffect(() => {
    if (!slug) {
      setState({ status: 'ready', report: sampleReport });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    // ?pw=… aus der Reporting-Erinnerung hat Vorrang vor dem gespeicherten
    // Passwort: nur so fuehrt der Link aus der Mail direkt ins Formular statt
    // auf die Passwort-Abfrage. Der Parameter wird sofort aus der Adresszeile
    // entfernt, damit er nicht in Screenshots oder geteilten Links landet.
    let fromLink = '';
    try { fromLink = (new URLSearchParams(window.location.search).get('pw') || '').trim(); } catch (e) { /* ignore */ }
    if (fromLink) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('pw');
        window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
      } catch (e) { /* ignore */ }
    }
    let stored = fromLink;
    try { stored = fromLink || localStorage.getItem(pwStorageKey(slug)) || ''; } catch (e) { /* ignore */ }
    (async () => {
      try {
        const resp = await fetchReport(stored);
        if (cancelled) return;
        if (resp.status === 401 || resp.status === 403) {
          try { localStorage.removeItem(pwStorageKey(slug)); } catch (e) { /* ignore */ }
          setState({ status: 'password' });
          return;
        }
        if (resp.status === 404) { setState({ status: 'notfound' }); return; }
        if (!resp.ok) {
          const d = await resp.json().catch(() => ({}));
          setState({ status: 'error', message: d.error || `Fehler (HTTP ${resp.status}).` });
          return;
        }
        const result = await applyResponse(resp);
        if (cancelled) return;
        if (result.error) setState({ status: 'error', message: result.error });
        else {
          // Passwort aus dem Link merken, damit auch das Einreichen des
          // Reportings (und ein spaeterer Aufruf) ohne Eingabe funktioniert.
          if (fromLink) {
            try { localStorage.setItem(pwStorageKey(slug), fromLink); } catch (e) { /* ignore */ }
          }
          setState({ status: 'ready', report: result.report, pipeline: result.pipeline, isAdmin: result.isAdmin, reportings: result.reportings, warmup: result.warmup, leadMagnetAudit: result.leadMagnetAudit });
        }
      } catch (e) {
        if (cancelled) return;
        setState({ status: 'error', message: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, [slug, fetchReport, applyResponse]);

  // View-Zaehler: sobald die Seite fuer einen echten Report geladen ist, zaehlen wir
  // EINEN Aufruf hoch – gedrosselt auf max. 1x pro 15 Min pro Browser. Admin-Aufrufe
  // zaehlen serverseitig nicht mit. Fire-and-forget, blockiert die Seite nie.
  useEffect(() => {
    if (!slug) return;
    if (state.status !== 'ready') return;
    if (state.isAdmin) return; // eigene Admin-Besuche nicht mitzaehlen
    let last = 0;
    try { last = Number(localStorage.getItem(viewThrottleKey(slug)) || 0); } catch (e) { /* ignore */ }
    if (Date.now() - last < VIEW_THROTTLE_MS) return;
    try { localStorage.setItem(viewThrottleKey(slug), String(Date.now())); } catch (e) { /* ignore */ }
    (async () => {
      try {
        let stored = '';
        try { stored = localStorage.getItem(pwStorageKey(slug)) || ''; } catch (e) { /* ignore */ }
        await fetch(VIEW_FN_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slug, password: stored }),
        });
      } catch (e) { /* Zaehler ist best-effort – Fehler ignorieren */ }
    })();
  }, [slug, state.status, state.isAdmin]);

  // Auf der Research-Seite die globale (vertikale) Scrollbar des Dokuments ausblenden –
  // gescrollt wird weiterhin normal, nur die Scrollbar wird versteckt (edler Look).
  useEffect(() => {
    document.body.classList.add('rp-hide-scrollbar');
    document.documentElement.classList.add('rp-hide-scrollbar');
    return () => {
      document.body.classList.remove('rp-hide-scrollbar');
      document.documentElement.classList.remove('rp-hide-scrollbar');
    };
  }, []);

  const submitPassword = async (e) => {
    e.preventDefault();
    if (verifying) return;
    const pw = pwInput.trim();
    if (!pw) { setPwError('Bitte Passwort eingeben.'); return; }
    setVerifying(true);
    setPwError('');
    try {
      const resp = await fetchReport(pw);
      if (resp.status === 401 || resp.status === 403) {
        setPwError('Falsches Passwort. Bitte erneut versuchen.');
        setVerifying(false);
        return;
      }
      if (resp.status === 404) { setState({ status: 'notfound' }); return; }
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        setPwError(d.error || `Fehler (HTTP ${resp.status}).`);
        setVerifying(false);
        return;
      }
      const result = await applyResponse(resp);
      if (result.error) { setPwError(result.error); setVerifying(false); return; }
      try { localStorage.setItem(pwStorageKey(slug), pw); } catch (e) { /* ignore */ }
      setState({ status: 'ready', report: result.report, pipeline: result.pipeline, isAdmin: result.isAdmin, reportings: result.reportings, warmup: result.warmup, leadMagnetAudit: result.leadMagnetAudit });
    } catch (e) {
      setPwError('Netzwerkfehler: ' + e.message);
      setVerifying(false);
    }
  };

  if (state.status === 'loading') {
    return (
      <div className="rp-loadscreen">
        <img src="/img/bonusclub_logo.webp" alt="BonusClub" className="rp-loadscreen-logo" />
        <div className="rp-loadspinner" />
      </div>
    );
  }
  if (state.status === 'password') {
    return (
      <div className="rp-gate">
        {/* SalesGod-Hintergrund von der Startseite */}
        <div className="rp-gate-bg" aria-hidden="true">
          <MorphBackground showText={false} />
        </div>
        <div className="rp-gate-veil" aria-hidden="true" />
        <div className="rp-gate-cipher" aria-hidden="true" />

        <form className="rp-gate-card" onSubmit={submitPassword}>
          <div className="rp-gate-vault" aria-hidden="true">
            <span className="rp-gate-ring rp-gate-ring-1" />
            <span className="rp-gate-ring rp-gate-ring-2" />
            <div className="rp-gate-lock">
              <svg className="rp-gate-star" width="34" height="34" viewBox="0 0 64 64" fill="currentColor" aria-hidden="true">
                <path d="M60,28.5H45.062L58,21.03a3.5,3.5,0,0,0,1.281-4.78h0a3.5,3.5,0,0,0-4.78-1.281L41.562,22.438,49.031,9.5a3.5,3.5,0,0,0-1.281-4.78h0A3.5,3.5,0,0,0,42.969,6L35.5,18.938V4A3.5,3.5,0,0,0,32,.5h0A3.5,3.5,0,0,0,28.5,4V18.938L21.031,6a3.5,3.5,0,0,0-4.78-1.281h0A3.5,3.5,0,0,0,14.969,9.5l7.468,12.937L9.5,14.969A3.5,3.5,0,0,0,4.72,16.25h0A3.5,3.5,0,0,0,6,21.03L18.938,28.5H4A3.5,3.5,0,0,0,.5,32v0A3.5,3.5,0,0,0,4,35.5H18.938L6,42.97A3.5,3.5,0,0,0,4.72,47.75h0A3.5,3.5,0,0,0,9.5,49.031l12.937-7.468L14.969,54.5a3.5,3.5,0,0,0,1.281,4.78h0A3.5,3.5,0,0,0,21.031,58L28.5,45.063V60A3.5,3.5,0,0,0,32,63.5h0A3.5,3.5,0,0,0,35.5,60V45.063L42.969,58a3.5,3.5,0,0,0,4.78,1.281h0a3.5,3.5,0,0,0,1.281-4.78L41.562,41.563,54.5,49.031a3.5,3.5,0,0,0,4.78-1.281h0A3.5,3.5,0,0,0,58,42.97L45.062,35.5H60A3.5,3.5,0,0,0,63.5,32v0A3.5,3.5,0,0,0,60,28.5Z" />
              </svg>
            </div>
          </div>

          <span className="rp-gate-eyebrow">SalesGod Research</span>
          <h1 className="rp-gate-title">
            <ScrambleText text="Geschützte Research-Seite" />
          </h1>
          <p className="rp-gate-sub">
            Bitte gib dein Zugangs-Passwort ein, um fortzufahren.
          </p>

          <div className={`rp-gate-field ${pwError ? 'rp-gate-field-error' : ''}`}>
            <span className="rp-gate-field-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="11" width="16" height="9" rx="2" />
                <path d="M8 11V8a4 4 0 0 1 8 0v3" />
              </svg>
            </span>
            <input
              type="password"
              className="rp-gate-input"
              value={pwInput}
              autoFocus
              placeholder="Passwort"
              onChange={(e) => { setPwInput(e.target.value); if (pwError) setPwError(''); }}
            />
          </div>
          {pwError && <span className="rp-gate-error">{pwError}</span>}

          <button type="submit" className="rp-gate-btn" disabled={verifying}>
            {verifying && <span className="rp-gate-btn-spin" aria-hidden="true" />}
            <span className="rp-gate-btn-label">{verifying ? 'Prüfe…' : 'Entsperren'}</span>
          </button>

          <span className="rp-gate-foot">Vertraulich · nur für das SalesGod-Team</span>
        </form>
      </div>
    );
  }
  if (state.status === 'notfound') {
    return (
      <div className="rp-loadscreen">
        <span className="rp-loadscreen-title">Report nicht gefunden</span>
        <span>Für „{slug}" existiert keine Research-Seite.</span>
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="rp-loadscreen">
        <span className="rp-loadscreen-title">Fehler beim Laden</span>
        <span>{state.message}</span>
      </div>
    );
  }

  return <ResearchReport key={slug || 'demo'} report={state.report} slug={slug} pipeline={state.pipeline} isAdmin={state.isAdmin} reportings={state.reportings} warmup={state.warmup} leadMagnetAudit={state.leadMagnetAudit} />;
};

export default ResearchPage;
